import {
  logAcabado,
  logAcabadoApi,
  logAcabadoTexture,
  logGola,
  logGolaApi,
  logGolaItemTexturesSnapshot,
  logGolaLinkedChildrenSnapshot,
  logItemTexturesSnapshot,
  logLinkedChildrenSnapshot,
} from '@context/utils/linked/linkedAcabadoChangeLogger'
import { isLinkedVisualBatchActive } from '@context/utils/linked/runLinkedVisualBatch'
import {
  applyIdCambioFromApi,
  isGolaEffective,
  normalizeLinkedCatalogBase,
} from '@context/utils/linked/linkedUnitVariants'
import { syncLinkedItemZonesFromTextures } from '@context/utils/linked/syncLinkedItemZonesFromTextures'
import { getProxyUrl } from '@proxies/index'
import { useAppStore } from '@store'
import {
  AnyID,
  BackGLBInfo,
  BackLinkedData,
  Getter,
  Item,
  ItemWithOptions,
  LinkedItem,
  LinkedItemID,
  // LinkedItem,
  LinkedItemSon,
  LinkedItemWithMorphs,
  LinkedItemsID,
  Material,
  Options,
  SceneObjects,
  Setter,
  Store,
  TiradorWithPositions,
} from '@types'
import { mapMorphsFromDTO } from '@utils/mapMorphsFromDTO'
import { API } from '@utils/queryBuilder'
import isLinked from '@utils/sceneObjects/assertions/isLinked'
import isLinkedItem from '@utils/sceneObjects/assertions/isLinkedItem'
import isLinkedSon from '@utils/sceneObjects/assertions/isLinkedSon'
import {
  getExistingTexturesFromScene,
  mapExistingTexturesToNewItemByDisplayName,
  syncZonesWithMappedTextures,
} from '@utils/sceneObjects/getExistingTexturesFromScene'
import getId from '@utils/sceneObjects/getId'
import { getZocaloMaterialFieldsFromOption } from '@utils/sceneObjects/getZocaloMaterialFromOption'
import { getZonesFromGLB } from '@utils/sceneObjects/getZonesFromGLB'
import { adjustChildrenPositionsAfterInheritance } from '@utils/sceneObjects/items/adjustChildrenToEncimera'
import { getGLB } from '@utils/sceneObjects/items/getGLB'
import { newGetDataFromGLB } from '@utils/sceneObjects/items/newGetDataFromGLB'
import { depthVisualOrderSorter } from '@utils/sceneObjects/linked/depthVisualOrderSorter'
import fetchLinkedChildrenInfo from '@utils/sceneObjects/linked/fetchLinkedChildrenInfo'
import {
  applyEncimeraThicknessMm,
  freeDimensionUsesLb8060StyleAltoOffset,
  getFreeDimensionEncimeraPositionYMm,
  getFreeDimensionParentLayoutAltoMm,
  getFreeDimensionVisualBodyTopYMm,
  isEncimeraLinkedSonItem,
  isRbFreeDimensionRegleta,
  logFreeDimensionAncho,
  placeLinkedEncimeraY,
  syncFreeDimensionEncimeraAnchoToParent,
  syncFreeDimensionEncimeraFondoToParent,
  usesFreeDimensionScaling,
} from '@utils/sceneObjects/linked/freeDimensionLinkedCatalogs'
import { setGLTFLoader } from '@utils/sceneObjects/setGLTFLoader'
import { Euler, Matrix4 } from 'three'
import { calculateTiradorPosition } from '../utils/calculateTiradorPosition'
import getLinkedChildrenInfo from './getLinkedChildrenInfo'

/** Cajón/drawer index: option identifica el cajón; nodo del API suele ser el tipo de elemento (2). */
function getTiradorDrawerNode(child: LinkedItemSon): number | undefined {
  return child.option ?? child.nodo
}

function resolveTiradorIdElementosEnlazados(
  tiradorOptions: ItemWithOptions[] | undefined,
  child: LinkedItemSon,
  clickedIdElementosEnlazados: number,
): number | null {
  const drawerNode = getTiradorDrawerNode(child)
  if (!tiradorOptions?.length || drawerNode === undefined) {
    return clickedIdElementosEnlazados
  }

  const newTiradorOption = tiradorOptions.find(
    (opt) => opt.idElementosEnlazados === clickedIdElementosEnlazados,
  )
  if (!newTiradorOption?.idUnidadEnlazada) {
    return clickedIdElementosEnlazados
  }

  const matchingOption = tiradorOptions.find(
    (opt) => opt.nodo === drawerNode && opt.idUnidadEnlazada === newTiradorOption.idUnidadEnlazada,
  )
  return matchingOption?.idElementosEnlazados ?? null
}

/** Grifo/fregadero usan posicionX/Y/Z; tiradores usan bcPosicion. No ocultar si hay posición válida. */
function resolveLinkedSonVisibility(
  responseData: Record<string, unknown>,
  prefix: 'a' | 'b',
  fallback?: { x?: number; y?: number; z?: number },
  options?: { isGolaUnit?: boolean; isTirador?: boolean },
): boolean {
  if (options?.isGolaUnit && options?.isTirador) return false

  const cx = responseData[`${prefix}cPosicionX`] as number | null | undefined
  const cy = responseData[`${prefix}cPosicionY`] as number | null | undefined
  const cz = responseData[`${prefix}cPosicionZ`] as number | null | undefined
  const x = cx ?? (responseData.posicionX as number | undefined) ?? fallback?.x ?? 0
  const y = cy ?? (responseData.posicionY as number | undefined) ?? fallback?.y ?? 0
  const z = cz ?? (responseData.posicionZ as number | undefined) ?? fallback?.z ?? 0

  return !(x === 0 && y === 0 && z === 0)
}

const buildTexturesFromBackData = (item: LinkedItem, _newUnidadData: any) => {
  const currentTextures = Array.isArray(item.textures) ? item.textures : []
  return {
    textures: currentTextures.map((texture: any) => ({ ...texture })),
  }
}

export type LinkedItemsActions = ReturnType<typeof createLinkedItemsActions>

export const createLinkedItemsActions = (set: Setter, get: Getter) => ({
  editLinkedTexture: (linkedID: LinkedItemsID, zoneName: string, material: Material) => {
    let newTexture
    set((s) => {
      const item = s.items.get(linkedID)
      if (!isLinked(item)) return
      const zone = item.zones[zoneName]
      if (!material || !zone) return
      const spreaded = { ...zone?.material, ...material }
      item.zones[zoneName].material = { ...spreaded }
      const texture = item.textures.find((el) => el.zoneName === zoneName)
      if (!texture) return
      newTexture = {
        ...texture,
        groupID: material.groupID,
        name: material.name,
        searchID: material.searchID,
        url: material.url,
        modelo: material.modelo,
        acabado: material.acabado,
        id: material.id,
      }
      texture.groupID = material.groupID
      texture.name = material.name
      texture.searchID = material.searchID
      texture.url = material.url
      texture.id = material.id
      texture.modelo = material.modelo
      texture.acabado = material.acabado
    })
    return newTexture
  },

  editLinkedTexture2: (
    linkedID: LinkedItemsID,
    zoneName: string,
    material: Material,
    modeEditor: 'Single' | 'All' | 'Selected' | 'MBJ' | 'MAL' | 'COL',
    selectedIds?: Set<LinkedItemsID>,
  ) => {
    let newTexture
    set((s) => {
      const item = s.items.get(linkedID)
      if (!isLinked(item)) return

      const hasZocalo = (targetItem: any): boolean => {
        if (!isLinked(targetItem)) return false
        return (
          targetItem.textures?.some(
            (t: any) =>
              t.zoneDisplayName === 'Zocalo' ||
              t.zone?.toLowerCase().includes('zocalo') ||
              t.zoneName?.toLowerCase().includes('zocalo'),
          ) || false
        )
      }

      const getZocaloTextures = (targetItem: any): any[] => {
        if (!isLinked(targetItem)) return []
        return (
          targetItem.textures?.filter(
            (t: any) =>
              t.zoneDisplayName === 'Zocalo' ||
              t.zone?.toLowerCase().includes('zocalo') ||
              t.zoneName?.toLowerCase().includes('zocalo'),
          ) || []
        )
      }

      const getItemModel = (targetItem: any): string | null => {
        if (!targetItem || !isLinked(targetItem) || !targetItem.textures) return null
        const textureWithModel = targetItem.textures.find((t: any) => t.modelo && t.modelo !== '')
        return textureWithModel?.modelo || null
      }

      let referenceModel = getItemModel(item)

      const isCompatibleModel = (targetItem: any): boolean => {
        const targetModel = getItemModel(targetItem)
        if (!targetModel || !referenceModel) return false

        const isVitrina = referenceModel.toLowerCase().includes('vitrina')
        if (isVitrina) {
          return targetModel === referenceModel
        }

        if (referenceModel === 'AcabadoModelo') {
          return targetModel === 'AcabadoModelo'
        }

        return targetModel.split('_')[0] === referenceModel.split('_')[0]
      }

      const zone = item.zones[zoneName]
      if (!material || !zone) return

      const spreaded = { ...zone.material, ...material }
      item.zones[zoneName].material = { ...spreaded }

      const texture = item.textures.find((el) => el.zoneName === zoneName)
      if (!texture) return

      referenceModel = texture.modelo || referenceModel
      if (!referenceModel) return

      newTexture = {
        ...texture,
        groupID: material.groupID,
        name: material.name,
        searchID: material.searchID,
        url: material.url,
        modelo: material.modelo,
        acabado: material.acabado,
        id: material.id,
        // Mantener todas las propiedades de brillo para la zona que se está modificando
        envMapIntensity: material.envMapIntensity,
        metalness: material.metalness,
        roughness: material.roughness,
        opacity: material.opacity,
        color: material.color,
        mirror: material.mirror,
        repeatX: material.repeatX,
        repeatY: material.repeatY,
        bumpMap: material.bumpMap,
        bumpScale: material.bumpScale,
        displacement: material.displacement,
        displacementScale: material.displacementScale,
        textureRotation: material.textureRotation,
        complemento: material.complemento,
        description: material.description,
        img: material.img,
      }

      Object.assign(texture, newTexture)

      const updateZocaloTexture = (targetItem: SceneObjects) => {
        if (!isLinked(targetItem)) return

        if (!hasZocalo(targetItem)) return

        const zocaloTextures = getZocaloTextures(targetItem)
        if (zocaloTextures.length === 0) return

        const complementoName = (material as any).complemento as string | undefined

        zocaloTextures.forEach((zocaloTexture) => {
          if (complementoName) {
            if (
              (targetItem as any).zocaloOptions &&
              Array.isArray((targetItem as any).zocaloOptions)
            ) {
              const cmp = complementoName.toLowerCase()
              const matchingTexture = (targetItem as any).zocaloOptions.find((t: any) => {
                const n = (t?.nombre || '').toLowerCase()
                const d = (t?.descripcion || '').toLowerCase()
                return n === cmp || n.includes(cmp) || d.includes(cmp)
              })

              if (matchingTexture) {
                const matchedZocaloMaterial = getZocaloMaterialFieldsFromOption(
                  matchingTexture,
                  (material as any).complemento,
                )

                Object.assign(zocaloTexture, matchedZocaloMaterial)

                const targetZoneKey = Object.keys(targetItem.zones).find(
                  (k) => targetItem.zones[k].name === zocaloTexture.zoneName,
                )
                if (targetZoneKey && targetItem.zones[targetZoneKey]?.material) {
                  Object.assign(targetItem.zones[targetZoneKey].material, matchedZocaloMaterial)
                }
                return
              }
            }

            Object.assign(zocaloTexture, {
              name: complementoName,
              acabado: 'Basico',
              description: material.description,
              // NO copiar propiedades de brillo del material de la Puerta
              // envMapIntensity: material.envMapIntensity,
              // metalness: material.metalness,
              // roughness: material.roughness,
              opacity: material.opacity,
              color: material.color,
              mirror: material.mirror,
              repeatX: material.repeatX,
              repeatY: material.repeatY,
              bumpMap: material.bumpMap,
              bumpScale: material.bumpScale,
              displacement: material.displacement,
              displacementScale: material.displacementScale,
              textureRotation: material.textureRotation,
              complemento: material.complemento,
              img: material.img,
            })

            const targetZoneKey = Object.keys(targetItem.zones).find(
              (k) => targetItem.zones[k].name === zocaloTexture.zoneName,
            )
            if (targetZoneKey && targetItem.zones[targetZoneKey]?.material) {
              Object.assign(targetItem.zones[targetZoneKey].material, {
                name: complementoName,
                acabado: 'Basico',
                description: material.description,
                // NO copiar propiedades de brillo del material de la Puerta
                // envMapIntensity: material.envMapIntensity,
                // metalness: material.metalness,
                // roughness: material.roughness,
                opacity: material.opacity,
                color: material.color,
                mirror: material.mirror,
                repeatX: material.repeatX,
                repeatY: material.repeatY,
                bumpMap: material.bumpMap,
                bumpScale: material.bumpScale,
                displacement: material.displacement,
                displacementScale: material.displacementScale,
                textureRotation: material.textureRotation,
                complemento: material.complemento,
                img: material.img,
              })
            }
            return
          }

          Object.assign(zocaloTexture, {
            groupID: material.groupID,
            name: material.name,
            searchID: material.searchID,
            url: material.url,
            modelo: material.modelo,
            acabado: 'Basico',
            id: material.id,
            description: material.description,
            // NO copiar propiedades de brillo del material de la Puerta
            // envMapIntensity: material.envMapIntensity,
            // metalness: material.metalness,
            // roughness: material.roughness,
            opacity: material.opacity,
            color: material.color,
            mirror: material.mirror,
            repeatX: material.repeatX,
            repeatY: material.repeatY,
            bumpMap: material.bumpMap,
            bumpScale: material.bumpScale,
            displacement: material.displacement,
            displacementScale: material.displacementScale,
            textureRotation: material.textureRotation,
            complemento: material.complemento,
            img: material.img,
          })

          const targetZoneKey = Object.keys(targetItem.zones).find(
            (k) => targetItem.zones[k].name === zocaloTexture.zoneName,
          )
          if (targetZoneKey && targetItem.zones[targetZoneKey]?.material) {
            Object.assign(targetItem.zones[targetZoneKey].material, {
              groupID: material.groupID,
              name: material.name,
              searchID: material.searchID,
              url: material.url,
              modelo: material.modelo,
              acabado: 'Basico',
              id: material.id,
              description: material.description,
              // NO copiar propiedades de brillo del material de la Puerta
              // envMapIntensity: material.envMapIntensity,
              // metalness: material.metalness,
              // roughness: material.roughness,
              opacity: material.opacity,
              color: material.color,
              mirror: material.mirror,
              repeatX: material.repeatX,
              repeatY: material.repeatY,
              bumpMap: material.bumpMap,
              bumpScale: material.bumpScale,
              displacement: material.displacement,
              displacementScale: material.displacementScale,
              textureRotation: material.textureRotation,
              complemento: material.complemento,
              img: material.img,
            })
          }
        })
      }

      const syncRelatedZones = (targetItem: SceneObjects, changedZoneDisplayName: string) => {
        if (!isLinked(targetItem) || !isCompatibleModel(targetItem)) return

        let relatedZoneDisplayName: string | null = null

        if (changedZoneDisplayName === 'Puerta') {
          relatedZoneDisplayName = 'Costado'
        } else if (changedZoneDisplayName === 'Costado') {
          relatedZoneDisplayName = 'Puerta'
        }

        if (!relatedZoneDisplayName) return

        const relatedTextures = targetItem.textures.filter(
          (el) => el.zoneDisplayName === relatedZoneDisplayName,
        )

        relatedTextures.forEach((relatedTexture) => {
          if (!relatedTexture) return

          Object.assign(relatedTexture, {
            groupID: material.groupID,
            name: material.name,
            searchID: material.searchID,
            url: material.url,
            acabado: material.acabado,
            id: material.id,
            envMapIntensity: material.envMapIntensity,
            metalness: material.metalness,
            roughness: material.roughness,
            opacity: material.opacity,
            color: material.color,
            mirror: material.mirror,
            repeatX: material.repeatX,
            repeatY: material.repeatY,
            bumpMap: material.bumpMap,
            bumpScale: material.bumpScale,
            displacement: material.displacement,
            displacementScale: material.displacementScale,
            textureRotation: material.textureRotation,
            complemento: material.complemento,
            description: material.description,
            img: material.img,
          })

          const relatedZoneKey = Object.keys(targetItem.zones).find(
            (k) => targetItem.zones[k].name === relatedTexture.zoneName,
          )

          if (relatedZoneKey && targetItem.zones[relatedZoneKey]?.material) {
            Object.assign(targetItem.zones[relatedZoneKey].material, {
              groupID: material.groupID,
              name: material.name,
              searchID: material.searchID,
              url: material.url,
              acabado: material.acabado,
              id: material.id,
              envMapIntensity: material.envMapIntensity,
              metalness: material.metalness,
              roughness: material.roughness,
              opacity: material.opacity,
              color: material.color,
              mirror: material.mirror,
              repeatX: material.repeatX,
              repeatY: material.repeatY,
              bumpMap: material.bumpMap,
              bumpScale: material.bumpScale,
              displacement: material.displacement,
              displacementScale: material.displacementScale,
              textureRotation: material.textureRotation,
              complemento: material.complemento,
              description: material.description,
              img: material.img,
            })
          }
        })
      }

      const changedZoneDisplayName = texture.zoneDisplayName

      if (modeEditor === 'Single') {
        syncRelatedZones(item, changedZoneDisplayName)

        if (changedZoneDisplayName === 'Puerta') {
          updateZocaloTexture(item)
        }
      } else if (modeEditor === 'All') {
        // Determinar si el cambio se está haciendo desde un padre (linkedItem) o desde un hijo (linkedSon)
        const isChangingFromParent = !linkedID.startsWith('linkedSon_')

        s.items.forEach((targetItem) => {
          if (!isLinked(targetItem)) return

          if (isChangingFromParent && targetItem.type === 'linkedSon') {
            return
          }

          // Para la zona "Gola", no verificar compatibilidad de modelo ya que puede tener modelos diferentes
          // (MuebleGola vs AcabadoModelo) pero aún así debe aplicarse a todos
          const isGolaZone = changedZoneDisplayName === 'Gola'
          if (!isGolaZone && !isCompatibleModel(targetItem)) return

          const targetTextures = targetItem.textures.filter(
            (el) => el.zoneDisplayName === changedZoneDisplayName,
          )

          targetTextures.forEach((targetTexture) => {
            if (!targetTexture) return

            Object.assign(targetTexture, {
              groupID: material.groupID,
              name: material.name,
              searchID: material.searchID,
              url: material.url,
              acabado: material.acabado,
              id: material.id,
              envMapIntensity: material.envMapIntensity,
              metalness: material.metalness,
              roughness: material.roughness,
              opacity: material.opacity,
              color: material.color,
              mirror: material.mirror,
              repeatX: material.repeatX,
              repeatY: material.repeatY,
              bumpMap: material.bumpMap,
              bumpScale: material.bumpScale,
              displacement: material.displacement,
              displacementScale: material.displacementScale,
              textureRotation: material.textureRotation,
              complemento: material.complemento,
              description: material.description,
              img: material.img,
            })

            const targetZoneKey = Object.keys(targetItem.zones).find(
              (k) => targetItem.zones[k].name === targetTexture.zoneName,
            )

            if (targetZoneKey && targetItem.zones[targetZoneKey]?.material) {
              Object.assign(targetItem.zones[targetZoneKey].material, {
                groupID: material.groupID,
                name: material.name,
                searchID: material.searchID,
                url: material.url,
                acabado: material.acabado,
                id: material.id,
                // Solo aplicar propiedades de brillo a elementos del mismo tipo
                ...(targetItem.description === item.description && {
                  envMapIntensity: material.envMapIntensity,
                  metalness: material.metalness,
                  roughness: material.roughness,
                }),
                opacity: material.opacity,
                color: material.color,
                mirror: material.mirror,
                repeatX: material.repeatX,
                repeatY: material.repeatY,
                bumpMap: material.bumpMap,
                bumpScale: material.bumpScale,
                displacement: material.displacement,
                displacementScale: material.displacementScale,
                textureRotation: material.textureRotation,
                complemento: material.complemento,
                description: material.description,
                img: material.img,
              })
            }
          })

          syncRelatedZones(targetItem, changedZoneDisplayName)
        })

        if (changedZoneDisplayName === 'Puerta') {
          s.items.forEach((targetItem) => {
            if (!isLinked(targetItem)) return
            if (isChangingFromParent && targetItem.type === 'linkedSon') {
              return
            }
            updateZocaloTexture(targetItem)
          })
        }
      } else if (modeEditor === 'MBJ' || modeEditor === 'MAL' || modeEditor === 'COL') {
        const targetTipo = modeEditor
        const isChangingFromParent = !linkedID.startsWith('linkedSon_')

        s.items.forEach((targetItem) => {
          if (
            !isLinked(targetItem) ||
            targetItem.tipoNormalizado !== targetTipo ||
            !isCompatibleModel(targetItem)
          )
            return

          if (isChangingFromParent && targetItem.type === 'linkedSon') {
            return
          }

          const targetTextures = targetItem.textures.filter(
            (el) => el.zoneDisplayName === changedZoneDisplayName,
          )

          targetTextures.forEach((targetTexture) => {
            if (!targetTexture) return

            Object.assign(targetTexture, {
              groupID: material.groupID,
              name: material.name,
              searchID: material.searchID,
              url: material.url,
              acabado: material.acabado,
              id: material.id,
              envMapIntensity: material.envMapIntensity,
              metalness: material.metalness,
              roughness: material.roughness,
              opacity: material.opacity,
              color: material.color,
              mirror: material.mirror,
              repeatX: material.repeatX,
              repeatY: material.repeatY,
              bumpMap: material.bumpMap,
              bumpScale: material.bumpScale,
              displacement: material.displacement,
              displacementScale: material.displacementScale,
              textureRotation: material.textureRotation,
              complemento: material.complemento,
              description: material.description,
              img: material.img,
            })

            const targetZoneKey = Object.keys(targetItem.zones).find(
              (k) => targetItem.zones[k].name === targetTexture.zoneName,
            )

            if (targetZoneKey && targetItem.zones[targetZoneKey]?.material) {
              Object.assign(targetItem.zones[targetZoneKey].material, {
                groupID: material.groupID,
                name: material.name,
                searchID: material.searchID,
                url: material.url,
                acabado: material.acabado,
                id: material.id,
                // Solo aplicar propiedades de brillo a elementos del mismo tipo
                ...(targetItem.description === item.description && {
                  envMapIntensity: material.envMapIntensity,
                  metalness: material.metalness,
                  roughness: material.roughness,
                }),
                opacity: material.opacity,
                color: material.color,
                mirror: material.mirror,
                repeatX: material.repeatX,
                repeatY: material.repeatY,
                bumpMap: material.bumpMap,
                bumpScale: material.bumpScale,
                displacement: material.displacement,
                displacementScale: material.displacementScale,
                textureRotation: material.textureRotation,
                complemento: material.complemento,
                description: material.description,
                img: material.img,
              })
            }
          })

          syncRelatedZones(targetItem, changedZoneDisplayName)

          if (changedZoneDisplayName === 'Puerta') {
            updateZocaloTexture(targetItem)
          }
        })
      } else if (modeEditor === 'Selected' && selectedIds) {
        // Determinar si el cambio se está haciendo desde un padre (linkedItem) o desde un hijo (linkedSon)
        const isChangingFromParent = !linkedID.startsWith('linkedSon_')

        selectedIds.forEach((selectedId) => {
          const selectedItem = s.items.get(selectedId)
          if (!selectedItem || !isLinked(selectedItem) || !isCompatibleModel(selectedItem)) return

          // NO aplicar cambios a linkedSon cuando se cambia desde el padre
          if (isChangingFromParent && selectedItem.type === 'linkedSon') {
            return
          }

          const targetTextures = selectedItem.textures.filter(
            (el) => el.zoneDisplayName === changedZoneDisplayName,
          )

          targetTextures.forEach((targetTexture) => {
            if (!targetTexture) return

            Object.assign(targetTexture, {
              groupID: material.groupID,
              name: material.name,
              searchID: material.searchID,
              url: material.url,
              acabado: material.acabado,
              id: material.id,
              envMapIntensity: material.envMapIntensity,
              metalness: material.metalness,
              roughness: material.roughness,
              opacity: material.opacity,
              color: material.color,
              mirror: material.mirror,
              repeatX: material.repeatX,
              repeatY: material.repeatY,
              bumpMap: material.bumpMap,
              bumpScale: material.bumpScale,
              displacement: material.displacement,
              displacementScale: material.displacementScale,
              textureRotation: material.textureRotation,
              complemento: material.complemento,
              description: material.description,
              img: material.img,
            })

            const targetZoneKey = Object.keys(selectedItem.zones).find(
              (k) => selectedItem.zones[k].name === targetTexture.zoneName,
            )

            if (targetZoneKey && selectedItem.zones[targetZoneKey]?.material) {
              Object.assign(selectedItem.zones[targetZoneKey].material, {
                groupID: material.groupID,
                name: material.name,
                searchID: material.searchID,
                url: material.url,
                acabado: material.acabado,
                id: material.id,
                // Solo aplicar propiedades de brillo a elementos del mismo tipo
                ...(selectedItem.description === item.description && {
                  envMapIntensity: material.envMapIntensity,
                  metalness: material.metalness,
                  roughness: material.roughness,
                }),
                opacity: material.opacity,
                color: material.color,
                mirror: material.mirror,
                repeatX: material.repeatX,
                repeatY: material.repeatY,
                bumpMap: material.bumpMap,
                bumpScale: material.bumpScale,
                displacement: material.displacement,
                displacementScale: material.displacementScale,
                textureRotation: material.textureRotation,
                complemento: material.complemento,
                description: material.description,
                img: material.img,
              })
            }
          })
          syncRelatedZones(selectedItem, changedZoneDisplayName)

          if (changedZoneDisplayName === 'Puerta') {
            updateZocaloTexture(selectedItem)
          }
        })
      }

      set((s) => {
        s.shouldUpdateCamera = true
      })
    })

    if (newTexture) {
      logAcabadoTexture('editLinkedTexture2_applied', newTexture as Record<string, unknown>, {
        linkedID,
        zoneName,
        modeEditor,
        materialAcabado: material.acabado,
        materialName: material.name,
      })
    } else {
      logAcabado('editLinkedTexture2_no_texture', { linkedID, zoneName, modeEditor })
    }

    return newTexture
  },

  updateLinkedMorph: () => {
    // This has yet to be commented together
  },

  removeLinkedFromParent: (linkedID: string) => {
    // This will make it independent from their parent
  },

  updateLinkedHierarchy: (parent: string) => {
    // This will update the 1.1, 1.2 and such when you delete/move an item
  },

  updateLinkedGLB: async (linkedID: string, idElementosEnlazados: number, info: Options) => {
    const sceneGLB = get().sceneGLB
    const prevItem = get().items.get(linkedID as AnyID)
    const items = get().items

    logAcabado('updateLinkedGLB_START', {
      linkedID,
      idElementosEnlazados,
      description: prevItem?.description,
      catalogID: prevItem?.catalogID,
      firstParentID: (prevItem as LinkedItemSon)?.firstParentID,
    })

    if (!prevItem) {
      logAcabado('updateLinkedGLB_ABORT', { linkedID, reason: 'no_item' })
      return
    }
    if (!isLinkedSon(prevItem)) {
      logAcabado('updateLinkedGLB_ABORT', { linkedID, reason: 'not_linkedSon' })
      return
    }

    let prevHand: 'Izquierda' | 'Centro' | 'Derecha' | 'Normal' = 'Derecha'
    if (prevItem.description === 'Tirador' && prevItem.handPosition) {
      prevHand = prevItem.handPosition
    }

    const parentID = prevItem.firstParentID
    const parentItem = items.get(parentID as AnyID)

    if (!parentItem) {
      return
    }
    if (!isLinkedItem(parentItem)) {
      return
    }
    if (!parentItem.idUnidades) {
      return
    }
    const parentIdUnidades = parentItem.idUnidades

    let targetIdElementosEnlazados = idElementosEnlazados
    if (prevItem.description === 'Tirador') {
      const resolved = resolveTiradorIdElementosEnlazados(
        parentItem.tiradorOptions,
        prevItem,
        idElementosEnlazados,
      )
      if (resolved === null) return
      targetIdElementosEnlazados = resolved
    }

    let newLinkedSonInfo
    try {
      const idAnt =
        prevItem.description === 'Tirador'
          ? prevItem.linkedID || targetIdElementosEnlazados
          : parentIdUnidades
      logAcabadoApi(
        'acabadosGetElementosEnlazadosUnidadAcabados2',
        { idAnt, targetIdElementosEnlazados },
      )
      const response = await API().api.acabadosGetElementosEnlazadosUnidadAcabados2(
        idAnt,
        targetIdElementosEnlazados,
      )
      logAcabadoApi(
        'acabadosGetElementosEnlazadosUnidadAcabados2',
        { idAnt, targetIdElementosEnlazados },
        response,
      )

      if (!response) {
        logAcabado('updateLinkedGLB_ABORT', { linkedID, reason: 'no_response' })
        return
      }
      if (!response.data) {
        logAcabado('updateLinkedGLB_ABORT', { linkedID, reason: 'no_response_data' })
        return
      }
      newLinkedSonInfo = response.data
    } catch (error) {
      logAcabado('updateLinkedGLB_ERROR', { linkedID, error: String(error) })
      return
    }
    const parent = items.get(prevItem.firstParentID as AnyID)
    if (!isLinkedItem(parent)) {
      return
    }
    const loader = await setGLTFLoader()

    if (newLinkedSonInfo && newLinkedSonInfo.unidad) {
      if (!newLinkedSonInfo.unidad) {
        return
      }
      const unidad = newLinkedSonInfo.unidad
      if (!unidad) {
        return
      }
      const glbRef = unidad.referenciaBlender
      if (!glbRef) {
        return
      }

      const glb = await loader.loadAsync(glbRef)

      if (!unidad.idUnidades) {
        return
      }
      const extraInfo = await API().api.acabadosGetUnidadAcabadosInfo(unidad.idUnidades)

      const backGLBInfo: BackGLBInfo = {
        ...unidad,
        linkedID: (unidad as any).linkedID ?? unidad.idUnidades ?? 0,
        idUnidades: unidad.idUnidades ?? 0,
        codigo: unidad.codigo ?? '',
        active: true,
      }
      const processedInfo = newGetDataFromGLB({
        glbData: glb,
        backGLBInfo,
        id: '',
        type: 'linkedSon',
        catalogID: unidad.codigo ?? '',
        extraInfo: {
          zonas: Array.isArray(extraInfo.data?.zonas)
            ? extraInfo.data.zonas.map((z) => ({
                idAcabadoUnidad: z.idAcabadoUnidad,
                zona: z.zona ?? '',
              }))
            : [],
        },
        itemInfo: {},
      })

      const backLinkedData = {
        ...newLinkedSonInfo,
        cantidad: newLinkedSonInfo.cantidad ?? 1,
      } as BackLinkedData

      const childrenInfo = getLinkedChildrenInfo(
        backLinkedData,
        info as unknown as Record<string, Record<string, ItemWithOptions[]>>,
        targetIdElementosEnlazados,
      )
      const newChildrenOrder = [...parent.childrenOrder]
      set((s) => {
        const currElement = s.items.get(linkedID as AnyID)
        if (!isLinkedSon(currElement)) return
        const prevPosition = { x: currElement.x, y: currElement.y, z: currElement.z }
        const prevSize = {
          width: currElement.width,
          height: currElement.height,
          thickness: currElement.thickness,
        }
        const prevMorphs = currElement.morphs

        let newX = prevPosition.x
        let newY = prevPosition.y
        let newZ = prevPosition.z

        if (parent) {
          const relativeX = prevPosition.x - parent.x
          const relativeY = prevPosition.y - parent.y
          const relativeZ = prevPosition.z - parent.z

          newX = parent.x + relativeX
          newY = parent.y + relativeY
          newZ = parent.z + relativeZ

          if (currElement.description !== 'Tirador') {
            if (
              Math.abs(relativeX) < 1000 &&
              Math.abs(relativeY) < 1000 &&
              Math.abs(relativeZ) < 1000
            ) {
              currElement.x = newX
              currElement.y = newY
              currElement.z = newZ
            }
          }
        }

        currElement.linkedID = targetIdElementosEnlazados
        currElement.catalogID = unidad.codigo!
        currElement.idUnidades = unidad.idUnidades ?? 0
        currElement.textures = processedInfo.textures
        currElement.zones = processedInfo.zones

        if (currElement.description === 'Tirador') {
          if (newLinkedSonInfo) {
            if (typeof newLinkedSonInfo.posicionX === 'number') {
              currElement.x = newLinkedSonInfo.posicionX
            } else {
              currElement.x = newX
            }
            if (typeof newLinkedSonInfo.posicionY === 'number') {
              currElement.y = newLinkedSonInfo.posicionY
            } else {
              currElement.y = newY
            }
            if (typeof newLinkedSonInfo.posicionZ === 'number') {
              currElement.z = newLinkedSonInfo.posicionZ
            } else {
              currElement.z = newZ
            }
          } else {
            currElement.x = newX
            currElement.y = newY
            currElement.z = newZ
          }
        } else if (currElement.typeOfSon === 'Fregadero' && newLinkedSonInfo) {
          const posicionBackendX = newLinkedSonInfo.posicionX ?? 0
          const posicionBackendY = newLinkedSonInfo.posicionY ?? 0
          const posicionBackendZ = newLinkedSonInfo.posicionZ ?? 0

          if (isNaN(posicionBackendX) || isNaN(posicionBackendY) || isNaN(posicionBackendZ)) {
            currElement.x = newX
            currElement.y = newY
            currElement.z = newZ
          } else {
            currElement.x = posicionBackendX
            currElement.y = posicionBackendY
            currElement.z = posicionBackendZ
            ;(currElement as any).baseY = posicionBackendY
          }

          setTimeout(() => {
            const positionFregaderoGrifoPlaca = get().positionFregaderoGrifoPlacaAccordingToEncimera
            if (positionFregaderoGrifoPlaca) {
              positionFregaderoGrifoPlaca(linkedID)
            }
          }, 0)
        } else if (currElement.typeOfSon === 'Placa' && newLinkedSonInfo) {
          const posicionBackendX = newLinkedSonInfo.posicionX ?? 0
          const posicionBackendY = newLinkedSonInfo.posicionY ?? 0
          const posicionBackendZ = newLinkedSonInfo.posicionZ ?? 0

          if (isNaN(posicionBackendX) || isNaN(posicionBackendY) || isNaN(posicionBackendZ)) {
            currElement.x = newX
            currElement.y = newY
            currElement.z = newZ
          } else {
            currElement.x = posicionBackendX
            currElement.y = posicionBackendY
            currElement.z = posicionBackendZ
          }
        } else if (
          (currElement.typeOfSon === 'Grifo' || currElement.typeOfSon === 'TPCGrifo') &&
          newLinkedSonInfo
        ) {
          const posicionBackendX = newLinkedSonInfo.posicionX ?? 0
          const posicionBackendY = newLinkedSonInfo.posicionY ?? 0
          const posicionBackendZ = newLinkedSonInfo.posicionZ ?? 0

          if (isNaN(posicionBackendX) || isNaN(posicionBackendY) || isNaN(posicionBackendZ)) {
            currElement.x = newX
            currElement.y = newY
            currElement.z = newZ
          } else {
            currElement.x = posicionBackendX
            currElement.y = posicionBackendY
            currElement.z = posicionBackendZ
            ;(currElement as any).baseY = posicionBackendY
          }
        } else if (currElement.typeOfSon === 'TPCGrifo' && newLinkedSonInfo) {
          const posicionBackendX = newLinkedSonInfo.posicionX ?? 0
          const posicionBackendY = newLinkedSonInfo.posicionY ?? 0
          const posicionBackendZ = newLinkedSonInfo.posicionZ ?? 0

          if (isNaN(posicionBackendX) || isNaN(posicionBackendY) || isNaN(posicionBackendZ)) {
            currElement.x = newX
            currElement.y = newY
            currElement.z = newZ
          } else {
            currElement.x = posicionBackendX
            currElement.y = posicionBackendY
            currElement.z = posicionBackendZ
            ;(currElement as any).baseY = posicionBackendY
          }
        } else {
          currElement.x = newX
          currElement.y = newY
          currElement.z = newZ
        }

        currElement.width = prevSize.width
        currElement.height = prevSize.height
        currElement.thickness = prevSize.thickness
        currElement.morphs = prevMorphs
        currElement.referenciaBlender = unidad.referenciaBlender
        currElement.glbPath = String(unidad?.referenciaBlender ?? '')
        if (unidad.codigo !== undefined && unidad.codigo !== null) {
          s.sceneGLB.set(String(unidad.codigo), { glb, backGLBInfo: unidad })
        }

        if (!isLinkedItem(parent)) return
        const parentObj = s.items.get(prevItem.firstParentID as AnyID)
        if (parentObj && isLinkedItem(parentObj)) {
          parentObj.childrenOrder = newChildrenOrder.sort(depthVisualOrderSorter)
        }
        currElement.typeOfSon =
          currElement.typeOfSon ||
          childrenInfo?.typeOfSon ||
          currElement.description ||
          'Desconocido'

        if (currElement.description !== 'Tirador') {
          const x = currElement.x ?? 0
          const y = currElement.y ?? 0
          const z = currElement.z ?? 0
          currElement.visible = !(x === 0 && y === 0 && z === 0)
        }

        if (currElement.description === 'Tirador' && newLinkedSonInfo) {
          const posicionesAlternativas = [
            'biPosicionX',
            'biPosicionY',
            'biPosicionZ',
            'bcPosicionX',
            'bcPosicionY',
            'bcPosicionZ',
            'bdPosicionX',
            'bdPosicionY',
            'bdPosicionZ',
            'aiPosicionX',
            'aiPosicionY',
            'aiPosicionZ',
            'acPosicionX',
            'acPosicionY',
            'acPosicionZ',
            'adPosicionX',
            'adPosicionY',
            'adPosicionZ',
            'biRotacionX',
            'biRotacionY',
            'biRotacionZ',
            'bcRotacionX',
            'bcRotacionY',
            'bcRotacionZ',
            'bdRotacionX',
            'bdRotacionY',
            'bdRotacionZ',
            'aiRotacionX',
            'aiRotacionY',
            'aiRotacionZ',
            'acRotacionX',
            'acRotacionY',
            'acRotacionZ',
            'adRotacionX',
            'adRotacionY',
            'adRotacionZ',
            'b2idRotacionX',
            'b2idRotacionY',
            'b2idRotacionZ',
            'a2idRotacionX',
            'a2idRotacionY',
            'a2idRotacionZ',
          ]

          const drawerNode = getTiradorDrawerNode(currElement)
          const newTirador: LinkedItemSon = {
            ...currElement,
            id: currElement.id,
            nodo: drawerNode,
            option: drawerNode,
            parentOption: currElement.parentOption,
            handPosition: currElement.handPosition || 'Centro',
            catalogID: newLinkedSonInfo.unidad?.codigo ?? currElement.catalogID ?? '',
            linkedID: targetIdElementosEnlazados,
            idElementosEnlazados: targetIdElementosEnlazados,
            morphs: currElement.morphs || newLinkedSonInfo.unidad?.morphs || [],
          }
          posicionesAlternativas.forEach((key) => {
            if (Object.prototype.hasOwnProperty.call(newLinkedSonInfo, key)) {
              // @ts-expect-error: Puede que no esté tipado en el DTO, pero lo copiamos si existe
              newTirador[key] = newLinkedSonInfo[key]
            }
          })
          if (newLinkedSonInfo.unidad?.morphs) {
            newTirador.morphs = mapMorphsFromDTO(newLinkedSonInfo.unidad.morphs)
          }
          if (typeof newLinkedSonInfo.posicionX === 'number')
            newTirador.x = newLinkedSonInfo.posicionX
          if (typeof newLinkedSonInfo.posicionY === 'number')
            newTirador.y = newLinkedSonInfo.posicionY
          if (typeof newLinkedSonInfo.posicionZ === 'number')
            newTirador.z = newLinkedSonInfo.posicionZ
          if (newLinkedSonInfo.unidad?.referenciaBlender)
            newTirador.glbPath = String(newLinkedSonInfo.unidad.referenciaBlender)

          const nuevaPos = calculateTiradorPosition(newTirador, parentItem)
          newTirador.x = nuevaPos.x
          newTirador.y = nuevaPos.y
          newTirador.z = nuevaPos.z

          s.items.set(currElement.id, newTirador)
        }
      })
      return
    }

    const parentOption = prevItem.parentOption
    const option = prevItem.option
    const optionsArray = info[parentOption]?.[option]
    const foundOption = Object.values(optionsArray ?? {}).find((el) => {
      const opt = el as unknown as ItemWithOptions
      return (
        typeof opt === 'object' &&
        opt !== null &&
        'idUnidadEnlazada' in opt &&
        opt.idUnidadEnlazada === idElementosEnlazados
      )
    }) as (ItemWithOptions & { codigo?: string | number }) | undefined
    const codigo = foundOption?.codigo
    if (!codigo) {
      return
    }

    const glb = await getGLB(sceneGLB, String(codigo))
    const backData = newLinkedSonInfo.unidad

    set((s) => {
      const currElement = s.items.get(linkedID as AnyID)
      if (!isLinkedSon(currElement)) return
      const prevPosition = { x: currElement.x, y: currElement.y, z: currElement.z }
      const prevSize = {
        width: currElement.width,
        height: currElement.height,
        thickness: currElement.thickness,
      }
      const prevMorphs = currElement.morphs
      let newX = prevPosition.x
      let newY = prevPosition.y
      let newZ = prevPosition.z
      if (currElement.description === 'Tirador' && parent) {
        const distToLeft = prevPosition.x - parent.x
        const distToBase = parent.y + parent.height - prevPosition.y
        const distToBack = parent.z + parent.thickness - prevPosition.z
        newX = parent.x + distToLeft
        newY = parent.y + parent.height - distToBase
        newZ = parent.z + parent.thickness - distToBack
      }

      currElement.glbData = glb
      currElement.backGLBInfo = {
        idUnidades: backData?.idUnidades ?? 0,
        linkedID: backData?.idUnidades ?? 0,
        active: true,
        codigo: backData?.codigo ?? '',
        referenciaBlender: backData?.referenciaBlender ?? '',
        elemento: backData?.elemento,
        descripcion: backData?.descripcion,
        descripcionLarga: backData?.descripcionLarga,
        ancho: backData?.ancho,
        fondo: backData?.fondo,
        alto: backData?.alto,
        mano: backData?.mano,
        altura: backData?.altura,
        alturaHaciaAbajo: backData?.alturaHaciaAbajo,
        colision: backData?.colision,
        cota: backData?.cota,
        tipoNormalizado: backData?.tipoNormalizado ?? 'MBJ',
        tipoUnidNorm: backData?.tipoUnidNorm ?? '',
        modificarAncho: backData?.modificarAncho,
        modificarFondo: backData?.modificarFondo,
        modificarAlto: backData?.modificarAlto,
        morphs: backData?.morphs ?? [],
        precio: backData?.precio,
        acabados: backData?.acabados ?? [],
        unidadesLuces: backData?.unidadesLuces ?? [],
        elementosEnlazados: backData?.elementosEnlazados ?? [],
        referenciaJpgpng: backData?.referenciaJpgpng,
        universal: false,
      } as unknown as BackGLBInfo
      currElement.visible = true
      currElement.icono = ''
      currElement.matrix = new Matrix4()
      currElement.linkedID = idElementosEnlazados
      currElement.catalogID = codigo

      currElement.x = newX
      currElement.y = newY
      currElement.z = newZ
        currElement.width = prevSize.width
        currElement.height = prevSize.height
        currElement.thickness = prevSize.thickness
        currElement.morphs = prevMorphs

        if (currElement.description !== 'Tirador') {
          const x = currElement.x ?? 0
          const y = currElement.y ?? 0
          const z = currElement.z ?? 0
          currElement.visible = !(x === 0 && y === 0 && z === 0)
        }

      if (newLinkedSonInfo.unidad) {
        currElement.glbPath = String(newLinkedSonInfo.unidad.referenciaBlender ?? '')
        if (
          newLinkedSonInfo.unidad.codigo !== undefined &&
          newLinkedSonInfo.unidad.codigo !== null
        ) {
          s.sceneGLB.set(String(newLinkedSonInfo.unidad.codigo), {
            glb,
            backGLBInfo: {
              idUnidades: newLinkedSonInfo.unidad?.idUnidades ?? 0,
              linkedID: newLinkedSonInfo.unidad?.idUnidades ?? 0,
              active: true,
              codigo: newLinkedSonInfo.unidad?.codigo ?? '',
              referenciaBlender: newLinkedSonInfo.unidad?.referenciaBlender ?? '',
              elemento: newLinkedSonInfo.unidad?.elemento,
              descripcion: newLinkedSonInfo.unidad?.descripcion,
              descripcionLarga: newLinkedSonInfo.unidad?.descripcionLarga,
              ancho: newLinkedSonInfo.unidad?.ancho,
              fondo: newLinkedSonInfo.unidad?.fondo,
              alto: newLinkedSonInfo.unidad?.alto,
              mano: newLinkedSonInfo.unidad?.mano,
              altura: newLinkedSonInfo.unidad?.altura,
              alturaHaciaAbajo: newLinkedSonInfo.unidad?.alturaHaciaAbajo,
              colision: newLinkedSonInfo.unidad?.colision,
              cota: newLinkedSonInfo.unidad?.cota,
              tipoNormalizado: newLinkedSonInfo.unidad?.tipoNormalizado ?? 'MBJ',
              tipoUnidNorm: newLinkedSonInfo.unidad?.tipoUnidNorm ?? '',
              modificarAncho: newLinkedSonInfo.unidad?.modificarAncho,
              modificarFondo: newLinkedSonInfo.unidad?.modificarFondo,
              modificarAlto: newLinkedSonInfo.unidad?.modificarAlto,
              morphs: newLinkedSonInfo.unidad?.morphs ?? [],
              precio: newLinkedSonInfo.unidad?.precio,
              acabados: newLinkedSonInfo.unidad?.acabados ?? [],
              unidadesLuces: newLinkedSonInfo.unidad?.unidadesLuces ?? [],
              elementosEnlazados: newLinkedSonInfo.unidad?.elementosEnlazados ?? [],
              referenciaJpgpng: newLinkedSonInfo.unidad?.referenciaJpgpng,
              universal: newLinkedSonInfo.unidad?.universal,
            } as unknown as BackGLBInfo,
          })
        }
      }

      currElement.typeOfSon =
        currElement.typeOfSon || prevItem.typeOfSon || prevItem.description || 'Desconocido'

      if (currElement.description === 'Tirador') {
        currElement.handPosition = currElement.handPosition || 'Centro'
        const nuevaPos = calculateTiradorPosition(currElement, parentItem)
        currElement.x = nuevaPos.x
        currElement.y = nuevaPos.y
        currElement.z = nuevaPos.z
        get().setLinkedHandPosition(currElement.id as LinkedItemID, currElement.handPosition)
      }
    })

    const updatedParentItem = get().items.get(parentID as AnyID)
    if (
      updatedParentItem &&
      updatedParentItem.type === 'linkedItem' &&
      updatedParentItem.childs &&
      Array.isArray(updatedParentItem.childs) &&
      updatedParentItem.childs.length > 0
    ) {
      adjustChildrenPositionsAfterInheritance(
        updatedParentItem,
        (childId) => get().items.get(childId),
        (childId, updates) => {
          get().editItem(childId, updates)
        },
      )
    }
  },

  updateLinkedGLBForGroup: async (
    parentIDs: Set<string>,
    idElementosEnlazados: number,
    info: Options,
    childDescription: string = 'Tirador',
    idElementosEnlazadosAnt?: number,
    modeEditor?: 'Single' | 'All' | 'Selected' | 'MBJ' | 'MAL' | 'COL',
    childIDsFilter?: Set<string>,
  ) => {
    logAcabado('updateLinkedGLBForGroup_START', {
      parentIDs: Array.from(parentIDs),
      idElementosEnlazados,
      idElementosEnlazadosAnt,
      childDescription,
      modeEditor,
      childIDsFilter: childIDsFilter ? Array.from(childIDsFilter) : undefined,
    })

    const batchActive = isLinkedVisualBatchActive()
    let originalFrameloop: Store['frameloop'] = 'demand'
    if (!batchActive) {
      set((store) => {
        originalFrameloop = store.frameloop
        store.frameloop = 'always'
      })
    }

    const loader = await setGLTFLoader()
    const state = get()

    try {
      if (childDescription === 'Fregadero' && modeEditor === 'All') {
        const allLinkedItems = Array.from(state.items.values()).filter(
          (item) => item.type === 'linkedItem',
        )

        for (const linkedItem of allLinkedItems) {
          if (!isLinkedItem(linkedItem) || !linkedItem.childs || linkedItem.childs.length === 0) {
            continue
          }

          for (const childID of linkedItem.childs) {
            const childItem = state.items.get(childID as AnyID)
            if (!isLinkedSon(childItem)) continue

            if (childItem.description === 'Fregadero' || childItem.typeOfSon === 'Fregadero') {
              if (linkedItem.fregaderoOptions && linkedItem.fregaderoOptions.length > 0) {
                let selectedDescription = null

                for (const parentKey in info) {
                  if (info[parentKey] && typeof info[parentKey] === 'object') {
                    for (const childKey in info[parentKey]) {
                      const childOptions = info[parentKey][childKey]
                      if (Array.isArray(childOptions)) {
                        const found = childOptions.find(
                          (item: any) => item.idElementosEnlazados === idElementosEnlazados,
                        )
                        if (found && found.descripcion) {
                          selectedDescription = found.descripcion
                          break
                        }
                      }
                    }
                    if (selectedDescription) break
                  }
                }

                if (selectedDescription) {
                  const selectedOption = linkedItem.fregaderoOptions.find(
                    (opt: any) => opt.descripcion === selectedDescription,
                  )

                  if (selectedOption) {
                    if (selectedOption.idElementosEnlazados) {
                      set((s) => {
                        const child = s.items.get(childID as AnyID)
                        if (child && isLinkedSon(child)) {
                          child.linkedID = selectedOption.idElementosEnlazados
                          child.idUnidades = selectedOption.idUnidadEnlazada
                        }
                      })

                      await get().updateLinkedGLB(
                        childID,
                        selectedOption.idElementosEnlazados,
                        info,
                      )
                    } else {
                      console.warn(
                        `[DEBUG] updateLinkedGLBForGroup - No se pudo obtener idElementosEnlazados para mueble ${linkedItem.id}`,
                      )
                    }
                  } else {
                    console.warn(
                      `[DEBUG] updateLinkedGLBForGroup - No se encontró opción para descripción "${selectedDescription}" en fregaderoOptions del mueble ${linkedItem.id}`,
                    )
                  }
                } else {
                  console.warn(
                    `[DEBUG] updateLinkedGLBForGroup - No se pudo extraer la descripción del elemento seleccionado para mueble ${linkedItem.id}`,
                  )
                }
              } else {
                console.warn(
                  `[DEBUG] updateLinkedGLBForGroup - Mueble ${linkedItem.id} no tiene fregaderoOptions guardadas`,
                )
              }
            }
          }
        }

        return
      }

      // NOTA: Este bloque está comentado porque estaba causando que todos los grifos
      // obtuvieran el mismo idElementosEnlazados cuando se cambiaba el GLB del padre.
      // Cuando se llama desde LinkedTexturesEditor.tsx (modeEditor undefined), cada grifo
      // debe procesarse individualmente según su nodo en la lógica normal más abajo.
      /*
      if (
        (childDescription === 'Grifo' || childDescription?.toLowerCase().includes('grifo')) &&
        !modeEditor
      ) {
        const allLinkedItems = Array.from(state.items.values()).filter(
          (item) => item.type === 'linkedItem',
        )

        for (const linkedItem of allLinkedItems) {
          if (!isLinkedItem(linkedItem) || !linkedItem.childs || linkedItem.childs.length === 0) {
            continue
          }

          for (const childID of linkedItem.childs) {
            const childItem = state.items.get(childID as AnyID)
            if (!isLinkedSon(childItem)) continue

            if (
              childItem.description?.toLowerCase().includes('grifo') ||
              childItem.typeOfSon?.toLowerCase().includes('grifo')
            ) {
              set((s) => {
                const child = s.items.get(childID as AnyID)
                if (child && isLinkedSon(child)) {
                  child.linkedID = idElementosEnlazados
                }
              })

              try {
                await get().updateLinkedGLB(childID, idElementosEnlazados, info)
              } catch (error) {
                console.error('❌ GRIFO DEBUG - Error en updateLinkedGLB:', error)
              }
            }
          }
        }

        return
      }
      */

      if (childDescription === 'TPCGrifo' && modeEditor === 'All') {
        const allLinkedItems = Array.from(state.items.values()).filter(
          (item) => item.type === 'linkedItem',
        )

        for (const linkedItem of allLinkedItems) {
          if (!isLinkedItem(linkedItem) || !linkedItem.childs || linkedItem.childs.length === 0) {
            continue
          }

          for (const childID of linkedItem.childs) {
            const childItem = state.items.get(childID as AnyID)
            if (!isLinkedSon(childItem)) continue

            if (childItem.description === 'TPCGrifo' || childItem.typeOfSon === 'TPCGrifo') {
              let selectedDescription = null
              for (const parentKey in info) {
                if (info[parentKey] && typeof info[parentKey] === 'object') {
                  for (const childKey in info[parentKey]) {
                    const childOptions = info[parentKey][childKey]
                    if (Array.isArray(childOptions)) {
                      const found = childOptions.find(
                        (item: any) => item.idElementosEnlazados === idElementosEnlazados,
                      )
                      if (found && found.descripcion) {
                        selectedDescription = found.descripcion
                        break
                      }
                    }
                  }
                  if (selectedDescription) break
                }
              }

              let correctIdElementosEnlazados: number | null = null
              let backendPositions: { x: number; y: number; z: number } | null = null

              if (
                selectedDescription &&
                linkedItem.grifoOptions &&
                linkedItem.grifoOptions.length > 0
              ) {
                const matchingOption = linkedItem.grifoOptions.find(
                  (opt: any) => opt.descripcion === selectedDescription,
                )

                if (matchingOption && matchingOption.idElementosEnlazados) {
                  correctIdElementosEnlazados = matchingOption.idElementosEnlazados

                  if (linkedItem.idUnidades) {
                    try {
                      const backendResponse =
                        await API().api.acabadosGetElementosEnlazadosUnidadAcabados2(
                          linkedItem.idUnidades,
                          correctIdElementosEnlazados,
                        )
                      if (backendResponse?.data) {
                        const backendData = backendResponse.data
                        backendPositions = {
                          x: backendData.posicionX ?? childItem.x,
                          y: backendData.posicionY ?? childItem.y,
                          z: backendData.posicionZ ?? childItem.z,
                        }
                      }
                    } catch (error) {
                      console.warn(
                        `❌ [TPCGrifo All] Error obteniendo posiciones del backend para ${childID}:`,
                        error,
                      )
                    }
                  }
                }
              }

              // Usar las posiciones del backend si están disponibles, sino usar las actuales
              const positionsToPreserve = backendPositions || {
                x: childItem.x,
                y: childItem.y,
                z: childItem.z,
              }

              if (selectedDescription) {
                set((s) => {
                  const child = s.items.get(childID as AnyID)
                  if (child && isLinkedSon(child)) {
                    child.linkedID = idElementosEnlazados

                    for (const parentKey in info) {
                      if (info[parentKey] && typeof info[parentKey] === 'object') {
                        for (const childKey in info[parentKey]) {
                          const childOptions = info[parentKey][childKey]
                          if (Array.isArray(childOptions)) {
                            const found = childOptions.find(
                              (item: any) => item.idElementosEnlazados === idElementosEnlazados,
                            )
                            if (found && found.idUnidadEnlazada) {
                              child.idUnidades = found.idUnidadEnlazada
                              break
                            }
                          }
                        }
                      }
                    }
                  }
                })

                try {
                  // Guardar las posiciones a preservar antes de updateLinkedGLB
                  const positionsToRestore = positionsToPreserve ? { ...positionsToPreserve } : null

                  await get().updateLinkedGLB(childID, idElementosEnlazados, info)

                  // Función para restaurar posiciones
                  const restorePositions = () => {
                    if (positionsToRestore) {
                      set((s) => {
                        const child = s.items.get(childID as AnyID)
                        if (child && isLinkedSon(child)) {
                          child.x = positionsToRestore.x
                          child.y = positionsToRestore.y
                          child.z = positionsToRestore.z
                          ;(child as any).baseY = positionsToRestore.y
                        }
                      })
                    }
                  }

                  restorePositions()

                  setTimeout(() => {
                    restorePositions()
                  }, 100)

                  // Restaurar después de un delay más largo (por si hay más callbacks)
                  setTimeout(() => {
                    restorePositions()
                    // Ajustar la posición Y según el acabado de la encimera
                    const positionFregaderoGrifoPlaca =
                      get().positionFregaderoGrifoPlacaAccordingToEncimera
                    if (positionFregaderoGrifoPlaca) {
                      positionFregaderoGrifoPlaca(childID)
                    }
                  }, 500)
                } catch (error) {
                  console.error('❌ TPCGRIFO DEBUG - Error en updateLinkedGLB:', error)
                }
              }
            }
          }
        }

        return
      }

      if (
        (childDescription === 'Grifo' || childDescription?.toLowerCase().includes('grifo')) &&
        modeEditor === 'All'
      ) {
        const allLinkedItems = Array.from(state.items.values()).filter(
          (item) => item.type === 'linkedItem',
        )

        for (const linkedItem of allLinkedItems) {
          if (!isLinkedItem(linkedItem) || !linkedItem.childs || linkedItem.childs.length === 0) {
            continue
          }

          for (const childID of linkedItem.childs) {
            const childItem = state.items.get(childID as AnyID)
            if (!isLinkedSon(childItem)) continue

            if (
              childItem.description?.toLowerCase().includes('grifo') ||
              childItem.typeOfSon?.toLowerCase().includes('grifo')
            ) {
              // Guardar el linkedID original y las posiciones del grifo actual
              const originalLinkedID = childItem.linkedID
              let originalPositions: { x: number; y: number; z: number } | null = null

              // Obtener las posiciones originales del backend usando el linkedID del grifo actual
              if (originalLinkedID && linkedItem.idUnidades) {
                try {
                  const originalResponse =
                    await API().api.acabadosGetElementosEnlazadosUnidadAcabados2(
                      linkedItem.idUnidades,
                      originalLinkedID,
                    )
                  if (originalResponse?.data) {
                    const originalData = originalResponse.data
                    originalPositions = {
                      x: originalData.posicionX ?? childItem.x,
                      y: originalData.posicionY ?? childItem.y,
                      z: originalData.posicionZ ?? childItem.z,
                    }
                  }
                } catch (error) {
                  console.warn('❌ Error obteniendo posiciones originales del grifo:', error)
                  // Si falla, usar las posiciones actuales del grifo
                  originalPositions = {
                    x: childItem.x,
                    y: childItem.y,
                    z: childItem.z,
                  }
                }
              } else {
                // Si no hay linkedID original, usar las posiciones actuales
                originalPositions = {
                  x: childItem.x,
                  y: childItem.y,
                  z: childItem.z,
                }
              }

              if (linkedItem.grifoOptions && linkedItem.grifoOptions.length > 0) {
                let selectedDescription = null

                for (const parentKey in info) {
                  if (info[parentKey] && typeof info[parentKey] === 'object') {
                    for (const childKey in info[parentKey]) {
                      const childOptions = info[parentKey][childKey]
                      if (Array.isArray(childOptions)) {
                        const found = childOptions.find(
                          (item: any) => item.idElementosEnlazados === idElementosEnlazados,
                        )
                        if (found && found.descripcion) {
                          selectedDescription = found.descripcion
                          break
                        }
                      }
                    }
                    if (selectedDescription) break
                  }
                }

                if (selectedDescription) {
                  const selectedOption = linkedItem.grifoOptions.find(
                    (opt: any) => opt.descripcion === selectedDescription,
                  )

                  if (selectedOption) {
                    if (selectedOption.idElementosEnlazados) {
                      set((s) => {
                        const child = s.items.get(childID as AnyID)
                        if (child && isLinkedSon(child)) {
                          child.linkedID = selectedOption.idElementosEnlazados
                          child.idUnidades = selectedOption.idUnidadEnlazada
                        }
                      })

                      await get().updateLinkedGLB(
                        childID,
                        selectedOption.idElementosEnlazados,
                        info,
                      )

                      // Restaurar las posiciones originales después de cambiar el modelo
                      if (originalPositions) {
                        set((s) => {
                          const child = s.items.get(childID as AnyID)
                          if (child && isLinkedSon(child)) {
                            child.x = originalPositions!.x
                            child.y = originalPositions!.y
                            child.z = originalPositions!.z
                            ;(child as any).baseY = originalPositions!.y
                          }
                        })

                        // Ajustar la posición Y según el acabado de la encimera después de restaurar
                        setTimeout(() => {
                          const positionFregaderoGrifoPlaca =
                            get().positionFregaderoGrifoPlacaAccordingToEncimera
                          if (positionFregaderoGrifoPlaca) {
                            positionFregaderoGrifoPlaca(childID)
                          }
                        }, 100)
                      }
                    } else {
                      console.warn(
                        `[DEBUG] updateLinkedGLBForGroup - No se pudo obtener idElementosEnlazados para mueble ${linkedItem.id}`,
                      )
                    }
                  } else {
                    console.warn(
                      `[DEBUG] updateLinkedGLBForGroup - No se encontró opción para descripción "${selectedDescription}" en grifoOptions del mueble ${linkedItem.id}`,
                    )
                  }
                } else {
                  console.warn(
                    `[DEBUG] updateLinkedGLBForGroup - No se pudo extraer la descripción del elemento seleccionado para mueble ${linkedItem.id}`,
                  )
                }
              } else {
                console.warn(
                  `[DEBUG] updateLinkedGLBForGroup - Mueble ${linkedItem.id} no tiene grifoOptions guardadas`,
                )
              }
            }
          }
        }

        return
      }

      if (childDescription === 'Placa' && modeEditor === 'All') {
        const allLinkedItems = Array.from(state.items.values()).filter(
          (item) => item.type === 'linkedItem',
        )

        for (const linkedItem of allLinkedItems) {
          if (!isLinkedItem(linkedItem) || !linkedItem.childs || linkedItem.childs.length === 0) {
            continue
          }

          for (const childID of linkedItem.childs) {
            const childItem = state.items.get(childID as AnyID)
            if (!isLinkedSon(childItem)) continue

            if (childItem.description === 'Placa' || childItem.typeOfSon === 'Placa') {
              if (linkedItem.placaOptions && linkedItem.placaOptions.length > 0) {
                let selectedDescription = null

                for (const parentKey in info) {
                  if (info[parentKey] && typeof info[parentKey] === 'object') {
                    for (const childKey in info[parentKey]) {
                      const childOptions = info[parentKey][childKey]
                      if (Array.isArray(childOptions)) {
                        const found = childOptions.find(
                          (item: any) => item.idElementosEnlazados === idElementosEnlazados,
                        )
                        if (found && found.descripcion) {
                          selectedDescription = found.descripcion
                          break
                        }
                      }
                    }
                    if (selectedDescription) break
                  }
                }

                if (selectedDescription) {
                  const selectedOption = linkedItem.placaOptions.find(
                    (opt: any) => opt.descripcion === selectedDescription,
                  )

                  if (selectedOption) {
                    if (selectedOption.idElementosEnlazados) {
                      set((s) => {
                        const child = s.items.get(childID as AnyID)
                        if (child && isLinkedSon(child)) {
                          child.linkedID = selectedOption.idElementosEnlazados
                          child.idUnidades = selectedOption.idUnidadEnlazada
                        }
                      })

                      await get().updateLinkedGLB(
                        childID,
                        selectedOption.idElementosEnlazados,
                        info,
                      )
                    } else {
                      console.warn(
                        `[DEBUG] updateLinkedGLBForGroup - No se pudo obtener idElementosEnlazados para mueble ${linkedItem.id}`,
                      )
                    }
                  } else {
                    console.warn(
                      `[DEBUG] updateLinkedGLBForGroup - No se encontró opción para descripción "${selectedDescription}" en placaOptions del mueble ${linkedItem.id}`,
                    )
                  }
                } else {
                  console.warn(
                    `[DEBUG] updateLinkedGLBForGroup - No se pudo extraer la descripción del elemento seleccionado para mueble ${linkedItem.id}`,
                  )
                }
              } else {
                console.warn(
                  `[DEBUG] updateLinkedGLBForGroup - Mueble ${linkedItem.id} no tiene placaOptions guardadas`,
                )
              }
            }
          }
        }

        return
      }

      const parentIDsArray = Array.from(parentIDs)
      const BATCH_SIZE = 5
      let processedCount = 0

      for (const parentID of parentIDsArray) {
        const parentItem = state.items.get(parentID as AnyID)
        if (!parentItem || !isLinkedItem(parentItem)) {
          continue
        }

        const childrenToProcess = Array.from(parentItem.childrenOrder)

        for (let i = 0; i < childrenToProcess.length; i++) {
          const childKey = childrenToProcess[i]
          const childID = childKey.split('!')[1]
          const childItem = state.items.get(childID as AnyID)
          if (!childItem || !isLinkedSon(childItem)) {
            continue
          }
          // Filtrar hijos por descripción, pero permitir grifos/TPCGrifos cuando childDescription es 'Grifo'
          // También permitir fregaderos cuando childDescription es 'Fregadero'
          const isGrifoMatch =
            (childDescription === 'Grifo' || childDescription === 'TPCGrifo') &&
            (childItem.description?.toLowerCase().includes('grifo') ||
              childItem.typeOfSon === 'Grifo' ||
              childItem.typeOfSon === 'TPCGrifo')

          const isFregaderoMatch =
            childDescription === 'Fregadero' &&
            (childItem.description?.toLowerCase().includes('fregadero') ||
              childItem.typeOfSon === 'Fregadero')

          if (childItem.description !== childDescription && !isGrifoMatch && !isFregaderoMatch) {
            continue
          }

          if (childIDsFilter && !childIDsFilter.has(childID)) {
            continue
          }

          try {
            let targetIdElementosEnlazados = idElementosEnlazados
            let targetIdElementosEnlazadosAnt = idElementosEnlazadosAnt

            // Si idElementosEnlazadosAnt no se proporcionó, usar el linkedID actual del hijo
            // Si tampoco hay linkedID, usar el nuevo idElementosEnlazados en ambos (lo que quiere el usuario)
            if (targetIdElementosEnlazadosAnt === undefined) {
              targetIdElementosEnlazadosAnt = childItem.linkedID || idElementosEnlazados
            }

            // Para tipos genéricos (Micro, Horno, HornoMicro, etc.), buscar el idElementosEnlazados correcto
            // basado en el nodo actual y el idUnidadEnlazada de la opción seleccionada
            const safeInfo = info || {}
            if (
              childDescription !== 'Tirador' &&
              childDescription !== 'Placa' &&
              childDescription !== 'Grifo' &&
              childDescription !== 'TPCGrifo' &&
              childDescription !== 'Fregadero' &&
              !childDescription?.toLowerCase().includes('grifo')
            ) {
              const currentNode = childItem.nodo
              const currentParentOption = childItem.parentOption

              // Solo intentar buscar la opción correcta si tenemos toda la información necesaria
              // Si no, usamos el idElementosEnlazados que se pasó directamente
              if (currentNode !== undefined && currentParentOption !== undefined) {
                // Buscar en safeInfo la opción con el idElementosEnlazados que pasamos
                // usando el parentOption del hijo
                const parentOptions = safeInfo[String(currentParentOption)]
                if (parentOptions && typeof parentOptions === 'object') {
                  const childOptions = parentOptions[childDescription]
                  if (Array.isArray(childOptions) && childOptions.length > 0) {
                    // Primero encontrar la opción seleccionada para obtener su idUnidadEnlazada
                    const selectedOption = childOptions.find(
                      (item: any) => item.idElementosEnlazados === idElementosEnlazados,
                    )

                    if (selectedOption) {
                      const newIdUnidadEnlazada = selectedOption.idUnidadEnlazada

                      // Buscar la opción que coincida con el nodo actual y el nuevo idUnidadEnlazada
                      const matchingOption = childOptions.find(
                        (option: any) =>
                          option.nodo === currentNode &&
                          option.idUnidadEnlazada === newIdUnidadEnlazada,
                      )

                      if (matchingOption) {
                        targetIdElementosEnlazados = matchingOption.idElementosEnlazados
                        // Si targetIdElementosEnlazadosAnt era undefined y se estableció como idElementosEnlazados inicial,
                        // actualizarlo al nuevo targetIdElementosEnlazados encontrado
                        if (targetIdElementosEnlazadosAnt === idElementosEnlazados) {
                          targetIdElementosEnlazadosAnt = targetIdElementosEnlazados
                        }
                      } else {
                        // Si no encontramos la opción, usamos el idElementosEnlazados que se pasó
                        // No hacemos continue, simplemente seguimos con el valor original
                        if (process.env.NODE_ENV === 'development') {
                          console.warn(
                            `No se encontró opción en ${childDescription} para nodo ${currentNode} con idUnidadEnlazada ${newIdUnidadEnlazada} en parentOption ${currentParentOption}, usando idElementosEnlazados original: ${idElementosEnlazados}`,
                          )
                        }
                      }
                    } else {
                      // Si no encontramos la opción seleccionada, usamos el idElementosEnlazados que se pasó
                      // No hacemos continue, simplemente seguimos con el valor original
                      if (process.env.NODE_ENV === 'development') {
                        console.warn(
                          `No se encontró la opción seleccionada con idElementosEnlazados ${idElementosEnlazados} para ${childDescription} en parentOption ${currentParentOption}, usando idElementosEnlazados original`,
                        )
                      }
                    }
                  }
                }
              }
              // Si currentNode o currentParentOption son undefined, simplemente continuamos
              // con el idElementosEnlazados que se pasó (no es un error, puede ser válido)
            }

            if (childDescription === 'Tirador') {
              let newIdUnidades = null

              for (const parentKey in info) {
                if (info[parentKey] && typeof info[parentKey] === 'object') {
                  for (const childKey in info[parentKey]) {
                    const childOptions = info[parentKey][childKey]
                    if (Array.isArray(childOptions)) {
                      const found = childOptions.find(
                        (item: any) => item.idElementosEnlazados === idElementosEnlazados,
                      )
                      if (found && found.idUnidadEnlazada) {
                        newIdUnidades = found.idUnidadEnlazada
                        break
                      }
                    }
                  }
                  if (newIdUnidades) break
                }
              }

              if (newIdUnidades) {
                try {
                  const options =
                    await API().api.acabadosGetElementosEnlazadosUnidadAcabados(newIdUnidades)
                  if (options.data && options.data['0'] && options.data['0']['Tirador']) {
                    set((s) => {
                      const item = s.items.get(parentID as AnyID)
                      if (item && isLinkedItem(item)) {
                        item.tiradorOptions = options.data['0']['Tirador'] as ItemWithOptions[]
                      }
                    })

                    const updatedParentItem = get().items.get(parentID as AnyID)
                    if (updatedParentItem && isLinkedItem(updatedParentItem)) {
                      parentItem.tiradorOptions = updatedParentItem.tiradorOptions
                    }
                  }

                  if (options.data && options.data['0'] && options.data['0']['Placa']) {
                    set((s) => {
                      const item = s.items.get(parentID as AnyID)
                      if (item && isLinkedItem(item)) {
                        item.placaOptions = options.data['0']['Placa'] as ItemWithOptions[]
                      }
                    })

                    const updatedParentItem = get().items.get(parentID as AnyID)
                    if (updatedParentItem && isLinkedItem(updatedParentItem)) {
                      parentItem.placaOptions = updatedParentItem.placaOptions
                    }
                  }

                  if (options.data && options.data['0'] && options.data['0']['TPCGrifo']) {
                    set((s) => {
                      const item = s.items.get(parentID as AnyID)
                      if (item && isLinkedItem(item)) {
                        item.grifoOptions = options.data['0']['TPCGrifo'] as ItemWithOptions[]
                      }
                    })

                    const updatedParentItem = get().items.get(parentID as AnyID)
                    if (updatedParentItem && isLinkedItem(updatedParentItem)) {
                      parentItem.grifoOptions = updatedParentItem.grifoOptions
                    }
                  } else {
                    console.log(
                      `❌ Nueva unidad ${parentItem.id} NO tiene grifoOptions disponibles`,
                    )
                  }
                } catch (error) {
                  console.warn(
                    'Error obteniendo tiradorOptions para nueva unidad:',
                    newIdUnidades,
                    error,
                  )
                }
              }

              if (!parentItem.tiradorOptions && parentItem.idUnidades) {
                try {
                  const tiradorOptionsResponse =
                    await API().api.acabadosGetElementosEnlazadosUnidadAcabados(
                      parentItem.idUnidades,
                    )

                  if (
                    tiradorOptionsResponse.data &&
                    tiradorOptionsResponse.data['0'] &&
                    tiradorOptionsResponse.data['0']['Tirador']
                  ) {
                    set((s) => {
                      const item = s.items.get(parentID as AnyID)
                      if (item && isLinkedItem(item)) {
                        item.tiradorOptions = tiradorOptionsResponse.data['0'][
                          'Tirador'
                        ] as ItemWithOptions[]
                      }
                    })

                    const updatedParentItem = get().items.get(parentID as AnyID)
                    if (updatedParentItem && isLinkedItem(updatedParentItem)) {
                      parentItem.tiradorOptions = updatedParentItem.tiradorOptions
                    }
                  }
                } catch (error) {
                  console.warn(
                    'Error obteniendo tiradorOptions para unidad:',
                    parentItem.idUnidades,
                    error,
                  )
                }
              }

              if (!parentItem.placaOptions && parentItem.idUnidades) {
                try {
                  const placaOptionsResponse =
                    await API().api.acabadosGetElementosEnlazadosUnidadAcabados(
                      parentItem.idUnidades,
                    )

                  if (
                    placaOptionsResponse.data &&
                    placaOptionsResponse.data['0'] &&
                    placaOptionsResponse.data['0']['Placa']
                  ) {
                    set((s) => {
                      const item = s.items.get(parentID as AnyID)
                      if (item && isLinkedItem(item)) {
                        item.placaOptions = placaOptionsResponse.data['0'][
                          'Placa'
                        ] as ItemWithOptions[]
                      }
                    })

                    const updatedParentItem = get().items.get(parentID as AnyID)
                    if (updatedParentItem && isLinkedItem(updatedParentItem)) {
                      parentItem.placaOptions = updatedParentItem.placaOptions
                    }
                  }
                } catch (error) {
                  console.warn(
                    'Error obteniendo placaOptions para unidad:',
                    parentItem.idUnidades,
                    error,
                  )
                }
              }

              if (!parentItem.grifoOptions && parentItem.idUnidades) {
                try {
                  const grifoOptionsResponse =
                    await API().api.acabadosGetElementosEnlazadosUnidadAcabados(
                      parentItem.idUnidades,
                    )

                  if (
                    grifoOptionsResponse.data &&
                    grifoOptionsResponse.data['0'] &&
                    grifoOptionsResponse.data['0']['TPCGrifo']
                  ) {
                    set((s) => {
                      const item = s.items.get(parentID as AnyID)
                      if (item && isLinkedItem(item)) {
                        item.grifoOptions = grifoOptionsResponse.data['0'][
                          'TPCGrifo'
                        ] as ItemWithOptions[]
                      }
                    })
                    const updatedParentItem = get().items.get(parentID as AnyID)
                    if (updatedParentItem && isLinkedItem(updatedParentItem)) {
                      parentItem.grifoOptions = updatedParentItem.grifoOptions
                    }
                  }
                } catch (error) {
                  console.warn(
                    'Error obteniendo grifoOptions para unidad:',
                    parentItem.idUnidades,
                    error,
                  )
                }
              }

              if (parentItem.tiradorOptions) {
                const drawerNode = getTiradorDrawerNode(childItem)

                if (drawerNode !== undefined) {
                  targetIdElementosEnlazadosAnt = childItem.linkedID

                  const resolved = resolveTiradorIdElementosEnlazados(
                    parentItem.tiradorOptions,
                    childItem,
                    idElementosEnlazados,
                  )

                  if (resolved === null) {
                    console.warn(
                      `No se encontró opción en tiradorOptions para cajón ${drawerNode}`,
                    )
                    continue
                  }
                  targetIdElementosEnlazados = resolved
                } else {
                  console.warn(`Tirador ${childID} no tiene cajón (option/nodo) definido`)
                  continue
                }
              }
            }

            if (childDescription === 'Placa') {
              const placaParentItem = Array.from(state.items.values()).find((item) => {
                if (item.type === 'linkedItem' && item.childs) {
                  return Array.from(item.childs).includes(childID as AnyID)
                }
                return false
              })

              if (
                placaParentItem &&
                isLinkedItem(placaParentItem) &&
                placaParentItem.placaOptions
              ) {
                const currentNode = childItem.nodo

                if (currentNode !== undefined) {
                  targetIdElementosEnlazadosAnt = childItem.linkedID

                  const newPlacaOption = (placaParentItem.placaOptions as any[]).find(
                    (option: any) => option.idElementosEnlazados === idElementosEnlazados,
                  )

                  if (newPlacaOption) {
                    const newIdUnidadEnlazada = newPlacaOption.idUnidadEnlazada

                    const matchingOption = (placaParentItem.placaOptions as any[]).find(
                      (option: any) => {
                        return (
                          option.nodo === currentNode &&
                          option.idUnidadEnlazada === newIdUnidadEnlazada
                        )
                      },
                    )

                    if (matchingOption) {
                      targetIdElementosEnlazados = matchingOption.idElementosEnlazados

                      set((s) => {
                        const child = s.items.get(childID as AnyID)
                        if (child && isLinkedSon(child)) {
                          child.linkedID = matchingOption.idElementosEnlazados
                          child.idUnidades = matchingOption.idUnidadEnlazada
                        }
                      })
                    } else {
                      console.warn(
                        `No se encontró opción en placaOptions para nodo ${currentNode} con idUnidadEnlazada ${newIdUnidadEnlazada}`,
                      )
                      continue
                    }
                  } else {
                    console.warn(
                      `No se encontró el nuevo placa con idElementosEnlazados ${idElementosEnlazados}`,
                    )
                    continue
                  }
                } else {
                  console.warn(`Placa ${childID} no tiene nodo definido`)
                  continue
                }
              } else {
                console.warn(`No se encontró la unidad padre para la placa ${childID}`)
              }
            }

            if (childDescription === 'Grifo' || childDescription?.toLowerCase().includes('grifo')) {
              const grifoParentItem = Array.from(state.items.values()).find((item) => {
                if (item.type === 'linkedItem' && item.childs) {
                  return Array.from(item.childs).includes(childID as AnyID)
                }
                return false
              })

              if (
                grifoParentItem &&
                isLinkedItem(grifoParentItem) &&
                grifoParentItem.grifoOptions
              ) {
                // Intentar obtener el nodo del grifo actual
                // El nodo puede estar en childItem.nodo, o puede ser childItem.option
                let currentNode = childItem.nodo

                // Si no está definido, intentar usar option (que suele corresponder al nodo)
                if (currentNode === undefined) {
                  const childOption = (childItem as any).option
                  if (childOption !== undefined) {
                    currentNode = childOption
                  }
                }

                // Si aún no está definido, buscar el nodo en las opciones del padre usando el linkedID actual del grifo
                if (currentNode === undefined) {
                  const currentGrifoOption = (grifoParentItem.grifoOptions as any[]).find(
                    (option: any) => option.idElementosEnlazados === childItem.linkedID,
                  )
                  if (currentGrifoOption && currentGrifoOption.nodo !== undefined) {
                    currentNode = currentGrifoOption.nodo
                  }
                }

                if (currentNode !== undefined) {
                  targetIdElementosEnlazadosAnt = childItem.linkedID

                  const newGrifoOption = (grifoParentItem.grifoOptions as any[]).find(
                    (option: any) => option.idElementosEnlazados === idElementosEnlazados,
                  )

                  if (newGrifoOption) {
                    const newIdUnidadEnlazada = newGrifoOption.idUnidadEnlazada

                    const matchingOption = (grifoParentItem.grifoOptions as any[]).find(
                      (option: any) => {
                        return (
                          option.nodo === currentNode &&
                          option.idUnidadEnlazada === newIdUnidadEnlazada
                        )
                      },
                    )

                    if (matchingOption) {
                      targetIdElementosEnlazados = matchingOption.idElementosEnlazados

                      set((s) => {
                        const child = s.items.get(childID as AnyID)
                        if (child && isLinkedSon(child)) {
                          child.linkedID = matchingOption.idElementosEnlazados
                          child.idUnidades = matchingOption.idUnidadEnlazada
                        }
                      })
                    } else {
                      console.warn(
                        `⚠️ [UPDATE GLB FOR GROUP] No se encontró opción en grifoOptions para nodo ${currentNode} con idUnidadEnlazada ${newIdUnidadEnlazada}, usando idElementosEnlazados pasado como fallback`,
                        {
                          childID,
                          currentNode,
                          newIdUnidadEnlazada,
                          idElementosEnlazadosPasado: idElementosEnlazados,
                        },
                      )
                      // Usar el idElementosEnlazados pasado como fallback en lugar de hacer continue
                      targetIdElementosEnlazados = idElementosEnlazados
                    }
                  } else {
                    console.warn(
                      `⚠️ [UPDATE GLB FOR GROUP] No se encontró el nuevo grifo con idElementosEnlazados ${idElementosEnlazados}, usando como fallback`,
                      {
                        childID,
                        idElementosEnlazados,
                      },
                    )
                    // Usar el idElementosEnlazados pasado como fallback en lugar de hacer continue
                    targetIdElementosEnlazados = idElementosEnlazados
                  }
                } else {
                  console.warn(
                    `⚠️ [UPDATE GLB FOR GROUP] Grifo ${childID} no tiene nodo definido, usando idElementosEnlazados pasado como fallback`,
                    {
                      childID,
                      idElementosEnlazados,
                    },
                  )
                  // Usar el idElementosEnlazados pasado como fallback en lugar de hacer continue
                  targetIdElementosEnlazados = idElementosEnlazados
                }
              } else {
                console.warn(
                  `⚠️ [UPDATE GLB FOR GROUP] No se encontró la unidad padre para el grifo ${childID}, usando idElementosEnlazados pasado como fallback`,
                  {
                    childID,
                    idElementosEnlazados,
                  },
                )
                // Usar el idElementosEnlazados pasado como fallback en lugar de hacer continue
                targetIdElementosEnlazados = idElementosEnlazados
              }
            }

            let response
            try {
              let finalIdAnt = targetIdElementosEnlazadosAnt
              if (
                (childDescription === 'Micro' ||
                  childDescription === 'Horno' ||
                  childDescription === 'HornoMicro') &&
                (targetIdElementosEnlazadosAnt === undefined ||
                  targetIdElementosEnlazadosAnt === null)
              ) {
                finalIdAnt = targetIdElementosEnlazados
              } else if (
                targetIdElementosEnlazadosAnt === undefined ||
                targetIdElementosEnlazadosAnt === null
              ) {
                // Para otros tipos, usar el linkedID del hijo si existe, o calcular como antes
                const idAntiguo =
                  targetIdElementosEnlazadosAnt !== undefined
                    ? targetIdElementosEnlazadosAnt
                    : parentItem.idUnidades || targetIdElementosEnlazados

                const idNuevo = targetIdElementosEnlazados

                finalIdAnt =
                  idAntiguo === idNuevo || targetIdElementosEnlazadosAnt === undefined
                    ? idNuevo
                    : idAntiguo
              }

              response = await API().api.acabadosGetElementosEnlazadosUnidadAcabados2(
                finalIdAnt,
                targetIdElementosEnlazados,
              )
            } catch (error) {
              console.error('[updateLinkedGLBForGroup] Error en llamada API:', error)
              continue
            }

            if (!response || !response.data || !response.data.unidad) {
              continue
            }
            const newLinkedSonInfo = response.data
            if (!newLinkedSonInfo.unidad) {
              continue
            }
            const glbRef = newLinkedSonInfo.unidad.referenciaBlender
            if (!glbRef) {
              continue
            }
            const glb = await loader.loadAsync(glbRef)
            if (!newLinkedSonInfo.unidad.idUnidades) {
              continue
            }
            const extraInfo = await API().api.acabadosGetUnidadAcabadosInfo(
              newLinkedSonInfo.unidad.idUnidades,
            )

            const backGLBInfo = {
              idUnidades: newLinkedSonInfo.unidad?.idUnidades ?? 0,
              linkedID: newLinkedSonInfo.unidad?.idUnidades ?? 0,
              active: true,
              codigo: newLinkedSonInfo.unidad?.codigo ?? '',
              referenciaBlender: newLinkedSonInfo.unidad?.referenciaBlender ?? '',
              elemento: newLinkedSonInfo.unidad?.elemento,
              descripcion: newLinkedSonInfo.unidad?.descripcion,
              descripcionLarga: newLinkedSonInfo.unidad?.descripcionLarga,
              ancho: newLinkedSonInfo.unidad?.ancho,
              fondo: newLinkedSonInfo.unidad?.fondo,
              alto: newLinkedSonInfo.unidad?.alto,
              mano: newLinkedSonInfo.unidad?.mano,
              altura: newLinkedSonInfo.unidad?.altura,
              alturaHaciaAbajo: newLinkedSonInfo.unidad?.alturaHaciaAbajo,
              colision: newLinkedSonInfo.unidad?.colision,
              cota: newLinkedSonInfo.unidad?.cota,
              tipoNormalizado: newLinkedSonInfo.unidad?.tipoNormalizado ?? 'MBJ',
              tipoUnidNorm: newLinkedSonInfo.unidad?.tipoUnidNorm ?? '',
              modificarAncho: newLinkedSonInfo.unidad?.modificarAncho,
              modificarFondo: newLinkedSonInfo.unidad?.modificarFondo,
              modificarAlto: newLinkedSonInfo.unidad?.modificarAlto,
              morphs: newLinkedSonInfo.unidad?.morphs ?? [],
              precio: newLinkedSonInfo.unidad?.precio,
              acabados: newLinkedSonInfo.unidad?.acabados ?? [],
              unidadesLuces: newLinkedSonInfo.unidad?.unidadesLuces ?? [],
              elementosEnlazados: newLinkedSonInfo.unidad?.elementosEnlazados ?? [],
              referenciaJpgpng: newLinkedSonInfo.unidad?.referenciaJpgpng,
              universal: newLinkedSonInfo.unidad?.universal,
            } as unknown as BackGLBInfo

            const processedInfo = newGetDataFromGLB({
              glbData: glb,
              backGLBInfo,
              id: childID,
              type: 'linkedSon',
              catalogID: newLinkedSonInfo.unidad.codigo ?? '',
              extraInfo: {
                zonas: Array.isArray(extraInfo.data?.zonas)
                  ? extraInfo.data.zonas.map((z) => ({
                      idAcabadoUnidad: z.idAcabadoUnidad,
                      zona: z.zona ?? '',
                    }))
                  : [],
              },
              itemInfo: {},
            })
            set((s) => {
              const currElement = s.items.get(childID as AnyID) as LinkedItemSon
              if (!isLinkedSon(currElement)) {
                return
              }

              const prevPosition = { x: currElement.x, y: currElement.y, z: currElement.z }
              const prevSize = {
                width: currElement.width,
                height: currElement.height,
                thickness: currElement.thickness,
              }
              const prevMorphs = currElement.morphs

              let newX = prevPosition.x
              let newY = prevPosition.y
              let newZ = prevPosition.z

              if (parentItem) {
                const relativeX = prevPosition.x - parentItem.x
                const relativeY = prevPosition.y - parentItem.y
                const relativeZ = prevPosition.z - parentItem.z

                newX = parentItem.x + relativeX
                newY = parentItem.y + relativeY
                newZ = parentItem.z + relativeZ
              }
              if (!newLinkedSonInfo.unidad) {
                return
              }

              let finalX = newX
              let finalY = newY
              let finalZ = newZ

              // Para Grifo, TPCGrifo, Fregadero, Placa y tipos genéricos (Micro, Horno, HornoMicro, etc.),
              // usar posiciones del backend si están disponibles
              // Cada grifo/fregadero tiene su propia posición específica del backend según la unidad padre
              if (
                (currElement.typeOfSon === 'Grifo' ||
                  currElement.typeOfSon === 'TPCGrifo' ||
                  currElement.typeOfSon === 'Fregadero' ||
                  currElement.typeOfSon === 'Placa' ||
                  currElement.typeOfSon === 'Micro' ||
                  currElement.typeOfSon === 'Horno' ||
                  currElement.typeOfSon === 'HornoMicro') &&
                newLinkedSonInfo
              ) {
                const posicionBackendX = newLinkedSonInfo.posicionX ?? 0
                const posicionBackendY = newLinkedSonInfo.posicionY ?? 0
                const posicionBackendZ = newLinkedSonInfo.posicionZ ?? 0

                if (
                  !isNaN(posicionBackendX) &&
                  !isNaN(posicionBackendY) &&
                  !isNaN(posicionBackendZ) &&
                  posicionBackendX !== 0 &&
                  posicionBackendY !== 0 &&
                  posicionBackendZ !== 0
                ) {
                  finalX = posicionBackendX
                  finalY = posicionBackendY
                  finalZ = posicionBackendZ

                  // Guardar baseY para que positionFregaderoGrifoPlacaAccordingToEncimera pueda ajustar correctamente
                  if (
                    currElement.typeOfSon === 'Grifo' ||
                    currElement.typeOfSon === 'TPCGrifo' ||
                    currElement.typeOfSon === 'Fregadero' ||
                    currElement.typeOfSon === 'Placa'
                  ) {
                    ;(currElement as any).baseY = posicionBackendY
                  }
                } else {
                  // Si las posiciones del backend no son válidas, mantener las posiciones actuales
                  // pero asegurarse de que no sean NaN
                  if (isNaN(finalX) || finalX === undefined) finalX = prevPosition.x || 0
                  if (isNaN(finalY) || finalY === undefined) finalY = prevPosition.y || 0
                  if (isNaN(finalZ) || finalZ === undefined) finalZ = prevPosition.z || 0
                }
              } else {
                // Para otros tipos, asegurarse de que las posiciones no sean NaN
                if (isNaN(finalX) || finalX === undefined) finalX = prevPosition.x || 0
                if (isNaN(finalY) || finalY === undefined) finalY = prevPosition.y || 0
                if (isNaN(finalZ) || finalZ === undefined) finalZ = prevPosition.z || 0
              }

              Object.assign(currElement, {
                linkedID: targetIdElementosEnlazados,
                idElementosEnlazados: targetIdElementosEnlazados,
                catalogID: newLinkedSonInfo.unidad.codigo,
                code: newLinkedSonInfo.unidad.codigo,
                idUnidades: newLinkedSonInfo.unidad.idUnidades ?? 0,
                textures: processedInfo.textures,
                zones: processedInfo.zones,
                glbPath: String(newLinkedSonInfo.unidad.referenciaBlender ?? ''),
                referenciaBlender: String(newLinkedSonInfo.unidad.referenciaBlender ?? ''),
                x: finalX,
                y: finalY,
                z: finalZ,
                long:
                  newLinkedSonInfo.unidad.descripcionLarga ||
                  newLinkedSonInfo.unidad.descripcion ||
                  newLinkedSonInfo.unidad.codigo ||
                  currElement.long,
                width: prevSize.width,
                height: prevSize.height,
                thickness: prevSize.thickness,
                morphs: prevMorphs,
              })
              if (
                newLinkedSonInfo.unidad.codigo !== undefined &&
                newLinkedSonInfo.unidad.codigo !== null
              ) {
                s.sceneGLB.set(String(newLinkedSonInfo.unidad.codigo), {
                  glb,
                  backGLBInfo: newLinkedSonInfo.unidad,
                })
              }
              currElement.typeOfSon =
                currElement.typeOfSon ||
                childItem.typeOfSon ||
                childItem.description ||
                'Desconocido'

              if (currElement.description !== 'Tirador') {
                const x = currElement.x ?? 0
                const y = currElement.y ?? 0
                const z = currElement.z ?? 0
                currElement.visible = !(x === 0 && y === 0 && z === 0)
              }

              if (currElement.description === 'Tirador' && newLinkedSonInfo) {
                const posicionesAlternativas = [
                  'biPosicionX',
                  'biPosicionY',
                  'biPosicionZ',
                  'bcPosicionX',
                  'bcPosicionY',
                  'bcPosicionZ',
                  'bdPosicionX',
                  'bdPosicionY',
                  'bdPosicionZ',
                  'aiPosicionX',
                  'aiPosicionY',
                  'aiPosicionZ',
                  'acPosicionX',
                  'acPosicionY',
                  'acPosicionZ',
                  'adPosicionX',
                  'adPosicionY',
                  'adPosicionZ',
                  'biRotacionX',
                  'biRotacionY',
                  'biRotacionZ',
                  'bcRotacionX',
                  'bcRotacionY',
                  'bcRotacionZ',
                  'bdRotacionX',
                  'bdRotacionY',
                  'bdRotacionZ',
                  'aiRotacionX',
                  'aiRotacionY',
                  'aiRotacionZ',
                  'acRotacionX',
                  'acRotacionY',
                  'acRotacionZ',
                  'adRotacionX',
                  'adRotacionY',
                  'adRotacionZ',
                  'b2idRotacionX',
                  'b2idRotacionY',
                  'b2idRotacionZ',
                  'a2idRotacionX',
                  'a2idRotacionY',
                  'a2idRotacionZ',
                ]
                posicionesAlternativas.forEach((key) => {
                  if (Object.prototype.hasOwnProperty.call(newLinkedSonInfo, key)) {
                    // @ts-expect-error: Puede que no esté tipado en el DTO, pero lo copiamos si existe
                    currElement[key] = newLinkedSonInfo[key]
                  }
                })
                if (newLinkedSonInfo.unidad?.morphs)
                  currElement.morphs = mapMorphsFromDTO(newLinkedSonInfo.unidad.morphs)

                const drawerNode = getTiradorDrawerNode(childItem)
                if (drawerNode !== undefined) {
                  currElement.nodo = drawerNode
                  currElement.option = drawerNode
                }
                currElement.parentOption = childItem.parentOption

                const nuevaPos = calculateTiradorPosition(currElement, parentItem)
                currElement.x = nuevaPos.x
                currElement.y = nuevaPos.y
                currElement.z = nuevaPos.z

                currElement.visible = !(
                  currElement.x === 0 &&
                  currElement.y === 0 &&
                  currElement.z === 0
                )
                get().setLinkedHandPosition(
                  currElement.id as LinkedItemID,
                  currElement.handPosition,
                )
              }
            })

            processedCount++
            if (processedCount >= BATCH_SIZE) {
              processedCount = 0

              await new Promise((resolve) => setTimeout(resolve, 0))
            }
          } catch (error) {
            console.error('[updateLinkedGLBForGroup] Error actualizando GLB para', childID, error)
          }
        }
      }
    } catch (error) {
      if (!batchActive) {
        set((s) => {
          s.frameloop = originalFrameloop
        })
      }
      throw error
    } finally {
      if (!batchActive) {
        setTimeout(() => {
          set((s) => {
            s.frameloop = originalFrameloop
          })
        }, 100)
      }
    }
  },

  setLinkedVisible: (linkedID: string, visible: boolean) => {
    // This will probably never be used but it also exists for items
  },
  editLinkedZone: (linkedID: string, zone: string) => {
    // This will be a mirror of item, so we either use the original (unless textures changes significantly)
  },
  editLinkedMatrix: (linkedID: string, matrix: number[]) => {
    // This will probably never be used but internally
  },
  // GENERAL PARENT MANIPULATION:
  editSelectedOption: (parentID: string, option: string) => {
    // This will generally be used to swap between options when selecting a new option. Usually will come at the same time than updateLinkedGLB
  },
  selectLinkedParent: (linkedID: string) => {
    // This is kinda trivial since every single item has a reference to their father, so:
    set((store) => {
      store.layers[store.currentLayer].selected.clear()
      const item = store.items.get(linkedID as AnyID)
      if (item && isLinkedSon(item)) {
        store.layers[store.currentLayer].selected.add((item as LinkedItemSon).firstParentID)
      }
    })
  },
  applyAllZonesAndChildrenToAllLinkedItems: (id: string, typeOfSon?: string, linkedID?: number) => {
    set((state) => {
      const originalItem = state.items.get(id as AnyID)
      if (!originalItem) return
      if (!isLinkedItem(originalItem)) return
      if (!originalItem.zones) return
      state.items.forEach((targetItem, key) => {
        if (!isLinkedItem(targetItem)) return
        targetItem.childrenOrder.forEach((el) => {
          const childId = el.split('!')[1]
          const child = state.items.get(childId as AnyID)
          if (!child || !isLinkedSon(child)) return
          if (
            (typeOfSon && child.typeOfSon !== typeOfSon) ||
            (linkedID && child.linkedID !== linkedID)
          )
            return
        })
      })
    })
  },
  applyToAllItemsAndChildrens: (id: string) => {
    set((state) => {
      const item = state.items.get(id as AnyID)
      // const parentID =
      //   id.startsWith('linkedSon_') && isLinkedSon(item)
      //     ? ((item as LinkedItemSon).firstParentID ?? '')
      //     : id
      // const originalItem = state.items.get(parentID as AnyID)
      const parenItem = state.items.get(id as AnyID)
      let finalParenItem = parenItem
      if (item?.type === 'linkedSon' && isLinkedSon(item)) {
        finalParenItem = state.items.get((item as LinkedItemSon).firstParentID as AnyID)
      }

      if (!isLinkedItem(finalParenItem)) {
        return
      }
      const childrensID = finalParenItem.childrenOrder.map((childId) => {
        return state.items.get(childId.split('!')[1] as AnyID)
      })
      const diccionaryChildren: Record<string, any> = {}
      childrensID.forEach((e) => {
        if (!isLinkedSon(e)) return
        if (!e.description) return
        if (!(e.description in diccionaryChildren)) {
          diccionaryChildren[e?.description] = e
        }
      })
      state.items.forEach((e) => {
        if (!isLinkedSon(e)) return
        if (
          e.description === 'Puerta' ||
          (e.description && e.description.toLowerCase().includes('encimera'))
        ) {
          if (diccionaryChildren[e.description]) {
            const propertys = diccionaryChildren[e.description]

            const isFregadero =
              e.description?.toLowerCase().includes('fregadero') ||
              (e as any).typeOfSon === 'Fregadero'
            const isEncimera = propertys.description?.toLowerCase().includes('encimera')

            if (isFregadero && isEncimera) {
              e.textures = [...propertys.textures]
              e.zones = { ...propertys.zones }
            } else {
              e.textures = [...propertys.textures]
              e.zones = { ...propertys.zones }
            }
          }
        }
      })
    })
  },
  applyAllZonesAndChildrenOfLinkedToAnotherLinkedWithTheSameName: (id: string) => {
    set((state) => {
      let parentID = id
      if (id.startsWith('linkedSon_')) {
        const item = state.items.get(id as AnyID)
        if (item && isLinkedSon(item)) {
          parentID = (item as LinkedItemSon).firstParentID ?? ''
        }
      }
      const originalItem = state.items.get(parentID as AnyID)
      if (!originalItem) {
        return
      }
      if (!isLinkedItem(originalItem)) {
        return
      }
      if (!originalItem.zones) {
        return
      }
      const zonesToCopy = Object.values(originalItem.zones)
      const childrenToCopy = originalItem.childrenOrder.map((el) =>
        state.items.get(el.split('!')[1] as AnyID),
      ) as LinkedItemSon[]
      if (!zonesToCopy.length && !childrenToCopy.length) {
        return
      }
      state.items.forEach((targetItem, key) => {
        if (key === originalItem.id) {
          return
        }
        if (!isLinkedItem(targetItem)) {
          return
        }
        if (targetItem.catalogID !== originalItem.catalogID) {
          return
        }
        if (targetItem.textures) {
          targetItem.textures = [...originalItem.textures]
        }
        zonesToCopy.forEach((zone) => {
          const zoneKey = Object.keys(targetItem.zones).find(
            (k) => targetItem.zones[k].name === zone.name,
          )
          if (zoneKey) {
            const newMaterial = { ...zone.material }
            targetItem.zones[zoneKey].material = newMaterial
            if (zone.material) {
              targetItem.zones[zoneKey].material.color = zone.material.color
              targetItem.zones[zoneKey].material.groupID = zone.material.groupID
              targetItem.zones[zoneKey].material.searchID = zone.material.searchID
              targetItem.zones[zoneKey].material.id = zone.material.id
              targetItem.zones[zoneKey].material.description = zone.material.description
            }
          }
        })
        targetItem.childrenOrder.map((el) => state.items.delete(el.split('!')[1] as AnyID))
        const newChildrenIDArr = childrenToCopy.map((el) => {
          const id = getId('linkedSon')
          return [el.id, id]
        })
        newChildrenIDArr.forEach(([oldId, newId], i) => {
          const childs: string[] = []
          const thisPreviousObject = childrenToCopy[i]
          thisPreviousObject.childs.forEach((el) => {
            const indexOfChild = childrenToCopy.findIndex((ele) => ele?.id === el)
            if (indexOfChild === -1) return
            childs.push(newChildrenIDArr[indexOfChild][1])
          })
          state.items.set(newId, {
            ...childrenToCopy[i],
            firstParentID: targetItem.id,
            childs: new Set(childs),
            id: newId,
          })
        })
        const newChildrenOrder = originalItem.childrenOrder.map((el, i) => {
          return `${el.split('!')[0]}!${newChildrenIDArr[i][1]}`
        })
        state.items.get(key as AnyID).childrenOrder = newChildrenOrder
        targetItem.childrenOrder = newChildrenOrder
        const parentChilds: string[] = []
        originalItem.childs.forEach((el, i) => {
          const indexOfChild = childrenToCopy.findIndex((ele) => ele?.id === el)
          if (indexOfChild === -1) return
          parentChilds.push(newChildrenIDArr[indexOfChild][1])
        })
        const setto: Set<`linkedSon_${string}`> = new Set(parentChilds as `linkedSon_${string}`[])
        state.items.get(key as AnyID).childs = setto
        targetItem.childs = setto
        state.items.get(key as AnyID).orderMap = originalItem.orderMap
        targetItem.orderMap = originalItem.orderMap
      })
    })
  },
  applyTheSelectedoZoneToAllLinkedZonesWithSameName: (id: LinkedItemsID, zoneName: string) => {
    set((s) => {
      const item = s.items.get(id)
      if (!isLinked(item)) {
        return
      }
      if (!item?.textures) {
        return
      }
      const texture = item.textures.find((el) => {
        return el.zone === zoneName || el.zoneName === zoneName || el.zoneDisplayName === zoneName
      })
      if (!texture) {
        return
      }
      const zoneDisplayName = texture.zoneDisplayName
      const zoneKey = Object.keys(item.zones).find(
        (key) => item.zones[key].name === texture.zoneName,
      )
      if (!zoneKey) {
        return
      }
      const zone = item.zones[zoneKey] as Item['zones'][string]
      const material = zone?.material as Material
      if (!zone || !zone.material || !material) {
        return
      }
      item.textures.forEach((el) => {
        if (el.zoneDisplayName === zoneDisplayName && el.zoneName !== texture.zoneName) {
          const targetZoneKey = Object.keys(item.zones).find(
            (k) => item.zones[k].name === el.zoneName,
          )
          if (targetZoneKey) {
            item.zones[targetZoneKey].material = { ...material }
            el.acabado = texture.acabado
            el.linkedTextureID = texture.linkedTextureID
            el.name = texture.name
            el.searchID = texture.searchID
            el.url = texture.url
            el.groupID = texture.groupID
          }
        }
      })
      s.items.forEach((value, key) => {
        if (!key.startsWith('linkedItem_') && !key.startsWith('linkedSon_')) {
          return
        }
        if (!isLinked(value)) {
          return
        }

        if (!value.textures || !Array.isArray(value.textures)) {
          return
        }

        value.textures.forEach((el) => {
          if (!value.zones) {
            return
          }

          const targetZoneKey = Object.keys(value.zones).find(
            (k) => value.zones[k].name === el.zoneName,
          )

          if (value.id === id && el.zoneName === texture.zoneName) {
            return
          }

          const isDecoItem =
            (typeof item.catalogID === 'string' && item.catalogID.includes('DECO')) ||
            (typeof item.referencia === 'string' && item.referencia.includes('DECO'))
          const isTargetDecoItem =
            (typeof value.catalogID === 'string' && value.catalogID.includes('DECO')) ||
            (typeof value.referencia === 'string' && value.referencia.includes('DECO'))

          const isDecoArmazon =
            isDecoItem &&
            texture.zoneDisplayName === 'Armazon' &&
            texture.modelo === 'AcabadoModelo'
          const isTargetPuertaAcabadoModelo =
            el.zoneDisplayName === 'Puerta' && el.modelo === 'AcabadoModelo'

          const isPuertaAcabadoModelo =
            texture.zoneDisplayName === 'Puerta' && texture.modelo === 'AcabadoModelo'
          const isTargetDecoArmazon =
            isTargetDecoItem && el.zoneDisplayName === 'Armazon' && el.modelo === 'AcabadoModelo'

          const shouldApplyDecoMapping =
            (isDecoArmazon && isTargetPuertaAcabadoModelo) ||
            (isPuertaAcabadoModelo && isTargetDecoArmazon)

          const isSameZone = el.zoneDisplayName === zoneDisplayName
          const isDecoMapping = shouldApplyDecoMapping

          if ((isSameZone || isDecoMapping) && targetZoneKey) {
            const zoneMaterial = value.zones[targetZoneKey]?.material as Material
            if (!zoneMaterial) {
              return
            }

            const shouldApplyTexture =
              el.zoneDisplayName === texture.zoneDisplayName && el.modelo === texture.modelo

            const isFregadero =
              value.description?.toLowerCase().includes('fregadero') ||
              (value as any).typeOfSon === 'Fregadero'
            const isEncimera = texture.zoneDisplayName?.toLowerCase().includes('encimera')

            if (isFregadero && isEncimera) {
              el.acabado = texture.acabado
              el.linkedTextureID = texture.linkedTextureID
              el.name = texture.name
              el.searchID = texture.searchID
              el.url = texture.url
              el.groupID = texture.groupID
            } else if (shouldApplyDecoMapping) {
              value.zones[targetZoneKey].material = { ...material }
              el.acabado = texture.acabado
              el.name = texture.name
              el.searchID = texture.searchID
              el.url = texture.url
              el.groupID = texture.groupID
            } else if (shouldApplyTexture) {
              value.zones[targetZoneKey].material = { ...material }
              el.acabado = texture.acabado
              el.linkedTextureID = texture.linkedTextureID
              el.name = texture.name
              el.searchID = texture.searchID
              el.url = texture.url
              el.groupID = texture.groupID
            }
          }
        })
      })
    })
  },
  applyTheSelectedZoneToSpecificLinkedZones: (
    id: LinkedItemsID,
    zoneName: string,
    selectedIds: Set<LinkedItemsID>,
  ) => {
    set((s) => {
      const item = s.items.get(id)
      if (!isLinked(item)) return
      if (!item?.textures) return

      const texture = item.textures.find((el) => {
        return el.zone === zoneName || el.zoneName === zoneName || el.zoneDisplayName === zoneName
      })

      if (!texture) return

      const zoneDisplayName = texture.zoneDisplayName
      const zoneKey = Object.keys(item.zones).find(
        (key) => item.zones[key].name === texture.zoneName,
      )

      if (!zoneKey) return

      const zone = item.zones[zoneKey] as Item['zones'][string]
      const material = zone?.material as Material

      if (!zone || !zone.material || !material) return

      selectedIds.forEach((selectedId) => {
        const selectedItem = s.items.get(selectedId)
        if (!selectedItem || !isLinked(selectedItem)) return

        if (selectedItem.tipoNormalizado === 'MAL') {
          const puertaTexture = selectedItem.textures.find((t) => t.zoneDisplayName === 'Puerta')
          const itemModelo = puertaTexture?.modelo || ''

          let targetModelo = ''
          if (s.currentObjectID) {
            const currentItem = s.items.get(s.currentObjectID as LinkedItemsID)
            if (currentItem && isLinked(currentItem) && currentItem.tipoNormalizado === 'MAL') {
              const currentTexture = currentItem.textures.find(
                (t) => t.zoneDisplayName === 'Puerta',
              )
              targetModelo = currentTexture?.modelo || ''
            }
          }

          if (!targetModelo) {
            const refItem = s.items.get(id)
            if (refItem && isLinked(refItem)) {
              const refTexture = refItem.textures.find((t) => t.zoneDisplayName === 'Puerta')
              targetModelo = refTexture?.modelo || ''
            }
          }

          if (itemModelo !== targetModelo) {
            return
          }
        }

        selectedItem.textures.forEach((el) => {
          const targetZoneKey = Object.keys(selectedItem.zones).find(
            (k) => selectedItem.zones[k].name === el.zoneName,
          )

          if (
            selectedItem.id === id &&
            !selectedIds.has(selectedItem.id) &&
            el.zoneName === texture.zoneName
          ) {
            return
          }

          if (el.zoneDisplayName === zoneDisplayName && targetZoneKey) {
            const zoneMaterial = selectedItem.zones[targetZoneKey]?.material as Material
            if (!zoneMaterial) return
            selectedItem.zones[targetZoneKey].material = { ...material }
            el.acabado = texture.acabado
            el.linkedTextureID = texture.linkedTextureID
            el.name = texture.name
            el.searchID = texture.searchID
            el.url = texture.url
            el.groupID = texture.groupID
            // el.modelo = texture.modelo // ← NO cambiar el modelo
          }
        })
      })
    })
  },
  editLinkedElementTexture: (
    linkedID: LinkedItemsID,
    material: Material,
    modeEditor: 'Single' | 'All' | 'Selected' | 'MBJ' | 'MAL' | 'COL',
    selectedIds?: Set<LinkedItemsID>,
  ) => {
    let newTexture: Material = material
    set((s) => {
      const item = s.items.get(linkedID)
      if (!isLinked(item)) return

      if (!item.textures || item.textures.length === 0) {
        return
      }

      const texture = item.textures[0]

      newTexture = {
        ...texture,
        groupID: material.groupID,
        name: material.name,
        searchID: material.searchID,
        url: material.url,
        modelo: material.modelo,
        acabado: material.acabado,
        id: material.id,
      }

      Object.assign(texture, newTexture)

      if (
        item.description?.toLowerCase().includes('fregadero') ||
        (item as any).typeOfSon === 'Fregadero'
      ) {
        const positionFregaderoGrifoPlaca = get().positionFregaderoGrifoPlacaAccordingToEncimera
        if (positionFregaderoGrifoPlaca) {
          positionFregaderoGrifoPlaca(linkedID)
        } else {
          console.error('❌ positionFregaderoGrifoPlacaAccordingToEncimera no encontrada')
        }
      }

      if (item.zones && Object.keys(item.zones).length > 0) {
        const firstZoneKey = Object.keys(item.zones)[0]
        const firstZone = item.zones[firstZoneKey]
        if (firstZone && firstZone.material) {
          firstZone.material = { ...firstZone.material, ...material }
        }
      }

      if (modeEditor === 'All') {
        // Determinar si el cambio se está haciendo desde un padre (linkedItem) o desde un hijo (linkedSon)
        const isChangingFromParent = !linkedID.startsWith('linkedSon_')
        // Determinar si la fuente del cambio es una encimera (ya sea linkedItem o linkedSon)
        const sourceIsEncimera = isEncimeraLinkedSonItem(item)

        s.items.forEach((targetItem, key) => {
          if (!key.startsWith('linkedItem_') && !key.startsWith('linkedSon_')) return
          if (!isLinked(targetItem)) return
          if (targetItem.id === linkedID) return

          // NO aplicar cambios a linkedSon cuando se cambia desde el padre,
          // EXCEPTO cuando la fuente es una encimera (para que las encimeras se propaguen entre sí)
          if (isChangingFromParent && !sourceIsEncimera && targetItem.type === 'linkedSon') {
            return
          }

          const matchesAllModeTarget = sourceIsEncimera
            ? isEncimeraLinkedSonItem(targetItem) // Propagar a CUALQUIER tipo de encimera (linkedItem y linkedSon)
            : targetItem.description === item.description

          if (matchesAllModeTarget) {
            if (targetItem.textures && targetItem.textures.length > 0) {
              const targetTexture = targetItem.textures[0]

              const originalZoneName = targetTexture.zoneName
              const originalZoneDisplayName = targetTexture.zoneDisplayName

              Object.assign(targetTexture, newTexture)

              targetTexture.zoneName = originalZoneName
              targetTexture.zoneDisplayName = originalZoneDisplayName

              if (
                targetItem.description?.toLowerCase().includes('fregadero') ||
                targetItem.typeOfSon === 'Fregadero'
              ) {
                // NO ajustar posiciones de linkedSon cuando se cambia desde un padre no-encimera
                if (!isChangingFromParent || sourceIsEncimera || targetItem.type !== 'linkedSon') {
                  const positionFregaderoGrifoPlaca =
                    get().positionFregaderoGrifoPlacaAccordingToEncimera
                  if (positionFregaderoGrifoPlaca) {
                    positionFregaderoGrifoPlaca(key)
                  } else {
                    console.error(
                      '❌ positionFregaderoGrifoPlacaAccordingToEncimera no encontrada en ALL mode',
                    )
                  }
                }
              }

              if (targetItem.zones && Object.keys(targetItem.zones).length > 0) {
                const targetZoneKey = Object.keys(targetItem.zones)[0]
                const targetZone = targetItem.zones[targetZoneKey]
                if (targetZone && targetZone.material) {
                  targetZone.material = { ...targetZone.material, ...material }
                }
              }
            }
          }
        })
      } else if (modeEditor === 'Selected' && selectedIds) {
        selectedIds.forEach((selectedId) => {
          if (selectedId === linkedID) return
          const selectedItem = s.items.get(selectedId)
          if (!selectedItem || !isLinked(selectedItem)) return

          if (selectedItem.textures && selectedItem.textures.length > 0) {
            const targetTexture = selectedItem.textures[0]
            Object.assign(targetTexture, newTexture)

            if (selectedItem.zones && Object.keys(selectedItem.zones).length > 0) {
              const targetZoneKey = Object.keys(selectedItem.zones)[0]
              const targetZone = selectedItem.zones[targetZoneKey]
              if (targetZone && targetZone.material) {
                targetZone.material = { ...targetZone.material, ...material }
              }
            }
          }
        })
      } else if (modeEditor === 'MBJ') {
        s.items.forEach((targetItem, key) => {
          if (!key.startsWith('linkedItem_') && !key.startsWith('linkedSon_')) return
          if (!isLinked(targetItem)) return
          if (targetItem.id === linkedID) return

          if (
            targetItem.description === 'Puerta' &&
            String(targetItem.catalogID).toLowerCase().includes('encimera')
          ) {
            if (targetItem.textures && targetItem.textures.length > 0) {
              const targetTexture = targetItem.textures[0]
              Object.assign(targetTexture, newTexture)

              if (targetItem.zones && Object.keys(targetItem.zones).length > 0) {
                const targetZoneKey = Object.keys(targetItem.zones)[0]
                const targetZone = targetItem.zones[targetZoneKey]
                if (targetZone && targetZone.material) {
                  targetZone.material = { ...targetZone.material, ...material }
                }
              }
            }
          }
        })
      } else if (modeEditor === 'COL') {
        s.items.forEach((targetItem, key) => {
          if (!key.startsWith('linkedItem_') && !key.startsWith('linkedSon_')) return
          if (!isLinked(targetItem)) return
          if (targetItem.id === linkedID) return

          if (
            targetItem.description === 'Puerta' &&
            String(targetItem.catalogID).toLowerCase().includes('encimera')
          ) {
            if (targetItem.textures && targetItem.textures.length > 0) {
              const targetTexture = targetItem.textures[0]
              Object.assign(targetTexture, newTexture)

              if (targetItem.zones && Object.keys(targetItem.zones).length > 0) {
                const targetZoneKey = Object.keys(targetItem.zones)[0]
                const targetZone = targetItem.zones[targetZoneKey]
                if (targetZone && targetZone.material) {
                  targetZone.material = { ...targetZone.material, ...material }
                }
              }
            }
          }
        })
      }
    })
    return newTexture
  },

  updateTiradorPositionsOnly: (parentID: string) => {
    set((state) => {
      const parentItem = state.items.get(parentID as AnyID)
      if (!isLinkedItem(parentItem)) return

      if (parentItem.childs?.size > 0) {
        parentItem.childs.forEach((childID) => {
          const childItem = state.items.get(childID as AnyID)
          if (!childItem || !isLinkedSon(childItem)) return
          if (childItem.typeOfSon === 'Tirador' || childItem.description === 'Tirador') {
            const pos = calculateTiradorPosition(childItem, parentItem)
            childItem.x = pos.x
            childItem.y = pos.y
            childItem.z = pos.z
          }
        })
      }
    })
  },

  updateLinkedSonPositions: (parentID: string, morphChanged: 'Ancho' | 'Fondo' | 'Alto') => {
    set((state) => {
      const parentItem = state.items.get(parentID as AnyID)
      if (!isLinkedItem(parentItem)) return

      if (usesFreeDimensionScaling(parentItem) && morphChanged === 'Ancho') {
        logFreeDimensionAncho('updateLinkedSonPositions before', parentItem, { morphChanged })
      }

      const parentHeight = parentItem.height
      const parentY = parentItem.y ?? 0
      const parentLayoutAlto = getFreeDimensionParentLayoutAltoMm(parentItem) ?? parentHeight
      const parentBodyTopY = usesFreeDimensionScaling(parentItem)
        ? getFreeDimensionVisualBodyTopYMm(parentItem)
        : parentHeight
      const parentEncimeraTopY = usesFreeDimensionScaling(parentItem)
        ? getFreeDimensionEncimeraPositionYMm(parentItem)
        : parentY + parentHeight
      const parentWidth = parentItem.width
      const parentThickness = parentItem.thickness

      const isRBorRegleta =
        /^rb/i.test(parentItem.catalogID ?? '') || /regleta/i.test(parentItem.description ?? '')

      if (parentItem.childs?.size > 0) {
        parentItem.childs.forEach((childID) => {
          const childItem = state.items.get(childID as AnyID)
          if (!childItem || !isLinkedSon(childItem)) return

          if (childItem.typeOfSon === 'Tirador' || childItem.description === 'Tirador') {
            const pos = calculateTiradorPosition(childItem, parentItem)
            childItem.x = pos.x
            childItem.y = pos.y
            childItem.z = pos.z
          } else {
            const isEncimera = childItem.description?.toLowerCase().includes('encimera')

            if (isEncimera) {
              const rbFreeDimEncimera =
                usesFreeDimensionScaling(parentItem) && isRbFreeDimensionRegleta(parentItem)
              if (morphChanged === 'Alto') {
                if (usesFreeDimensionScaling(parentItem)) {
                  placeLinkedEncimeraY(childItem, parentItem)
                } else if (isRBorRegleta) {
                  const parentMorphAlto = parentItem.morphs?.find((m) => m.name === 'Alto')
                  const _def = (parentMorphAlto?._default as number) ?? parentHeight
                  const def = (parentMorphAlto?.default as number) ?? parentHeight
                  const delta = _def - def

                  const mark = '__rbOriginalY'
                  if ((childItem as any)[mark] == null) {
                    ;(childItem as any)[mark] = childItem.y ?? 0
                  }
                  const originalY = (childItem as any)[mark] as number
                  childItem.y = originalY - delta
                } else {
                  childItem.y = parentEncimeraTopY
                }
                applyEncimeraThicknessMm(childItem)
              } else if (rbFreeDimEncimera) {
                if (morphChanged === 'Ancho') {
                  syncFreeDimensionEncimeraAnchoToParent(childItem, parentItem)
                } else if (morphChanged === 'Fondo') {
                  syncFreeDimensionEncimeraFondoToParent(childItem, parentItem)
                }
              } else if (usesFreeDimensionScaling(parentItem) && morphChanged === 'Fondo') {
                placeLinkedEncimeraY(childItem, parentItem)
              } else if (!usesFreeDimensionScaling(parentItem)) {
                const relativeY = (childItem.y ?? 0) - parentY
                childItem.y = parentY + relativeY
              }
            } else {
              childItem.y =
                parentY + (usesFreeDimensionScaling(parentItem) ? parentBodyTopY : parentLayoutAlto)
            }
          }

          if (childItem.morphs?.length > 0 && !childItem.description.includes('Tirador')) {
            childItem.morphs.forEach((morph) => {
              if (morph.name === morphChanged) {
                const isEncimera = childItem.description?.toLowerCase().includes('encimera')

                if (isEncimera) {
                  if (isRBorRegleta && morph.name === 'Alto') {
                    return
                  }
                  // Grosor por acabado (10/12/20/30 mm); nunca copiar Alto/ancho del padre al morph Alto.
                  if (morph.name === 'Alto') {
                    applyEncimeraThicknessMm(childItem)
                    return
                  }
                  // LB8060/CB9060: ancho = lateral; fondo = catálogo encimera (620), no fondo lateral (600).
                  if (freeDimensionUsesLb8060StyleAltoOffset(parentItem)) {
                    if (morph.name === 'Ancho') {
                      morph.default = parentWidth
                      childItem.width = parentWidth
                      return
                    }
                    if (morph.name === 'Fondo') {
                      syncFreeDimensionEncimeraFondoToParent(childItem, parentItem)
                      return
                    }
                  }

                  // FREE_DIMENSION: encimera con morph GLB (50 mm), no estirar al ancho del padre (100 mm).
                  if (usesFreeDimensionScaling(parentItem)) {
                    if (morph.name === 'Ancho') {
                      syncFreeDimensionEncimeraAnchoToParent(childItem, parentItem)
                      return
                    }
                    if (morph.name === 'Fondo') {
                      syncFreeDimensionEncimeraFondoToParent(childItem, parentItem)
                      return
                    }
                  }

                  const parentMorph = parentItem.morphs?.find((m) => m.name === morph.name)
                  if (parentMorph) {
                    const currentParentValue =
                      morph.name === 'Ancho'
                        ? parentWidth
                        : morph.name === 'Fondo'
                          ? parentThickness
                          : morph.name === 'Alto'
                            ? isEncimera && usesFreeDimensionScaling(parentItem)
                              ? parentEncimeraTopY
                              : parentLayoutAlto
                            : morph.name === 'Regleta'
                              ? parentItem.regleta || 0
                              : 0

                    let newValue: number
                    if (isRBorRegleta && morph.name === 'Ancho' && isEncimera) {
                      const cap =
                        morph.max != null && morph.min != null && morph.max > morph.min
                          ? morph.max
                          : currentParentValue
                      newValue = Math.min(currentParentValue, cap)
                    } else if (isRBorRegleta && morph.name === 'Ancho') {
                      newValue = currentParentValue
                    } else {
                      // Para otros casos, calcular la diferencia como antes
                      const difference = morph.max - parentMorph.max
                      newValue = currentParentValue + difference
                    }

                    morph.default = newValue
                    // NO actualizar _default - mantener el valor original del modelo GLB

                    if (morph.name === 'Ancho') {
                      childItem.width = newValue
                    } else if (morph.name === 'Fondo') {
                      childItem.thickness = newValue
                    } else if (morph.name === 'Alto') {
                      if (!(isRBorRegleta && isEncimera)) {
                        childItem.height = newValue
                      }
                    } else if (morph.name === 'Regleta') {
                      childItem.regleta = newValue
                    }
                  }
                } else {
                  if (morph.name === 'Ancho') {
                    morph.default = parentWidth
                    morph._default = parentWidth
                    childItem.width = parentWidth
                  } else if (morph.name === 'Fondo') {
                    morph.default = parentThickness
                    morph._default = parentThickness
                    childItem.thickness = parentThickness
                  } else if (morph.name === 'Alto') {
                    morph.default = parentHeight
                    morph._default = parentHeight
                    childItem.height = parentHeight
                  } else if (morph.name === 'Regleta') {
                    morph.default = parentItem.regleta || 0
                    morph._default = parentItem.regleta || 0
                    childItem.regleta = parentItem.regleta || 0
                  }
                }
              }
            })
          }
        })
      }

      if (usesFreeDimensionScaling(parentItem) && morphChanged === 'Ancho') {
        const enc = parentItem.childs
          ? Array.from(parentItem.childs)
              .map((cid) => state.items.get(cid as AnyID))
              .find((c) => c?.description?.toLowerCase().includes('encimera'))
          : null
        logFreeDimensionAncho('updateLinkedSonPositions after', parentItem, {
          encimeraWidth: enc?.width,
          encimeraY: enc?.y,
        })
      }

      // Recalcular UV contiguas de encimeras (padre o vecinos MBJ cambiaron ancho/fondo/alto).
      if (
        parentItem.tipoNormalizado === 'MBJ' &&
        (morphChanged === 'Ancho' || morphChanged === 'Fondo' || morphChanged === 'Alto')
      ) {
        state.contadorModificaciones = (state.contadorModificaciones || 0) + 1
      }
    })
  },
  setLinkedHandPosition: (
    linkedID: LinkedItemID,
    hand: 'Izquierda' | 'Centro' | 'Derecha' | 'Normal',
  ) => {
    set((s) => {
      const item = s.items.get(linkedID)
      if (item && item.type === 'linkedSon' && item.description === 'Tirador') {
        const parent = s.items.get(item.firstParentID as LinkedItemID) as LinkedItemWithMorphs
        if (parent) {
          let prefix = 'b'
          if (parent.tipoNormalizado === 'MAL' || parent.tipoNormalizado === 'COL') prefix = 'a'

          const morphs = parent.morphs || []
          const morphAncho = morphs.find((m) => m.name === 'Ancho')
          const morphAlto = morphs.find((m) => m.name === 'Alto')
          const morphFondo = morphs.find((m) => m.name === 'Fondo')

          const widthBase = morphAncho?._default ?? parent.width ?? 0
          const widthActual = morphAncho?.value ?? parent.width ?? 0
          const heightBase = morphAlto?._default ?? parent.height ?? 0
          const heightActual = morphAlto?.value ?? parent.height ?? 0
          const depthBase = morphFondo?._default ?? parent.thickness ?? 0
          const depthActual = morphFondo?.value ?? parent.thickness ?? 0

          let X = 0,
            Y = 0,
            Z = 0

          const parentHand = (parent as LinkedItem).hand
          const isCentralHandNormal =
            parentHand === 'CN' && (hand === 'Izquierda' || hand === 'Derecha')

          if (hand === 'Izquierda') {
            if (isCentralHandNormal) {
              const bdX = (item as TiradorWithPositions).bdPosicionX
              const bdY = (item as TiradorWithPositions).bdPosicionY
              const bdZ = (item as TiradorWithPositions).bdPosicionZ
              const bcX = (item as TiradorWithPositions).bcPosicionX
              const bcY = (item as TiradorWithPositions).bcPosicionY
              const bcZ = (item as TiradorWithPositions).bcPosicionZ

              let x = bdX ?? bcX ?? 0
              let y = bdY ?? bcY ?? 0
              let z = bdZ ?? bcZ ?? 0

              if (parent.tipoNormalizado === 'MBJ') {
                if (bdX !== null && bdX !== undefined) {
                  if (isCentralHandNormal) {
                    x = bdX + (widthActual - widthBase) / 2
                  } else {
                    x = bdX + (widthActual - widthBase)
                  }
                } else if (bcX !== null && bcX !== undefined) {
                  x = bcX + (widthActual - widthBase) / 2
                }
                y = y - (heightBase - heightActual)
                z = z - (depthBase - depthActual)
              } else if (parent.tipoNormalizado === 'COL') {
                if (bdX !== null && bdX !== undefined) {
                  if (isCentralHandNormal) {
                    x = bdX + (widthActual - widthBase) / 2
                  } else {
                    x = bdX + (widthActual - widthBase)
                  }
                } else if (bcX !== null && bcX !== undefined) {
                  x = bcX + (widthActual - widthBase) / 2
                }
                z = z - (depthBase - depthActual)
              } else if (parent.tipoNormalizado === 'MAL') {
                if (bdX !== null && bdX !== undefined) {
                  if (isCentralHandNormal) {
                    x = bdX + (widthActual - widthBase) / 2
                  } else {
                    x = bdX + (widthActual - widthBase)
                  }
                } else if (bcX !== null && bcX !== undefined) {
                  x = bcX + (widthActual - widthBase) / 2
                }
                if (depthActual !== depthBase) {
                  z = z - (depthBase - depthActual)
                }
              }

              X = x
              Y = y
              Z = z
            } else {
              X = (item as TiradorWithPositions)[`${prefix}iPosicionX`] ?? 0
              Y = (item as TiradorWithPositions)[`${prefix}iPosicionY`] ?? 0
              Z = (item as TiradorWithPositions)[`${prefix}iPosicionZ`] ?? 0
            }
          } else if (hand === 'Centro') {
            X = (item as TiradorWithPositions)[`${prefix}cPosicionX`] ?? 0
            Y = (item as TiradorWithPositions)[`${prefix}cPosicionY`] ?? 0
            Z = (item as TiradorWithPositions)[`${prefix}cPosicionZ`] ?? 0
          } else if (hand === 'Derecha') {
            if (isCentralHandNormal) {
              const biX = (item as TiradorWithPositions).biPosicionX
              const biY = (item as TiradorWithPositions).biPosicionY
              const biZ = (item as TiradorWithPositions).biPosicionZ
              const bcX = (item as TiradorWithPositions).bcPosicionX
              const bcY = (item as TiradorWithPositions).bcPosicionY
              const bcZ = (item as TiradorWithPositions).bcPosicionZ

              let x = biX ?? bcX ?? 0
              let y = biY ?? bcY ?? 0
              let z = biZ ?? bcZ ?? 0

              if (parent.tipoNormalizado === 'MBJ') {
                if (biX !== null && biX !== undefined) {
                  if (isCentralHandNormal) {
                    x = biX + (widthActual - widthBase) / 2
                  } else {
                    x = biX + (widthActual - widthBase)
                  }
                } else if (bcX !== null && bcX !== undefined) {
                  x = bcX + (widthActual - widthBase) / 2
                }
                y = y - (heightBase - heightActual)
                z = z - (depthBase - depthActual)
              } else if (parent.tipoNormalizado === 'COL') {
                if (biX !== null && biX !== undefined) {
                  if (isCentralHandNormal) {
                    x = biX + (widthActual - widthBase) / 2
                  } else {
                    x = biX + (widthActual - widthBase)
                  }
                } else if (bcX !== null && bcX !== undefined) {
                  x = bcX + (widthActual - widthBase) / 2
                }
                z = z - (depthBase - depthActual)
              } else if (parent.tipoNormalizado === 'MAL') {
                if (biX !== null && biX !== undefined) {
                  if (isCentralHandNormal) {
                    x = biX + (widthActual - widthBase) / 2
                  } else {
                    x = biX + (widthActual - widthBase)
                  }
                } else if (bcX !== null && bcX !== undefined) {
                  x = bcX + (widthActual - widthBase) / 2
                }
                if (depthActual !== depthBase) {
                  z = z - (depthBase - depthActual)
                }
              }

              X = x
              Y = y
              Z = z
            } else {
              X = (item as TiradorWithPositions)[`${prefix}dPosicionX`] ?? 0
              Y = (item as TiradorWithPositions)[`${prefix}dPosicionY`] ?? 0
              Z = (item as TiradorWithPositions)[`${prefix}dPosicionZ`] ?? 0
            }
          }

          let x = X
          let y = Y
          let z = Z

          if (parent.tipoNormalizado === 'MBJ') {
            if (hand === 'Centro') {
              x = X + (widthActual - widthBase) / 2
            } else if (hand === 'Derecha') {
              x = X + (widthActual - widthBase)
            }
            y = Y - (heightBase - heightActual)
            z = Z - (depthBase - depthActual)
          } else if (parent.tipoNormalizado === 'COL') {
            if (hand === 'Centro') {
              x = X + (widthActual - widthBase) / 2
            } else if (hand === 'Derecha') {
              x = X + (widthActual - widthBase)
            }

            z = Z - (depthBase - depthActual)
          } else if (parent.tipoNormalizado === 'MAL') {
            if (hand === 'Centro') {
              x = X + (widthActual - widthBase) / 2
            } else if (hand === 'Derecha') {
              x = X + (widthActual - widthBase)
            }
            if (depthActual !== depthBase) {
              z = Z - (depthBase - depthActual)
            }
          }

          let rotationX = 0,
            rotationY = 0,
            rotationZ = 0

          if (hand === 'Izquierda') {
            if (isCentralHandNormal) {
              rotationX = (item as TiradorWithPositions)[`${prefix}cRotacionX`] ?? 0
              rotationY = (item as TiradorWithPositions)[`${prefix}cRotacionY`] ?? 0
              rotationZ = (item as TiradorWithPositions)[`${prefix}cRotacionZ`] ?? 0
            } else {
              rotationX = (item as TiradorWithPositions)[`${prefix}iRotacionX`] ?? 0
              rotationY = (item as TiradorWithPositions)[`${prefix}iRotacionY`] ?? 0
              rotationZ = (item as TiradorWithPositions)[`${prefix}iRotacionZ`] ?? 0
            }
          } else if (hand === 'Centro') {
            rotationX = (item as TiradorWithPositions)[`${prefix}cRotacionX`] ?? 0
            rotationY = (item as TiradorWithPositions)[`${prefix}cRotacionY`] ?? 0
            rotationZ = (item as TiradorWithPositions)[`${prefix}cRotacionZ`] ?? 0
          } else if (hand === 'Derecha') {
            if (isCentralHandNormal) {
              rotationX = (item as TiradorWithPositions)[`${prefix}cRotacionX`] ?? 0
              rotationY = (item as TiradorWithPositions)[`${prefix}cRotacionY`] ?? 0
              rotationZ = (item as TiradorWithPositions)[`${prefix}cRotacionZ`] ?? 0
            } else {
              rotationX = (item as TiradorWithPositions)[`${prefix}dRotacionX`] ?? 0
              rotationZ = (item as TiradorWithPositions)[`${prefix}dRotacionZ`] ?? 0
              rotationY = (item as TiradorWithPositions)[`${prefix}dRotacionY`] ?? 0
            }
          }

          let newMatrix: Matrix4

          const hasRotationValues = rotationX !== 0 || rotationY !== 0 || rotationZ !== 0
          const rotationZRad = (rotationZ * Math.PI) / 180

          if (hasRotationValues) {
            const rotationXRad = (rotationX * Math.PI) / 180
            const rotationYRad = (rotationY * Math.PI) / 180

            // Matriz de rotación - Z es el eje principal para horizontal/vertical
            // const rotationMatrix = new Matrix4().makeRotationFromEuler(
            //   new Euler(rotationXRad, rotationYRad, rotationZRad, 'XYZ'),
            // )

            newMatrix = new Matrix4()
              .makeRotationFromEuler(new Euler(rotationXRad, rotationYRad, rotationZRad, 'XYZ'))
              .setPosition(x, y, z)
          } else {
            newMatrix = new Matrix4().setPosition(x, y, z)
          }

          const posKey = hand === 'Izquierda' ? 'i' : hand === 'Centro' ? 'c' : 'd'
          const rawX = (item as any)[`${prefix}${posKey}PosicionX`]
          const rawY = (item as any)[`${prefix}${posKey}PosicionY`]
          const rawZ = (item as any)[`${prefix}${posKey}PosicionZ`]
          const isNull = rawX == null && rawY == null && rawZ == null
          const isZero = rawX === 0 && rawY === 0 && rawZ === 0
          const shouldHide = isNull || isZero

          const updated = {
            ...item,
            handPosition: hand,
            x: x,
            y: y,
            z: z,
            matrix: newMatrix,
            rotation: hasRotationValues ? rotationZRad : item.rotation,
            // Visibilidad basada SOLO en la posición final calculada
            visible: !(x === 0 && y === 0 && z === 0),
          }
          s.items.set(linkedID, updated)
          s.shouldUpdateCamera = true
        } else {
          const updated = { ...item, handPosition: hand } as TiradorWithPositions
          const posKey = hand === 'Izquierda' ? 'i' : hand === 'Centro' ? 'c' : 'd'
          // Detectar prefijo disponible: usar 'b' por defecto, caer a 'a' si 'b' no tiene datos pero 'a' sí
          let prefix: 'a' | 'b' = 'b'
          const bx = (updated as any)[`b${posKey}PosicionX`]
          const by = (updated as any)[`b${posKey}PosicionY`]
          const bz = (updated as any)[`b${posKey}PosicionZ`]
          const bHas = bx != null || by != null || bz != null
          const ax = (updated as any)[`a${posKey}PosicionX`]
          const ay = (updated as any)[`a${posKey}PosicionY`]
          const az = (updated as any)[`a${posKey}PosicionZ`]
          const aHas = ax != null || ay != null || az != null
          if (!bHas && aHas) prefix = 'a'

          if (hand === 'Izquierda') {
            updated.x = (updated as any)[`${prefix}iPosicionX`] ?? updated.x
            updated.y = (updated as any)[`${prefix}iPosicionY`] ?? updated.y
            updated.z = (updated as any)[`${prefix}iPosicionZ`] ?? updated.z
          } else if (hand === 'Centro') {
            updated.x = (updated as any)[`${prefix}cPosicionX`] ?? updated.x
            updated.y = (updated as any)[`${prefix}cPosicionY`] ?? updated.y
            updated.z = (updated as any)[`${prefix}cPosicionZ`] ?? updated.z
          } else if (hand === 'Derecha') {
            updated.x = (updated as any)[`${prefix}dPosicionX`] ?? updated.x
            updated.y = (updated as any)[`${prefix}dPosicionY`] ?? updated.y
            updated.z = (updated as any)[`${prefix}dPosicionZ`] ?? updated.z
          }

          // Visibilidad basada SOLO en la posición final calculada
          ;(updated as any).visible = !(updated.x === 0 && updated.y === 0 && updated.z === 0)

          s.items.set(linkedID, updated)
        }
      }
    })
  },
  restoreDefaultTiradoresForLinkedItem: async (linkedID: LinkedItemsID) => {
    const item = get().items.get(linkedID)
    if (!isLinkedItem(item)) return

    const catalogId = String(item.catalogID || '')
    if (!catalogId) return

    logGola('restoreDefaultTiradoresForLinkedItem', { linkedID, catalogId })

    await get().updateLinkedSonsForNewGLB(linkedID, catalogId, catalogId, {
      forceRestoreDefaultTiradores: true,
      useCatalogDefaultTextures: true,
    })

    set((s) => {
      const parent = s.items.get(linkedID)
      if (!isLinkedItem(parent) || !parent.childs) return
      parent.childs.forEach((childID) => {
        const child = s.items.get(childID)
        if (child?.type === 'linkedSon' && child.description === 'Tirador') {
          child.visible = true
        }
      })
    })
  },

  applyLinkedItemGolaModo: async (linkedID: LinkedItemsID, golaActiva: boolean) => {
    logGola('applyLinkedItemGolaModo', { linkedID, golaActiva })

    if (golaActiva) {
      set((s) => {
        const item = s.items.get(linkedID)
        if (!isLinkedItem(item)) return
        item.golaActiva = true
        if (item.childs) {
          item.childs.forEach((childID) => {
            const child = s.items.get(childID)
            if (child?.type === 'linkedSon' && child.description === 'Tirador') {
              child.visible = false
            }
          })
        }
      })
    } else {
      set((s) => {
        const item = s.items.get(linkedID)
        if (isLinkedItem(item)) item.golaActiva = false
      })
      await get().restoreDefaultTiradoresForLinkedItem(linkedID)
    }

    set((s) => {
      s.shouldUpdateCamera = true
    })
  },

  updateLinkedItemGola: async (linkedID: LinkedItemsID, newUnidadData: any) => {
    const item = get().items.get(linkedID)
    if (!isLinkedItem(item)) {
      logGola('updateLinkedItemGola_ABORT', { linkedID, reason: 'invalid_item' })
      return
    }

    const currentCatalogID = item.catalogID?.toString() || ''
    const newCatalogIDPreview = newUnidadData.codigo?.toString() || ''

    logGola('updateLinkedItemGola_START', {
      linkedID,
      from: currentCatalogID,
      to: newCatalogIDPreview,
      idUnidadesActual: item.idUnidades,
      idUnidadesNuevo: newUnidadData.idUnidades,
      idCambioArmazon: newUnidadData.idCambioArmazon,
      idCambioGolaBlanco: newUnidadData.idCambioGolaBlanco,
      idCambioGolaPetroleo: newUnidadData.idCambioGolaPetroleo,
      referenciaBlender: newUnidadData.referenciaBlender,
    })
    if (item.childs) {
      logGolaLinkedChildrenSnapshot(
        'updateLinkedItemGola_before',
        linkedID,
        (id) => get().items.get(id as AnyID),
        item.childs,
      )
      logGolaItemTexturesSnapshot(
        'updateLinkedItemGola_before',
        linkedID,
        item.textures as Array<Record<string, unknown>>,
        currentCatalogID,
      )
    }
    const itemDescription = String(item.description || '').toLowerCase()

    // NO procesar laterales - no deberían tener Gola automáticamente
    // KAB puede ser MAL (unidades altas) o lateral, pero si tiene " GO" es una unidad Gola válida
    // Solo detectar laterales reales: LA, LB, LC, CC, CB (sin " GO")
    const catalogUpper = currentCatalogID.toUpperCase()
    const isLateral =
      (itemDescription.includes('lateral') && !catalogUpper.includes(' GO')) ||
      (catalogUpper.startsWith('LA') && !catalogUpper.includes(' GO')) ||
      (catalogUpper.startsWith('LB') && !catalogUpper.includes(' GO')) ||
      (catalogUpper.startsWith('LC') && !catalogUpper.includes(' GO')) ||
      (catalogUpper.startsWith('CC') && !catalogUpper.includes(' GO')) ||
      (catalogUpper.startsWith('CB') && !catalogUpper.includes(' GO'))

    if (isLateral) {
      logGola('updateLinkedItemGola_SKIP', { linkedID, reason: 'is_lateral', currentCatalogID })
      return
    }

    const newCatalogID = newCatalogIDPreview
    const isCurrentlyGola = currentCatalogID.includes(' GO')
    const isChangingToGola = newCatalogID.includes(' GO')

    logGola('GOLA_STATE', {
      linkedID,
      isCurrentlyGola,
      isChangingToGola,
      direction: isChangingToGola ? 'to_gola' : 'from_gola',
    })

    // Detectar si es una vitrina (KABV o KAPV)
    const isVitrina =
      currentCatalogID.toUpperCase().startsWith('KABV') ||
      currentCatalogID.toUpperCase().startsWith('KAPV') ||
      currentCatalogID.toUpperCase().startsWith('PE KABV') ||
      currentCatalogID.toUpperCase().startsWith('PE KAPV')

    // Detectar si estamos cambiando el estado de gola o entre diferentes tipos de gola
    // Si ambas unidades tienen " GO", también debemos restaurar tiradores
    const isChangingGolaStateValue = isCurrentlyGola !== isChangingToGola
    const isChangingBetweenGolaTypes =
      isCurrentlyGola && isChangingToGola && currentCatalogID !== newCatalogID
    const shouldChangeChildren = isChangingGolaStateValue || isChangingBetweenGolaTypes

    let newLinkedOptions = null
    try {
      const linkedOptionsUrl = `api/Acabados/getelementosenlazadosunidadacabados/${newUnidadData.idUnidades}`
      logGolaApi('getelementosenlazadosunidadacabados', {
        idUnidades: newUnidadData.idUnidades,
        linkedID,
      })
      const response = await fetch(getProxyUrl(linkedOptionsUrl), {
        method: 'GET',
        mode: 'cors',
        headers: {
          'Accept-Language': 'ES',
          'Content-Type': 'application/json',
          Authorization: 'Ninpo ' + sessionStorage.getItem('access_token'),
        },
      })

      if (response.ok) {
        newLinkedOptions = await response.json()
        logGolaApi(
          'getelementosenlazadosunidadacabados',
          { idUnidades: newUnidadData.idUnidades, linkedID },
          newLinkedOptions,
          {
            hasTirador: Boolean(newLinkedOptions?.['0']?.Tirador || newLinkedOptions?.Tirador),
            hasGrifo: Boolean(newLinkedOptions?.['0']?.TPCGrifo || newLinkedOptions?.TPCGrifo),
            hasFregadero: Boolean(
              newLinkedOptions?.['0']?.Fregadero || newLinkedOptions?.Fregadero,
            ),
          },
        )
      } else {
        logGola('LINKED_OPTIONS_FAIL', {
          linkedID,
          status: response.status,
          idUnidades: newUnidadData.idUnidades,
        })
      }
    } catch (error) {
      logGola('LINKED_OPTIONS_ERROR', { linkedID, error: String(error) })
      console.error('GOLA UPDATE - Error obteniendo linkedOptions:', error)
    }

    if (newLinkedOptions) {
      set((s) => {
        const item = s.items.get(linkedID)
        if (!isLinkedItem(item)) {
          return
        }

        const optionsData = newLinkedOptions['0'] || newLinkedOptions

        // Preservar las opciones existentes si la nueva unidad no tiene opciones
        const preserveOptions = (
          newOptions: any[] | undefined,
          existingOptions: any[] | undefined,
        ) => {
          if (!newOptions || newOptions.length === 0) {
            return existingOptions || []
          }
          return newOptions
        }

        if (isChangingToGola) {
          item.tiradorOptions = []
          item.grifoOptions = preserveOptions(optionsData?.TPCGrifo, item.grifoOptions)
          item.fregaderoOptions = preserveOptions(optionsData?.Fregadero, item.fregaderoOptions)
          item.encimeraOptions = preserveOptions(optionsData?.Encimera, item.encimeraOptions)
          item.zocaloOptions = preserveOptions(optionsData?.Zocalo, item.zocaloOptions)
        } else {
          item.tiradorOptions = preserveOptions(optionsData?.Tirador, item.tiradorOptions)
          item.grifoOptions = preserveOptions(optionsData?.TPCGrifo, item.grifoOptions)
          item.fregaderoOptions = preserveOptions(optionsData?.Fregadero, item.fregaderoOptions)
          item.encimeraOptions = preserveOptions(optionsData?.Encimera, item.encimeraOptions)
          item.zocaloOptions = preserveOptions(optionsData?.Zocalo, item.zocaloOptions)
        }
      })
    }

    // Crear mapas para buscar texturas por zoneDisplayName y por zoneName
    const userAppliedTexturesByDisplayName = new Map<string, any>()
    const userAppliedTexturesByZoneName = new Map<string, any>()

    item.textures.forEach((texture) => {
      if (texture.zoneDisplayName) {
        userAppliedTexturesByDisplayName.set(texture.zoneDisplayName, texture)
      }
      if (texture.zoneName) {
        userAppliedTexturesByZoneName.set(texture.zoneName, texture)
      }
    })

    const store = get()
    const currentChilds = new Map<string, any>()

    if (item.childs?.size > 0) {
      for (const childID of item.childs) {
        const childItem = store.items.get(childID)
        if (childItem && childItem.type === 'linkedSon') {
          currentChilds.set(childItem.description || 'unknown', childItem)
        }
      }
    }

    const isChangingFromGola = isCurrentlyGola

    // Capturar el catalogID anterior ANTES de actualizarlo
    const previousCatalogID = currentCatalogID

    set((s) => {
      const item = s.items.get(linkedID)
      if (!isLinkedItem(item)) {
        return
      }

      if (newUnidadData.idUnidades) {
        item.idUnidades = newUnidadData.idUnidades
      }
      if (newUnidadData.codigo) {
        item.catalogID = newUnidadData.codigo
        item.golaActiva = String(newUnidadData.codigo).includes(' GO')
      }
      if (newUnidadData.referencia) {
        item.referencia = newUnidadData.referencia
      }
      applyIdCambioFromApi(item as Record<string, unknown>, newUnidadData)

      if (typeof newUnidadData.referenciaBlender === 'string') {
        item.glbPath = newUnidadData.referenciaBlender
      }
      if (typeof newUnidadData.referenciaJpgpng === 'string') {
        item.image = newUnidadData.referenciaJpgpng
      }

      const prevRegleta = item.regleta
      const prevRegletaMinimo = item.regletaMinimo
      const prevRegletaMaximo = item.regletaMaximo

      // Actualizar regleta, regletaMinimo y regletaMaximo desde newUnidadData si existen
      if (
        newUnidadData.regleta !== undefined &&
        newUnidadData.regleta !== null &&
        newUnidadData.regletaMinimo !== undefined &&
        newUnidadData.regletaMinimo !== null &&
        newUnidadData.regletaMaximo !== undefined &&
        newUnidadData.regletaMaximo !== null
      ) {
        item.regleta = newUnidadData.regleta
        item.regletaMinimo = newUnidadData.regletaMinimo
        item.regletaMaximo = newUnidadData.regletaMaximo
      } else if (
        prevRegleta !== undefined &&
        prevRegleta !== null &&
        prevRegletaMinimo !== undefined &&
        prevRegletaMinimo !== null &&
        prevRegletaMaximo !== undefined &&
        prevRegletaMaximo !== null
      ) {
        item.regleta = prevRegleta
        item.regletaMinimo = prevRegletaMinimo
        item.regletaMaximo = prevRegletaMaximo
      }

      if (newUnidadData.morphs && Array.isArray(newUnidadData.morphs)) {
        const preservedMap = new Map<string, number>()
        ;(item.morphs || []).forEach((m) => {
          const current = typeof m.default === 'number' ? m.default : m._default
          if (typeof current === 'number') preservedMap.set(m.name, current)
        })

        item.morphs = newUnidadData.morphs.map((morph: any) => {
          const name = morph.nombre || morph.name
          const min = morph.valorMin || morph.min
          const max = morph.valorMax || morph.max
          const base = morph.valor || morph.default
          let next = base
          if (preservedMap.has(name)) {
            const preserved = preservedMap.get(name) as number
            const clamped = Math.max(min, Math.min(max, preserved))
            next = clamped
          }
          return {
            _default: next,
            default: next,
            max,
            min,
            modifyable: true,
            morphId: morph.idMorph || morph.id,
            name,
            order: morph.orden || morph.order,
            show: true,
            type: morph.tipo || morph.type,
          }
        })

        const hasRegletaProps =
          item.regleta !== undefined &&
          item.regleta !== null &&
          item.regletaMinimo !== undefined &&
          item.regletaMinimo !== null &&
          item.regletaMaximo !== undefined &&
          item.regletaMaximo !== null

        if (hasRegletaProps) {
          const existingRegletaIndex = item.morphs.findIndex((m) => m.name === 'Regleta')
          const preservedRegletaValue = preservedMap.get('Regleta')

          if (existingRegletaIndex >= 0) {
            const regletaValue =
              preservedRegletaValue !== undefined ? preservedRegletaValue : item.regleta
            const clamped = Math.max(item.regletaMinimo, Math.min(item.regletaMaximo, regletaValue))
            item.morphs[existingRegletaIndex] = {
              ...item.morphs[existingRegletaIndex],
              default: clamped,
              _default: clamped,
              min: item.regletaMinimo,
              max: item.regletaMaximo,
            }
          } else {
            const regletaValue =
              preservedRegletaValue !== undefined ? preservedRegletaValue : item.regleta
            const clamped = Math.max(item.regletaMinimo, Math.min(item.regletaMaximo, regletaValue))
            item.morphs.push({
              default: clamped,
              _default: clamped,
              morphId: -1, // ID especial para regleta
              order: 999, // Al final
              type: 'R',
              min: item.regletaMinimo,
              max: item.regletaMaximo,
              name: 'Regleta',
              show: true,
              modifyable: true,
            })
          }
        }

        item.morphs.forEach((m) => {
          if (m.name === 'Ancho') {
            item.width = m.default
          } else if (m.name === 'Alto') {
            item.height = m.default
          } else if (m.name === 'Fondo') {
            item.thickness = m.default
          } else if (m.name === 'Regleta') {
            item.regleta = m.default
          }
        })
      }

      if (newUnidadData.acabados && Array.isArray(newUnidadData.acabados)) {
        // Para vitrinas, usar lógica especial para preservar TODAS las texturas
        if (isVitrina) {
          // Crear mapas para buscar texturas por zoneDisplayName y por zoneName
          const currentTexturesByZoneDisplayName = new Map<string, any>()
          const currentTexturesByZoneName = new Map<string, any>()
          const currentTexturesMap = new Map<string, any>()

          item.textures.forEach((texture) => {
            if (texture.zoneDisplayName) {
              currentTexturesByZoneDisplayName.set(texture.zoneDisplayName, texture)
              currentTexturesMap.set(texture.zoneDisplayName, texture)
            }
            if (texture.zoneName) {
              currentTexturesByZoneName.set(texture.zoneName, texture)
              if (!currentTexturesMap.has(texture.zoneDisplayName || '')) {
                currentTexturesMap.set(texture.zoneName, texture)
              }
            }
          })

          const updatedTextures: any[] = []
          const processedZoneNames = new Set<string>()
          const processedZoneDisplayNames = new Set<string>()

          // Primero procesar los acabados del backend
          newUnidadData.acabados.forEach((acabado: any) => {
            const displayName = acabado.descripcion || acabado.zona
            const zoneName = acabado.zona || ''
            processedZoneDisplayNames.add(displayName)
            if (zoneName) processedZoneNames.add(zoneName)

            // Buscar textura existente por zoneDisplayName o zoneName
            let existingTexture =
              currentTexturesByZoneDisplayName.get(displayName) ||
              currentTexturesByZoneName.get(zoneName)

            // Si no se encuentra, buscar por cualquier match parcial
            if (!existingTexture) {
              // Buscar por nombre similar (para casos como "Puerta" vs "Marco")
              for (const [key, texture] of currentTexturesMap.entries()) {
                if (
                  (texture.zoneDisplayName &&
                    texture.zoneDisplayName.toLowerCase() === displayName.toLowerCase()) ||
                  (texture.zoneName && texture.zoneName === zoneName) ||
                  (zoneName &&
                    texture.zoneName &&
                    texture.zoneName.includes(zoneName.split('_')[0]))
                ) {
                  existingTexture = texture
                  break
                }
              }
            }

            if (existingTexture) {
              // Preservar name y description de la textura existente (la que realmente está aplicada)
              const preservedName = existingTexture.name || acabado.texturas?.nombre
              const preservedDescription =
                existingTexture.description || existingTexture.descripcion || preservedName
              updatedTextures.push({
                ...existingTexture,
                linkedTextureID:
                  acabado.texturas?.identificadorTextura ||
                  acabado.idAcabadoUnidad ||
                  existingTexture.linkedTextureID,
                id: acabado.texturas?.identificadorTextura || existingTexture.id,
                groupID: acabado.texturas?.idGruposDeTexturas || existingTexture.groupID,
                searchID: acabado.texturas?.idTexturas || existingTexture.searchID,
                name: preservedName,
                description: preservedDescription,
                descripcion: preservedDescription,
                zoneName: zoneName || existingTexture.zoneName,
                zoneDisplayName: displayName || existingTexture.zoneDisplayName,
                zone: displayName || existingTexture.zone || existingTexture.zoneDisplayName,
              })
            } else {
              updatedTextures.push({
                id: acabado.texturas?.identificadorTextura || acabado.idAcabadoUnidad,
                name: acabado.texturas?.nombre || '',
                zoneName: zoneName,
                zoneDisplayName: displayName,
                zone: acabado.zona || '',
                url: acabado.texturas?.url || '',
                groupID: acabado.texturas?.idGruposDeTexturas || 0,
                searchID: acabado.texturas?.idTexturas || 0,
                linkedTextureID: acabado.texturas?.identificadorTextura || acabado.idAcabadoUnidad,
                color: acabado.texturas?.color || '#FFFFFF',
                repeatX: acabado.texturas?.factorRepeticionX || 1,
                repeatY: acabado.texturas?.factorRepeticionY || 1,
                opacity: acabado.texturas?.opacidad ?? 1,
                roughness: acabado.texturas?.brillo ?? 1,
                metalness: acabado.texturas?.metal ?? 0,
                envMapIntensity: acabado.texturas?.reflejo ?? 0,
                bumpScale: acabado.texturas?.rugosidad ?? 0,
                mirror: acabado.texturas?.espejo ?? false,
                modelo: acabado.modelo,
                acabado: acabado.acabado,
              })
            }
          })

          item.textures.forEach((existingTexture) => {
            const alreadyProcessed =
              processedZoneDisplayNames.has(existingTexture.zoneDisplayName) ||
              processedZoneNames.has(existingTexture.zoneName) ||
              updatedTextures.some(
                (t) =>
                  (t.zoneDisplayName && t.zoneDisplayName === existingTexture.zoneDisplayName) ||
                  (t.zoneName && t.zoneName === existingTexture.zoneName),
              )

            if (!alreadyProcessed) {
              updatedTextures.push(existingTexture)
            }
          })

          item.textures = updatedTextures
        } else {
          const currentTexturesByZoneDisplayName = new Map<string, any>()
          const currentTexturesByZoneName = new Map<string, any>()
          item.textures.forEach((texture) => {
            if (texture.zoneDisplayName) {
              currentTexturesByZoneDisplayName.set(texture.zoneDisplayName, texture)
            }
            if (texture.zoneName) {
              currentTexturesByZoneName.set(texture.zoneName, texture)
            }
          })

          const updatedTextures: any[] = []
          const processedZones = new Set<string>()

          newUnidadData.acabados.forEach((acabado: any) => {
            const displayName = acabado.descripcion || acabado.zona
            const zoneName = acabado.zona || ''
            processedZones.add(displayName)

            const existingTexture =
              currentTexturesByZoneDisplayName.get(displayName) ||
              currentTexturesByZoneName.get(zoneName)

            // Para la zona Gola, siempre usar los datos del backend, no preservar los existentes
            const isGolaZone = displayName === 'Gola' || zoneName === 'Gola'

            if (existingTexture && !isGolaZone) {
              const preservedName = existingTexture.name || acabado.texturas?.nombre
              // Para description, priorizar existingTexture.description o existingTexture.descripcion sobre el backend
              const preservedDescription =
                existingTexture.description || existingTexture.descripcion || preservedName
              updatedTextures.push({
                ...existingTexture,
                linkedTextureID: acabado.texturas?.identificadorTextura || acabado.idAcabadoUnidad,
                url: existingTexture.url || acabado.texturas?.url || '',
                id: acabado.texturas?.identificadorTextura || existingTexture.id,
                groupID: acabado.texturas?.idGruposDeTexturas || existingTexture.groupID,
                searchID: acabado.texturas?.idTexturas || existingTexture.searchID,
                acabado: existingTexture.acabado,
                modelo: existingTexture.modelo,
                name: preservedName,
                description: preservedDescription,
                descripcion: preservedDescription,
                color: existingTexture.color,
                repeatX: existingTexture.repeatX,
                repeatY: existingTexture.repeatY,
                opacity: existingTexture.opacity,
                roughness: existingTexture.roughness,
                metalness: existingTexture.metalness,
                mirror: existingTexture.mirror,
                bumpMap: existingTexture.bumpMap,
                bumpScale: existingTexture.bumpScale,
                displacement: existingTexture.displacement,
                displacementScale: existingTexture.displacementScale,
                textureRotation: existingTexture.textureRotation,
                complemento: existingTexture.complemento,
                img: existingTexture.img,
                envMapIntensity: existingTexture.envMapIntensity,
                zoneName: zoneName,
                zoneDisplayName: displayName,
                zone: displayName,
              })
            } else {
              // Para Gola o zonas nuevas, usar completamente los datos del backend
              const newTexture = {
                id: acabado.texturas?.identificadorTextura || acabado.idAcabadoUnidad,
                name: acabado.texturas?.nombre || '',
                zoneName: zoneName,
                zoneDisplayName: displayName,
                zone: acabado.zona || '',
                url: acabado.texturas?.url || '',
                groupID: acabado.texturas?.idGruposDeTexturas || 0,
                searchID: acabado.texturas?.idTexturas || 0,
                linkedTextureID: acabado.texturas?.identificadorTextura || acabado.idAcabadoUnidad,
                color: acabado.texturas?.color || '#FFFFFF',
                repeatX: acabado.texturas?.factorRepeticionX || 1,
                repeatY: acabado.texturas?.factorRepeticionY || 1,
                opacity: acabado.texturas?.opacidad ?? 1,
                roughness: acabado.texturas?.brillo ?? 1,
                metalness: acabado.texturas?.metal ?? 0,
                envMapIntensity: acabado.texturas?.reflejo ?? 0,
                bumpScale: acabado.texturas?.rugosidad ?? 0,
                mirror: acabado.texturas?.espejo ?? false,
                modelo: acabado.modelo,
                acabado: acabado.acabado,
              }
              updatedTextures.push(newTexture)
            }
          })

          item.textures.forEach((existingTexture) => {
            const alreadyProcessed = updatedTextures.some(
              (t) =>
                (t.zoneDisplayName === existingTexture.zoneDisplayName &&
                  existingTexture.zoneDisplayName) ||
                (t.zoneName === existingTexture.zoneName && existingTexture.zoneName),
            )

            if (!alreadyProcessed) {
              updatedTextures.push(existingTexture)
            }
          })

          item.textures = updatedTextures

          userAppliedTexturesByDisplayName.clear()
          userAppliedTexturesByZoneName.clear()
          updatedTextures.forEach((texture) => {
            if (texture.zoneDisplayName) {
              userAppliedTexturesByDisplayName.set(texture.zoneDisplayName, texture)
            }
            if (texture.zoneName) {
              userAppliedTexturesByZoneName.set(texture.zoneName, texture)
            }
          })
        }
      }

      if (shouldChangeChildren && isChangingToGola) {
        const childrenToRemove: string[] = []
        item.childs.forEach((childID) => {
          const child = s.items.get(childID)
          if (child && child.type === 'linkedSon' && child.description === 'Tirador') {
            childrenToRemove.push(childID)
          }
        })

        childrenToRemove.forEach((childID) => {
          s.items.delete(childID)
          item.childs.delete(childID as any)
        })

        item.childs.forEach((childID) => {
          const child = s.items.get(childID)
          if (child && child.type === 'linkedSon') {
            s.editItem(childID as any, { visible: true })
          }
        })
      }
    })

    try {
      const newCatalogId = newUnidadData.codigo || newUnidadData.referencia

      const glbCached = get().sceneGLB.has(newCatalogId)
      logGola('GLB_LOAD', {
        linkedID,
        newCatalogId,
        referenciaBlender: newUnidadData.referenciaBlender,
        glbCached,
      })
      if (!glbCached) {
        const loader = await import('@utils/sceneObjects/setGLTFLoader').then((mod) =>
          mod.setGLTFLoader(),
        )
        const glb = await loader.loadAsync(newUnidadData.referenciaBlender)

        set((s) => {
          if (!s.sceneGLB.has(newCatalogId)) {
            s.sceneGLB.set(newCatalogId, {
              glb,
              backGLBInfo: newUnidadData,
            })
          }
        })
      }
      logGola('GLB_LOAD_DONE', { linkedID, newCatalogId, glbCached })

      logGolaApi('acabadosGetUnidadAcabadosInfo', {
        idUnidades: newUnidadData.idUnidades,
        linkedID,
      })
      const acabadosInfo = await API().api.acabadosGetUnidadAcabadosInfo(newUnidadData.idUnidades)
      logGolaApi(
        'acabadosGetUnidadAcabadosInfo',
        { idUnidades: newUnidadData.idUnidades, linkedID },
        acabadosInfo,
        { zonasCount: acabadosInfo?.data?.zonas?.length },
      )

      const glbData = get().sceneGLB.get(newCatalogId)
      if (glbData?.glb) {
        const extraInfo = acabadosInfo.data.zonas
          ? { zonas: acabadosInfo.data.zonas as { idAcabadoUnidad: number; zona: string }[] }
          : undefined

        const { zones, textures: glbTextures } = getZonesFromGLB(
          glbData.glb.scene?.children,
          newUnidadData.acabados,
          extraInfo,
        )

        set((s) => {
          const item = s.items.get(linkedID)
          if (item && item.type === 'linkedItem') {
            const existingZones = { ...item.zones }
            const existingZonesByMaterial = new Map<string, any>()

            Object.keys(existingZones).forEach((zoneKey) => {
              const zone = existingZones[zoneKey]
              if (zone.material?.modelo) {
                existingZonesByMaterial.set(zone.material.modelo, zone)
              }
              if (zone.material?.acabado) {
                existingZonesByMaterial.set(zone.material.acabado, zone)
              }
              if (zone.material?.name) {
                existingZonesByMaterial.set(zone.material.name, zone)
              }
            })

            // Crear un conjunto de zonas válidas según los acabados de la API
            // Esto filtra zonas del GLB que no deberían estar (ej: Gola cuando el elemento no tiene Gola)
            const validZonesFromAPI = new Set<string>()
            if (Array.isArray(newUnidadData.acabados)) {
              newUnidadData.acabados.forEach((acabado: any) => {
                if (acabado.zona) {
                  validZonesFromAPI.add(acabado.zona)
                  if (acabado.descripcion) {
                    validZonesFromAPI.add(acabado.descripcion)
                  }
                }
              })
            }

            const zonesCopy: any = JSON.parse(JSON.stringify(zones))

            // Filtrar zonas que no están en los acabados de la API
            const filteredZonesCopy: any = {}
            Object.keys(zonesCopy).forEach((zoneKey) => {
              const newZone = zonesCopy[zoneKey]
              const zoneDisplayName = newZone.material?.zoneDisplayName || newZone.material?.zone

              // Incluir todas las zonas por ahora (las zonas pueden tener nombres diferentes)
              // Pero filtrar si podemos determinar que no es válida
              // Por ahora, incluimos todas las zonas y nos enfocamos en filtrar las texturas
              filteredZonesCopy[zoneKey] = newZone
            })

            Object.keys(filteredZonesCopy).forEach((zoneKey) => {
              const newZone = filteredZonesCopy[zoneKey]

              let existingZone = null
              if (newZone.material?.modelo) {
                existingZone = existingZonesByMaterial.get(newZone.material.modelo)
              }
              if (!existingZone && newZone.material?.acabado) {
                existingZone = existingZonesByMaterial.get(newZone.material.acabado)
              }
              if (!existingZone && newZone.material?.name) {
                existingZone = existingZonesByMaterial.get(newZone.material.name)
              }

              if (existingZone) {
                filteredZonesCopy[zoneKey] = {
                  ...newZone,
                  ...(existingZone.centerInfo && { centerInfo: existingZone.centerInfo }),
                  ...(existingZone.contextInfo && { contextInfo: existingZone.contextInfo }),
                  ...Object.keys(existingZone).reduce(
                    (acc: Record<string, any>, key) => {
                      if (
                        key.includes('Info') ||
                        key.includes('Context') ||
                        key.includes('Center')
                      ) {
                        acc[key] = existingZone[key]
                      }
                      return acc
                    },
                    {} as Record<string, any>,
                  ),
                }
              }
            })

            item.zones = filteredZonesCopy

            const newTextures: any[] = []
            const processedZoneNames = new Set<string>()
            const processedZoneDisplayNames = new Set<string>()

            // ACTUALIZAR mapas de texturas con las texturas que acabamos de establecer
            // Esto es crítico para que use las texturas correctas del backend (especialmente Gola)
            const updatedUserTexturesByDisplayName = new Map<string, any>()
            const updatedUserTexturesByZoneName = new Map<string, any>()
            item.textures.forEach((texture) => {
              if (texture.zoneDisplayName) {
                updatedUserTexturesByDisplayName.set(texture.zoneDisplayName, texture)
              }
              if (texture.zoneName) {
                updatedUserTexturesByZoneName.set(texture.zoneName, texture)
              }
            })

            glbTextures.forEach((glbTexture: any) => {
              const zoneName = glbTexture.zoneName
              const zoneDisplayName = glbTexture.zoneDisplayName

              // FILTRAR: Solo incluir texturas cuya zona está en los acabados de la API
              const isValidZone =
                validZonesFromAPI.has(zoneDisplayName) ||
                validZonesFromAPI.has(glbTexture.zone) ||
                zoneDisplayName === 'Armazon' // Armazon puede estar en el GLB pero no en acabados

              if (!isValidZone) {
                return
              }

              // Usar los mapas actualizados que tienen las texturas correctas del backend
              let userTexture =
                updatedUserTexturesByDisplayName.get(zoneDisplayName) ||
                updatedUserTexturesByZoneName.get(zoneName) ||
                userAppliedTexturesByDisplayName.get(zoneDisplayName) ||
                userAppliedTexturesByZoneName.get(zoneName)

              if (isVitrina && !userTexture) {
                for (const [key, texture] of userAppliedTexturesByDisplayName.entries()) {
                  if (
                    texture.zoneDisplayName?.toLowerCase() === zoneDisplayName?.toLowerCase() ||
                    (zoneName && texture.zoneName && texture.zoneName === zoneName) ||
                    (zoneName &&
                      texture.zoneName &&
                      texture.zoneName.includes(zoneName.split('_')[0]))
                  ) {
                    userTexture = texture
                    break
                  }
                }
              }

              if (userTexture) {
                processedZoneNames.add(zoneName)
                processedZoneDisplayNames.add(zoneDisplayName)

                newTextures.push({
                  ...userTexture,
                  id: glbTexture.id || userTexture.id,
                  linkedTextureID: glbTexture.linkedTextureID || userTexture.linkedTextureID,
                  zoneName: zoneName || userTexture.zoneName,
                  zoneDisplayName: zoneDisplayName || userTexture.zoneDisplayName,
                  zone: zoneDisplayName || userTexture.zone || userTexture.zoneDisplayName,
                })
              } else {
                processedZoneNames.add(zoneName)
                processedZoneDisplayNames.add(zoneDisplayName)
                newTextures.push({
                  ...glbTexture,
                  zoneName: zoneName,
                  zoneDisplayName: zoneDisplayName,
                  zone: zoneDisplayName,
                })
              }
            })

            item.textures.forEach((existingTexture) => {
              const alreadyProcessed =
                processedZoneDisplayNames.has(existingTexture.zoneDisplayName) ||
                processedZoneNames.has(existingTexture.zoneName) ||
                newTextures.some(
                  (t) =>
                    (t.zoneDisplayName && t.zoneDisplayName === existingTexture.zoneDisplayName) ||
                    (t.zoneName && t.zoneName === existingTexture.zoneName),
                )

              // También verificar que la textura existente esté en los acabados de la API
              const isValidExistingTexture =
                validZonesFromAPI.has(existingTexture.zoneDisplayName) ||
                validZonesFromAPI.has(existingTexture.zone) ||
                existingTexture.zoneDisplayName === 'Armazon'

              if (!alreadyProcessed && isValidExistingTexture) {
                newTextures.push(existingTexture)
              }
            })

            item.textures = newTextures

            // Filtrar zonas para que solo se incluyan las que tienen texturas válidas
            const validZoneNames = new Set<string>()
            newTextures.forEach((texture) => {
              if (texture.zoneName) {
                validZoneNames.add(texture.zoneName)
              }
            })

            // Filtrar zonas que no tienen texturas válidas
            const finalFilteredZones: any = {}
            Object.keys(item.zones).forEach((zoneKey) => {
              if (validZoneNames.has(zoneKey)) {
                finalFilteredZones[zoneKey] = item.zones[zoneKey]
              }
            })
            item.zones = finalFilteredZones

            syncLinkedItemZonesFromTextures(item, {
              itemId: linkedID,
              catalogID: String(item.catalogID ?? newCatalogId),
              source: 'updateLinkedItemGola',
            })
          }
        })
      }

      set((s) => {
        // No retornar nada - solo forzar re-render
      })
    } catch (error) {
      console.error('Error actualizando GLB del item principal:', error)
    }

    if (newLinkedOptions) {
      const optionsData = newLinkedOptions['0'] || newLinkedOptions

      const finalItem = get().items.get(linkedID)
      if (finalItem && finalItem.type === 'linkedItem' && finalItem.childs) {
        finalItem.childs.forEach((childID) => {
          const child = get().items.get(childID)
          if (child && child.type === 'linkedSon') {
            let matchingOption = null

            if (child.description === 'TPCGrifo' && optionsData.TPCGrifo) {
              const currentDescription = child.catalogID || child.referencia
              matchingOption = optionsData.TPCGrifo.find(
                (opt: any) => opt.descripcion === currentDescription,
              )
            } else if (child.description === 'Fregadero' && optionsData.Fregadero) {
              const currentDescription = child.catalogID || child.referencia

              matchingOption = optionsData.Fregadero.find(
                (opt: any) => opt.descripcion === currentDescription,
              )
            } else if (child.description === 'Encimera' && optionsData.Encimera) {
              const currentDescription = child.catalogID || child.referencia

              matchingOption = optionsData.Encimera.find(
                (opt: any) => opt.descripcion === currentDescription,
              )
            }

            if (matchingOption) {
              get().editItem(childID as any, {
                linkedID: matchingOption.idElementosEnlazados,
                idUnidades: matchingOption.idUnidadEnlazada,
                catalogID: matchingOption.descripcion,
                referencia: matchingOption.descripcion,
              })
            }
          }
        })
      }
    }

    logGola('CHILDREN_DECISION', {
      linkedID,
      shouldChangeChildren,
      isChangingFromGola,
      isChangingToGola,
      willRestoreTiradores: shouldChangeChildren && isChangingFromGola,
      willRemoveTiradores: shouldChangeChildren && isChangingToGola,
      previousCatalogID,
      newCatalogID,
    })

    if (shouldChangeChildren && isChangingFromGola) {
      try {
        logGola('CALL_updateLinkedSonsForNewGLB', {
          linkedID,
          newCatalogId: newUnidadData.codigo || newUnidadData.referencia,
          previousCatalogID,
        })
        await store.updateLinkedSonsForNewGLB(
          linkedID,
          newUnidadData.codigo || newUnidadData.referencia,
          previousCatalogID,
        )
        logGola('RESULT_updateLinkedSonsForNewGLB', { linkedID })
        logGolaLinkedChildrenSnapshot(
          'after_restore_tiradores',
          linkedID,
          (id) => get().items.get(id as AnyID),
          get().items.get(linkedID)?.childs ?? [],
        )
      } catch (error) {
        logGola('RESTORE_TIRADORES_ERROR', { linkedID, error: String(error) })
        console.error('GOLA UPDATE - Error restaurando tiradores:', error)
      }
    }

    // Solo establecer visibilidad si NO estamos cambiando el estado de gola
    // Cuando cambiamos gola, la visibilidad se maneja en updateLinkedSonsForNewGLB
    const isRestoringTiradores = shouldChangeChildren && isChangingFromGola
    if (!isRestoringTiradores) {
      const finalUpdatedItem = get().items.get(linkedID)
      if (finalUpdatedItem && finalUpdatedItem.type === 'linkedItem' && finalUpdatedItem.childs) {
        finalUpdatedItem.childs.forEach((childID) => {
          const child = get().items.get(childID)
          if (child && child.type === 'linkedSon') {
            get().editItem(childID as any, { visible: true })
          }
        })
      }
    }

    // NO ajustar posiciones de hijos cuando solo se cambia un acabado (ej: Gola)
    // Solo ajustar cuando cambia la altura del padre (morphs de Alto)
    // Esto evita que cambiar acabados de zonas modifique incorrectamente las posiciones de los linkedSon
    const updatedItem = get().items.get(linkedID)
    const previousHeight = item.height
    const newHeight = updatedItem?.height

    // Solo ajustar posiciones si cambió la altura del padre
    const heightChanged = previousHeight !== newHeight

    if (
      heightChanged &&
      updatedItem &&
      updatedItem.type === 'linkedItem' &&
      updatedItem.childs &&
      Array.isArray(updatedItem.childs) &&
      updatedItem.childs.length > 0
    ) {
      adjustChildrenPositionsAfterInheritance(
        updatedItem,
        (childId) => get().items.get(childId),
        (childId, updates) => {
          get().editItem(childId, updates)
        },
      )
    }

    if (newLinkedOptions) {
      const contextUpdateEvent = new CustomEvent('linkedOptionsUpdated', {
        detail: {
          linkedID,
          newLinkedOptions,
          targetIdUnidades: newUnidadData.idUnidades,
        },
      })
      window.dispatchEvent(contextUpdateEvent)

      set((s) => {
        const item = s.items.get(linkedID as AnyID)
        if (item && isLinkedItem(item)) {
          item.lastLinkedOptionsUpdate = Date.now()
        }
      })

      const contextUpdateEvent2 = new CustomEvent('linkedOptionsUpdated', {
        detail: {
          linkedID,
          newLinkedOptions,
          targetIdUnidades: newUnidadData.idUnidades,
          forceUpdate: true,
        },
      })
      window.dispatchEvent(contextUpdateEvent2)

      setTimeout(() => {
        const contextUpdateEvent3 = new CustomEvent('linkedOptionsUpdated', {
          detail: {
            linkedID,
            newLinkedOptions,
            targetIdUnidades: newUnidadData.idUnidades,
            forceUpdate: true,
            delayed: true,
          },
        })
        window.dispatchEvent(contextUpdateEvent3)
      }, 200)
    }

    const finalItem = get().items.get(linkedID)
    if (finalItem && isLinkedItem(finalItem)) {
      logGolaItemTexturesSnapshot(
        'updateLinkedItemGola_after',
        linkedID,
        finalItem.textures as Array<Record<string, unknown>>,
        String(finalItem.catalogID || ''),
      )
      if (finalItem.childs) {
        logGolaLinkedChildrenSnapshot(
          'updateLinkedItemGola_after',
          linkedID,
          (id) => get().items.get(id as AnyID),
          finalItem.childs,
        )
      }
    }
    logGola('updateLinkedItemGola_DONE', {
      linkedID,
      catalogID: finalItem?.catalogID,
      idUnidades: isLinkedItem(finalItem) ? finalItem.idUnidades : null,
      idcambiogolablanco: isLinkedItem(finalItem) ? finalItem.idcambiogolablanco : null,
      idcambiogolapetroleo: isLinkedItem(finalItem) ? finalItem.idcambiogolapetroleo : null,
      idcambioarmazon: isLinkedItem(finalItem) ? finalItem.idcambioarmazon : null,
    })
  },
  updateLinkedItemArmazonOptimized: async (linkedID: LinkedItemsID, newUnidadData: any) => {
    const parentItem = get().items.get(linkedID)
    if (!isLinkedItem(parentItem)) {
      return
    }

    // const parentInfo = {
    //   catalogID: parentItem.catalogID,
    //   tipoNormalizado: parentItem.tipoNormalizado,
    // }

    // const parentId = linkedID
    const newIdUnidades = newUnidadData.idUnidades || parentItem.idUnidades
    // Guardar el catalogID antes de actualizar para poder actualizar sceneGLB después
    const catalogIDAntesDeActualizar = String(parentItem.catalogID || '')

    // Obtener tiradorOptions sin cargar GLB
    let newTiradorOptions: any[] | null = null
    try {
      const tiradorOptionsResponse =
        await API().api.acabadosGetElementosEnlazadosUnidadAcabados(newIdUnidades)

      if (
        tiradorOptionsResponse?.data &&
        tiradorOptionsResponse.data['0'] &&
        Array.isArray(tiradorOptionsResponse.data['0'].Tirador)
      ) {
        newTiradorOptions = tiradorOptionsResponse.data['0'].Tirador as any[]
      }
    } catch (error) {
      console.error('Error obteniendo tiradorOptions:', error)
    }

    // Procesar tiradores (igual que antes)
    const tiradorElementsById = new Map<number, any>()
    const tiradorElementsByNode = new Map<number, any>()

    if (Array.isArray(newUnidadData.elementosEnlazados)) {
      newUnidadData.elementosEnlazados.forEach((element: any) => {
        if (element?.descripcion !== 'Tirador') return

        if (typeof element.idElementosEnlazados === 'number') {
          tiradorElementsById.set(element.idElementosEnlazados, element)
        }

        if (typeof element.nodo === 'number' && !tiradorElementsByNode.has(element.nodo)) {
          tiradorElementsByNode.set(element.nodo, element)
        }
      })
    }

    const tiradorUpdatesByChild = new Map<string, { option?: any; positions?: any }>()

    if (parentItem.childs?.size > 0) {
      for (const childID of parentItem.childs) {
        const childItem = get().items.get(childID)
        if (!childItem || childItem.type !== 'linkedSon' || childItem.description !== 'Tirador') {
          continue
        }

        const childNode = childItem.option ?? childItem.nodo
        const childDescription =
          childItem.catalogID || childItem.referencia || childItem.description

        let targetOption: any = null
        if (Array.isArray(newTiradorOptions)) {
          targetOption = newTiradorOptions.find(
            (option: any) => option.nodo === childNode && option.descripcion === childDescription,
          )

          if (!targetOption) {
            targetOption = newTiradorOptions.find(
              (option: any) => option.nodo === childNode && option.porDefecto,
            )
          }

          if (!targetOption) {
            targetOption = newTiradorOptions.find((option: any) => option.nodo === childNode)
          }
        }

        let positions =
          (targetOption && tiradorElementsById.get(targetOption.idElementosEnlazados)) ||
          (childItem.linkedID ? tiradorElementsById.get(childItem.linkedID) : null) ||
          (typeof childNode === 'number' ? tiradorElementsByNode.get(childNode) : null)

        if (targetOption) {
          try {
            const response = await API().api.acabadosGetElementosEnlazadosUnidadAcabados2(
              newIdUnidades,
              targetOption.idElementosEnlazados,
            )

            if (response?.data) {
              positions = response.data
            }
          } catch (error) {
            console.error('Error obteniendo posiciones del tirador:', error)
          }
        }

        tiradorUpdatesByChild.set(childID as string, {
          option: targetOption ?? undefined,
          positions: positions ?? undefined,
        })
      }
    }

    // Actualizar propiedades del item SIN cargar GLB
    set((s) => {
      const item = s.items.get(linkedID)
      if (!isLinkedItem(item)) {
        return
      }

      if (Array.isArray(newTiradorOptions)) {
        item.tiradorOptions = newTiradorOptions as any[]
      }

      if (newUnidadData.idUnidades) {
        item.idUnidades = newUnidadData.idUnidades
      }

      const armazonBlancoFromBackend =
        newUnidadData.ArmazonBlanco !== undefined
          ? newUnidadData.ArmazonBlanco
          : (newUnidadData as any).armazonBlanco !== undefined
            ? (newUnidadData as any).armazonBlanco
            : undefined

      if (typeof armazonBlancoFromBackend === 'boolean') {
        item.ArmazonBlanco = armazonBlancoFromBackend
      } else {
        // Si no viene del backend, invertir el valor actual
        item.ArmazonBlanco = !item.ArmazonBlanco
      }

      // Actualizar catalogID para reflejar correctamente el armazón (blanco/petróleo)
      // El prefijo "PE " indica que es petróleo, sin prefijo es blanco
      // Priorizar newUnidadData.codigo si está disponible, sino sincronizar con ArmazonBlanco
      if (newUnidadData.codigo) {
        item.catalogID = newUnidadData.codigo
      } else {
        // Si no hay codigo en newUnidadData, sincronizar catalogID con ArmazonBlanco
        const currentCatalogID = String(item.catalogID || '')
        const baseCatalogID = currentCatalogID.startsWith('PE ')
          ? currentCatalogID.substring(3)
          : currentCatalogID

        if (item.ArmazonBlanco) {
          // Si es blanco, asegurar que no tenga prefijo "PE "
          if (currentCatalogID.startsWith('PE ')) {
            item.catalogID = baseCatalogID
          }
        } else {
          // Si es petróleo, asegurar que tenga prefijo "PE "
          if (!currentCatalogID.startsWith('PE ')) {
            item.catalogID = `PE ${baseCatalogID}`
          }
        }
      }

      // Forzar actualización del contexto incrementando un contador interno
      // Esto asegura que el objeto se actualice en LinkedItemZoneContext
      const itemAny = item as any
      if (!itemAny._armazonUpdateCounter) {
        itemAny._armazonUpdateCounter = 0
      }
      itemAny._armazonUpdateCounter++
      if (newUnidadData.idCambioArmazon) {
        item.idcambioarmazon = newUnidadData.idCambioArmazon
      }
      if (newUnidadData.idCambioGolaBlanco) {
        item.idcambiogolablanco = newUnidadData.idCambioGolaBlanco
      }
      if (newUnidadData.idCambioGolaPetroleo) {
        item.idcambiogolapetroleo = newUnidadData.idCambioGolaPetroleo
      }

      if (typeof newUnidadData.referencia === 'string') {
        item.referencia = newUnidadData.referencia
      }
      // NO actualizar glbPath - el GLB debe permanecer igual, solo cambia la textura del armazón
      // El cambio de armazón solo afecta el color/textura, no el modelo 3D
      // if (typeof newUnidadData.referenciaBlender === 'string' && newUnidadData.referenciaBlender) {
      //   item.glbPath = newUnidadData.referenciaBlender
      // }
      if (typeof newUnidadData.referenciaJpgpng === 'string') {
        item.image = newUnidadData.referenciaJpgpng
      }

      // Actualizar morphs (igual que antes)
      if (newUnidadData.morphs && Array.isArray(newUnidadData.morphs)) {
        item.morphs?.forEach((m) => {
          if (m.name === 'Ancho') {
            m.value = item.width
            m.default = item.width
            m._default = item.width
          }
          if (m.name === 'Alto') {
            m.value = item.height
            m.default = item.height
            m._default = item.height
          }
          if (m.name === 'Fondo') {
            m.value = item.thickness
            m.default = item.thickness
            m._default = item.thickness
          }
        })

        const morphsToPreserve =
          item.morphs?.map((m) => ({
            name: m.name,
            value: m.value ?? m.default ?? m._default,
          })) || []

        item.morphs = newUnidadData.morphs.map((morph: any) => ({
          _default: morph.valor || morph.default,
          default: morph.valor || morph.default,
          max: morph.valorMax || morph.max,
          min: morph.valorMin || morph.min,
          modifyable: true,
          morphId: morph.idMorph || morph.id,
          name: morph.nombre || morph.name,
          order: morph.orden || morph.order,
          show: true,
          type: morph.tipo || morph.type,
          value: morph.valor || morph.default,
        }))

        item.morphs.forEach((m) => {
          const preserved = morphsToPreserve.find((pm) => pm.name === m.name)
          if (preserved) {
            m.value = preserved.value
            m.default = preserved.value
          }
        })

        item.morphs.forEach((m) => {
          if (m.name === 'Ancho') {
            item.width = m.value
          } else if (m.name === 'Alto') {
            item.height = m.value
          } else if (m.name === 'Fondo') {
            item.thickness = m.value
          }
        })
      }

      // ACTUALIZAR SOLO TEXTURAS DE ZONA "Armazon" (sin procesar GLB)
      if (newUnidadData.acabados && Array.isArray(newUnidadData.acabados)) {
        // Actualizar texturas
        item.textures = item.textures.map((texture) => {
          if (texture.zoneDisplayName !== 'Armazon') {
            return texture
          }

          const matchingAcabado = newUnidadData.acabados.find((acabado: any) => {
            return (
              acabado.acabado?.toLowerCase().includes('armazon') ||
              acabado.acabado?.toLowerCase().includes('basico') ||
              acabado.acabado?.toLowerCase().includes('petroleo')
            )
          })

          if (matchingAcabado) {
            return {
              ...texture,
              linkedTextureID:
                matchingAcabado.texturas?.identificadorTextura || matchingAcabado.idAcabadoUnidad,
              id: matchingAcabado.texturas?.identificadorTextura || matchingAcabado.idAcabadoUnidad,
              name: matchingAcabado.texturas?.nombre || texture.name,
              url: matchingAcabado.texturas?.url || texture.url,
              searchID: matchingAcabado.texturas?.idTexturas || texture.searchID,
              groupID: matchingAcabado.texturas?.idGruposDeTexturas || texture.groupID,
              acabado: matchingAcabado.texturas?.descripcion || texture.acabado,
            }
          }
          return texture
        })

        // Actualizar zonas (materiales)
        Object.keys(item.zones).forEach((zoneKey) => {
          const zone = item.zones[zoneKey]
          const matchingTexture = item.textures.find((texture) => texture.zoneName === zone.name)

          if (matchingTexture && matchingTexture.zoneDisplayName === 'Armazon') {
            const matchingAcabado = newUnidadData.acabados.find((acabado: any) => {
              return (
                acabado.acabado?.toLowerCase().includes('armazon') ||
                acabado.acabado?.toLowerCase().includes('basico') ||
                acabado.acabado?.toLowerCase().includes('petroleo')
              )
            })

            if (matchingAcabado && matchingAcabado.texturas) {
              zone.material = {
                ...zone.material,
                color: matchingAcabado.texturas.color || zone.material.color,
                url: matchingAcabado.texturas.url || zone.material.url,
                name: matchingAcabado.texturas.nombre || zone.material.name,
                groupID: matchingAcabado.texturas.idGruposDeTexturas || zone.material.groupID,
                searchID: matchingAcabado.texturas.idTexturas || zone.material.searchID,
                id: matchingAcabado.texturas.identificadorTextura || zone.material.id,
              }
            }
          }
        })
      }

      // Actualizar hijos (tiradores, encimeras) - igual que antes
      if (item.childs?.size > 0) {
        item.childs.forEach((childID) => {
          const childItem = s.items.get(childID)
          if (childItem && childItem.type === 'linkedSon') {
            if (childItem.description === 'Tirador') {
              const tiradorUpdate = tiradorUpdatesByChild.get(childID as string)
              const tiradorNewPositions = tiradorUpdate?.positions

              if (tiradorUpdate?.option) {
                childItem.linkedID = tiradorUpdate.option.idElementosEnlazados
                childItem.idElementosEnlazados = tiradorUpdate.option.idElementosEnlazados
                childItem.idUnidades = tiradorUpdate.option.idUnidadEnlazada
                childItem.catalogID = tiradorUpdate.option.descripcion ?? childItem.catalogID
                childItem.code = tiradorUpdate.option.descripcion ?? childItem.code
                childItem.referencia = tiradorUpdate.option.descripcion ?? childItem.referencia
              }

              if (tiradorNewPositions) {
                const posicionesAlternativas = [
                  'biPosicionX',
                  'biPosicionY',
                  'biPosicionZ',
                  'bcPosicionX',
                  'bcPosicionY',
                  'bcPosicionZ',
                  'bdPosicionX',
                  'bdPosicionY',
                  'bdPosicionZ',
                  'aiPosicionX',
                  'aiPosicionY',
                  'aiPosicionZ',
                  'acPosicionX',
                  'acPosicionY',
                  'acPosicionZ',
                  'adPosicionX',
                  'adPosicionY',
                  'adPosicionZ',
                  'biRotacionX',
                  'biRotacionY',
                  'biRotacionZ',
                  'bcRotacionX',
                  'bcRotacionY',
                  'bcRotacionZ',
                  'bdRotacionX',
                  'bdRotacionY',
                  'bdRotacionZ',
                  'aiRotacionX',
                  'aiRotacionY',
                  'aiRotacionZ',
                  'acRotacionX',
                  'acRotacionY',
                  'acRotacionZ',
                  'adRotacionX',
                  'adRotacionY',
                  'adRotacionZ',
                  'b2idRotacionX',
                  'b2idRotacionY',
                  'b2idRotacionZ',
                  'a2idRotacionX',
                  'a2idRotacionY',
                  'a2idRotacionZ',
                ]

                posicionesAlternativas.forEach((key) => {
                  if (Object.prototype.hasOwnProperty.call(tiradorNewPositions, key)) {
                    ;(childItem as any)[key] = tiradorNewPositions[key]
                  }
                })

                const acabadosTirador = tiradorNewPositions?.unidad?.acabados
                if (Array.isArray(acabadosTirador)) {
                  const existingZoneKeys = Object.keys(childItem.zones || {})
                  const zoneKey = existingZoneKeys.length > 0 ? existingZoneKeys[0] : 'Tirador'

                  childItem.textures = acabadosTirador.map((acabado: any) => ({
                    id: acabado.texturas?.idTexturas || acabado.idAcabadoUnidad || 0,
                    name: acabado.texturas?.nombre || 'Tirador',
                    zoneName: zoneKey,
                    zoneDisplayName: acabado.descripcion || 'Tirador',
                    zone: zoneKey,
                    url: acabado.texturas?.url || '',
                    description: acabado.texturas?.descripcion || '',
                    groupID: acabado.texturas?.idGruposDeTexturas || 0,
                    searchID: acabado.texturas?.idTexturas || 0,
                    color: acabado.texturas?.color || '#FFFFFF',
                    modelo: acabado.modelo || null,
                    acabado: acabado.acabado || null,
                    linkedTextureID: acabado.idAcabadoUnidad,
                  }))

                  acabadosTirador.forEach((acabado: any) => {
                    if (!childItem.zones) {
                      childItem.zones = {}
                    }

                    if (childItem.zones[zoneKey]) {
                      childItem.zones[zoneKey].material = {
                        id: acabado.texturas?.idTexturas || acabado.idAcabadoUnidad || 0,
                        searchID: acabado.texturas?.idTexturas || 0,
                        name: acabado.texturas?.nombre || 'Tirador',
                        url: acabado.texturas?.url || '',
                        description: acabado.texturas?.descripcion || '',
                        acabado: acabado.acabado || null,
                        groupID: acabado.texturas?.idGruposDeTexturas || 0,
                        color: acabado.texturas?.color || '#FFFFFF',
                        roughness: acabado.texturas?.brillo ?? 1,
                        metalness: acabado.texturas?.metal ?? 0,
                        envMapIntensity: acabado.texturas?.reflejo ?? 0,
                        bumpScale: acabado.texturas?.rugosidad ?? 0,
                        opacity: acabado.texturas?.opacidad ?? 1,
                        repeatX: acabado.texturas?.factorRepeticionX || 1,
                        repeatY: acabado.texturas?.factorRepeticionY || 1,
                        textureRotation: 0,
                        mirror: acabado.texturas?.espejo ?? false,
                        displacement: 0,
                        displacementScale: 1,
                        bumpScale: 0,
                        bumpMap: null,
                      }
                    }
                  })
                }

                const recalculatedPosition = calculateTiradorPosition(childItem, item)
                childItem.x = recalculatedPosition.x
                childItem.y = recalculatedPosition.y
                childItem.z = recalculatedPosition.z
                childItem.visible = !(childItem.x === 0 && childItem.y === 0 && childItem.z === 0)
              }
            } else if (childItem.description === 'Encimera') {
              const relativeX = (childItem.x ?? 0) - (item.x ?? 0)
              const relativeZ = (childItem.z ?? 0) - (item.z ?? 0)

              childItem.x = (item.x ?? 0) + relativeX
              childItem.z = (item.z ?? 0) + relativeZ
              if (usesFreeDimensionScaling(item)) {
                placeLinkedEncimeraY(childItem, item)
              } else {
                childItem.y = (item.y ?? 0) + (item.height ?? 0)
              }
            }
          }
        })
      }

      // Actualizar sceneGLB para que el nuevo catalogID también apunte al mismo GLB
      // Esto es necesario porque useGLBInstance busca el GLB usando el catalogID
      const itemAfterUpdate = s.items.get(linkedID)
      if (isLinkedItem(itemAfterUpdate)) {
        const nuevoCatalogID = String(itemAfterUpdate.catalogID || '')

        // Si el catalogID cambió, actualizar sceneGLB
        if (nuevoCatalogID !== catalogIDAntesDeActualizar && catalogIDAntesDeActualizar) {
          const glbDataAntes = s.sceneGLB.get(catalogIDAntesDeActualizar)
          if (glbDataAntes && !s.sceneGLB.has(nuevoCatalogID)) {
            // Copiar el GLB al nuevo catalogID para que useGLBInstance lo encuentre
            s.sceneGLB.set(nuevoCatalogID, glbDataAntes)
          }
        }
      }
    })
  },
  setLinkedTiradorPosition: (linkedID: string, x: number, y: number, z: number) => {
    set((s) => {
      const item = s.items.get(linkedID as AnyID)
      if (item && item.type === 'linkedSon' && item.description === 'Tirador') {
        s.items.set(linkedID as AnyID, { ...item, x, y, z })
      }
    })
  },
  setLinkedHingePosition: (linkedID: LinkedItemID, hinge: 'Interior' | 'Exterior') => {
    set((s) => {
      const item = s.items.get(linkedID)
      if (item && item.type === 'linkedItem') {
        const hingeBoolean = hinge === 'Interior'
        s.items.set(linkedID, { ...item, hinge: hingeBoolean })
      }
    })
  },
  updateLinkedSonsForNewGLB: async (
    itemId: string,
    newCatalogId: string,
    previousCatalogId?: string,
    options?: {
      forceRestoreDefaultTiradores?: boolean
      useCatalogDefaultTextures?: boolean
    },
  ) => {
    logAcabado('updateLinkedSonsForNewGLB_START', {
      itemId,
      newCatalogId,
      previousCatalogId,
    })
    logGola('updateLinkedSonsForNewGLB_START', {
      itemId,
      newCatalogId,
      previousCatalogId,
    })

    const store = get()
    const linkedItem = store.items.get(itemId)

    if (!linkedItem || linkedItem.type !== 'linkedItem' || !linkedItem.idUnidades) {
      logAcabado('updateLinkedSonsForNewGLB_ABORT', { itemId, reason: 'invalid_item' })
      return false
    }

    // Usar el catalogID anterior si se proporciona, sino usar el actual del linkedItem
    // Esto es importante porque el catalogID del linkedItem puede haber sido actualizado
    // antes de llamar a esta función
    const currentCatalogID = previousCatalogId || linkedItem.catalogID?.toString() || ''

    try {
      const newLinkedOptions = await API().api.acabadosGetElementosEnlazadosUnidadAcabados(
        linkedItem.idUnidades,
      )

      if (!newLinkedOptions?.data) {
        return false
      }

      const newLinkedData = await API().api.unidadesGetUnidadPost({
        codigo: newCatalogId,
        date: '2023-05-12',
        catalogoId: newCatalogId,
      })

      if (!newLinkedData?.data) {
        return false
      }

      const elementosEnlazados = newLinkedData.data.elementosEnlazados || []

      const newAvailableElements = new Map<string, any>()

      if (Array.isArray(elementosEnlazados)) {
        elementosEnlazados.forEach((element: any) => {
          if (
            element.nodoPadre !== undefined &&
            element.nodo !== undefined &&
            element.descripcion
          ) {
            const key = `${element.nodoPadre}-${element.nodo}-${element.descripcion}`
            newAvailableElements.set(key, element)
          }
        })
      }

      for (const [parentNode, childNodeObj] of Object.entries(newLinkedOptions.data)) {
        for (const [childNode, elements] of Object.entries(childNodeObj as any)) {
          elements.forEach((element: any) => {
            const key = `${parentNode}-${element.nodo}-${element.descripcion}`

            if (!newAvailableElements.has(key) && Array.isArray(elementosEnlazados)) {
              const fullElement = elementosEnlazados.find(
                (el: any) =>
                  el.nodoPadre === parseInt(parentNode) &&
                  el.nodo === element.nodo &&
                  el.descripcion === element.descripcion,
              )
              if (fullElement) {
                newAvailableElements.set(key, fullElement)
              }
            }
          })
        }
      }

      if (newAvailableElements.size === 0 && Array.isArray(elementosEnlazados)) {
        elementosEnlazados.forEach((element: any) => {
          if (
            element.nodoPadre !== undefined &&
            element.nodo !== undefined &&
            element.descripcion
          ) {
            const key = `${element.nodoPadre}-${element.nodo}-${element.descripcion}`
            newAvailableElements.set(key, element)
          }
        })
      }

      // Detectar si estamos cambiando de gola a no-gola, viceversa, o entre diferentes tipos de gola
      const isCurrentGola = currentCatalogID.includes(' GO')
      const isNewGola = newCatalogId.includes(' GO')
      const isChangingGolaState = isCurrentGola !== isNewGola

      // Si ambas unidades tienen " GO", también estamos cambiando el estado de gola
      // (cambiamos de un tipo de gola a otro, por lo que los tiradores pueden cambiar)
      const isChangingBetweenGolaTypes =
        isCurrentGola && isNewGola && currentCatalogID !== newCatalogId

      const forceRestoreDefaultTiradores = options?.forceRestoreDefaultTiradores === true
      // Solo procesar tiradores cuando cambiamos gola o forzamos restauración al catálogo
      const shouldOnlyProcessTiradores =
        forceRestoreDefaultTiradores || isChangingGolaState || isChangingBetweenGolaTypes

      const currentChildren = Array.from(linkedItem.childs || [])

      const childrenToRemove: string[] = []
      const childrenToKeep: string[] = []
      const existingChildKeys = new Set()

      for (const childId of currentChildren) {
        const child = store.items.get(childId as AnyID)
        if (child && child.type === 'linkedSon') {
          const linkedSon = child as LinkedItemSon
          const childKey = `${linkedSon.parentOption}-${linkedSon.option}-${linkedSon.description}`

          if (forceRestoreDefaultTiradores && linkedSon.description === 'Tirador') {
            childrenToRemove.push(childId)
            continue
          }

          if (shouldOnlyProcessTiradores && linkedSon.description !== 'Tirador') {
            // Mantener todos los hijos que NO son tiradores sin cambios
            childrenToKeep.push(childId)
            existingChildKeys.add(childKey)
            continue
          }

          // Para tiradores o cuando NO estamos cambiando el estado de gola
          if (newAvailableElements.has(childKey)) {
            childrenToKeep.push(childId)
            existingChildKeys.add(childKey)
          } else {
            if (shouldOnlyProcessTiradores) {
              if (linkedSon.description === 'Tirador') {
                childrenToRemove.push(childId)
              } else {
                childrenToKeep.push(childId)
              }
            } else {
              childrenToRemove.push(childId)
            }
          }
        }
      }

      const childrenToAdd: string[] = []
      for (const [key, element] of newAvailableElements) {
        if (!existingChildKeys.has(key)) {
          // Si estamos cambiando el estado de gola o entre tipos de gola, solo agregar tiradores
          if (shouldOnlyProcessTiradores && element.descripcion !== 'Tirador') {
            continue
          }
          childrenToAdd.push(key)
        }
      }

      if (childrenToRemove.length > 0 || childrenToAdd.length > 0) {
        set((s) => {
          const newItems = new Map(s.items)
          const updatedLinkedItem = { ...newItems.get(itemId) }

          if (!updatedLinkedItem || updatedLinkedItem.type !== 'linkedItem') {
            return s
          }

          for (const childId of childrenToRemove) {
            if (shouldOnlyProcessTiradores) {
              const child = newItems.get(childId as AnyID)
              if (child && child.type === 'linkedSon') {
                const linkedSon = child as LinkedItemSon
                if (linkedSon.description === 'Tirador') {
                  newItems.delete(childId as AnyID)
                }
              }
            } else {
              newItems.delete(childId as AnyID)
            }
          }

          const finalChildrenToKeep = [...childrenToKeep]
          if (shouldOnlyProcessTiradores) {
            for (const childId of currentChildren) {
              if (!finalChildrenToKeep.includes(childId)) {
                const child = store.items.get(childId as AnyID)
                if (child && child.type === 'linkedSon') {
                  const linkedSon = child as LinkedItemSon
                  if (linkedSon.description !== 'Tirador') {
                    finalChildrenToKeep.push(childId)
                  }
                }
              }
            }
          }

          const newChildren = [...finalChildrenToKeep]
          const newChildrenOrder: string[] = []

          for (const addKey of childrenToAdd) {
            const [parentNodeStr, childNodeStr, description] = addKey.split('-')
            const parentNode = parseInt(parentNodeStr)
            const childNode = parseInt(childNodeStr)

            const elementData = newAvailableElements.get(addKey)
            if (elementData) {
              const linkedID = getId('linkedSon')

              let initialX = elementData.posicionX || 0
              let initialY = elementData.posicionY || 0
              let initialZ = elementData.posicionZ || 0

              let prefix = 'b'
              if (linkedItem.tipoNormalizado === 'MAL' || linkedItem.tipoNormalizado === 'COL') {
                prefix = 'a'
              }

              if (
                elementData[`${prefix}cPosicionX`] !== null &&
                elementData[`${prefix}cPosicionX`] !== undefined
              ) {
                initialX = elementData[`${prefix}cPosicionX`]
                initialY = elementData[`${prefix}cPosicionY`] || initialY
                initialZ = elementData[`${prefix}cPosicionZ`] || initialZ
              }

              const handPosition = 'Centro'

              const parentForGola = get().items.get(itemId)
              const isGolaUnit =
                isLinkedItem(parentForGola) && isGolaEffective(parentForGola as LinkedItem)
              const isTirador = elementData.descripcion === 'Tirador'
              const shouldBeVisible = resolveLinkedSonVisibility(
                elementData,
                prefix,
                { x: initialX, y: initialY, z: initialZ },
                { isGolaUnit, isTirador },
              )

              const linkedSon: LinkedItemSon = {
                id: linkedID,
                type: 'linkedSon',
                catalogID: elementData.unidad?.codigo || '',
                description: elementData.descripcion,
                parentOption: parentNode,
                option: childNode,
                nodo: childNode,
                idElementosEnlazados: elementData.idElementosEnlazados,
                x: initialX,
                y: initialY,
                z: initialZ,
                visible: shouldBeVisible,
                increment: `${s.increment}`,
                firstParentID: itemId,
                quantity: elementData.cantidad || 1,
                code: elementData.unidad?.codigo || '',
                isUnlinkable: !elementData.desenlazar,
                linkedID: elementData.idElementosEnlazados,
                visualOrder: 1,
                typeOfSon: elementData.descripcion || 'Tirador',
                referenciaBlender: elementData.unidad?.referenciaBlender,
                childs: new Set(),
                matrix: new Matrix4(),
                handPosition: handPosition,

                textures: [],
                zones: {},
                morphs: [],

                width: elementData.unidad?.ancho || 0,
                height: elementData.unidad?.alto || 0,
                thickness: elementData.unidad?.fondo || 0,
                rotation: 0,
                selected: false,
                glbPath: elementData.unidad?.referenciaBlender || '',
                glbSize: undefined,
                price: 0,
                image: '',
                long:
                  elementData.unidad?.descripcionLarga ||
                  elementData.unidad?.descripcion ||
                  elementData.unidad?.codigo ||
                  elementData.descripcion ||
                  '',
                url: '',
                universal: false,
                canCollide: true,
                boundingBox: undefined,
                hasMorphs: false,
                isBlocked: false,
                herenciaProcessed: false,
                hand: elementData.unidad?.mano || '',
                mano: elementData.unidad?.mano || '',
                depth: 1,
                snappedWall: null,
                paginaPdf: null,
                regleta: null,
                regletaMinimo: null,
                regletaMaximo: null,
                idcambioarmazon: null,
                idcambiogolablanco: null,
                idcambiogolapetroleo: null,
                hinge: false,
                bisagra: false,
                ArmazonBlanco: false,
                idUnidades: elementData.unidad?.idUnidades || 0,
                referencia: elementData.unidad?.referencia || '',
                tipoNormalizado: elementData.unidad?.tipoNormalizado || '',
                tipoUnidNorm: elementData.unidad?.tipoUnidNorm || '',
                // Propiedades adicionales que pueden ser null
                a2idPosicionX: null,
                a2idPosicionY: null,
                a2idPosicionZ: null,
                a2idRotacionX: null,
                a2idRotacionY: null,
                a2idRotacionZ: null,
                acPosicionX: null,
                acPosicionY: null,
                acPosicionZ: null,
                acRotacionX: null,
                acRotacionY: null,
                acRotacionZ: null,
                adPosicionX: null,
                adPosicionY: null,
                adPosicionZ: null,
                adRotacionX: null,
                adRotacionY: null,
                adRotacionZ: null,
                aiPosicionX: null,
                aiPosicionY: null,
                aiPosicionZ: null,
                aiRotacionX: null,
                aiRotacionY: null,
                aiRotacionZ: null,
                b2idPosicionX: null,
                b2idPosicionY: null,
                b2idPosicionZ: null,
                b2idRotacionX: null,
                b2idRotacionY: null,
                b2idRotacionZ: null,
                bcRotacionX: null,
                bcRotacionY: null,
                bcRotacionZ: null,
                bdRotacionX: null,
                bdRotacionY: null,
                bdRotacionZ: null,
                biRotacionX: null,
                biRotacionY: null,
                biRotacionZ: null,
                rotacionX: null,
                rotacionY: null,
                rotacionZ: null,
              } as LinkedItemSon

              void API()
                .api.acabadosGetElementosEnlazadosUnidadAcabados2(
                  elementData.idElementosEnlazados,
                  elementData.idElementosEnlazados,
                )
                .then((acabadosResp) => {
                  const unidadInfo = acabadosResp?.data?.unidad
                  if (!unidadInfo) return

                  const mappedTextures = Array.isArray(unidadInfo.acabados)
                    ? unidadInfo.acabados.map((acabado: any) => ({
                        id: acabado.texturas?.idTexturas || acabado.idAcabadoUnidad || 0,
                        name: acabado.zona || acabado.texturas?.nombre || 'Tirador',
                        zoneName: acabado.zona || 'Tirador',
                        zoneDisplayName: acabado.descripcion || acabado.zona || 'Tirador',
                        zone: acabado.zona || 'Tirador',
                        url: acabado.texturas?.url || '',
                        description: acabado.texturas?.descripcion || '',
                        groupID: acabado.texturas?.idGruposDeTexturas || 0,
                        searchID: acabado.texturas?.idTexturas || 0,
                        color: acabado.texturas?.color || '#FFFFFF',
                        repeatX: acabado.texturas?.factorRepeticionX || 1,
                        repeatY: acabado.texturas?.factorRepeticionY || 1,
                        opacity: acabado.texturas?.opacidad ?? 1,
                        roughness: acabado.texturas?.rugosidad ?? 0,
                        metalness: acabado.texturas?.metal ?? 0,
                        mirror: acabado.texturas?.espejo ?? false,
                        modelo: acabado.modelo,
                        acabado: acabado.acabado,
                        linkedTextureID: acabado.idAcabadoUnidad,
                      }))
                    : []

                  if (mappedTextures.length === 0) return

                  set((s) => {
                    const newItems = new Map(s.items)
                    const curr = newItems.get(linkedID as AnyID) as any
                    if (!curr || curr.type !== 'linkedSon') return s

                    curr.textures = mappedTextures as any

                    if (!curr.zones || Object.keys(curr.zones).length === 0) {
                      const t = mappedTextures[0]
                      const zoneKey = t.zoneName || t.zoneDisplayName || 'Tirador'
                      curr.zones = {
                        [zoneKey]: {
                          index: 0,
                          idAcabadoUnidadModeloAcabado: `${zoneKey}_${t.linkedTextureID ?? t.id}`,
                          material: {
                            id: t.id,
                            searchID: t.searchID,
                            name: t.name,
                            url: t.url,
                            description: t.description,
                            acabado: t.acabado,
                            groupID: t.groupID,
                            color: t.color || '#FFFFFF',
                            roughness: t.roughness ?? 1,
                            metalness: t.metalness ?? 0,
                            envMapIntensity: 0,
                            opacity: t.opacity ?? 1,
                            repeatX: t.repeatX ?? 1,
                            repeatY: t.repeatY ?? 1,
                            textureRotation: 0,
                            mirror: t.mirror ?? false,
                            displacement: 0,
                            displacementScale: 1,
                            bumpScale: 0,
                            bumpMap: null,
                          },
                        },
                      }
                    }

                    newItems.set(linkedID as AnyID, curr)
                    return { ...s, items: newItems }
                  })
                })
                .catch(() => {})

              const posicionesAlternativas = [
                'posicionX',
                'posicionY',
                'posicionZ',
                'biPosicionX',
                'biPosicionY',
                'biPosicionZ',
                'bcPosicionX',
                'bcPosicionY',
                'bcPosicionZ',
                'bdPosicionX',
                'bdPosicionY',
                'bdPosicionZ',
                'aiPosicionX',
                'aiPosicionY',
                'aiPosicionZ',
                'acPosicionX',
                'acPosicionY',
                'acPosicionZ',
                'adPosicionX',
                'adPosicionY',
                'adPosicionZ',
                'rotacionX',
                'rotacionY',
                'rotacionZ',
                'biRotacionX',
                'biRotacionY',
                'biRotacionZ',
                'bcRotacionX',
                'bcRotacionY',
                'bcRotacionZ',
                'bdRotacionX',
                'bdRotacionY',
                'bdRotacionZ',
                'aiRotacionX',
                'aiRotacionY',
                'aiRotacionZ',
                'acRotacionX',
                'acRotacionY',
                'acRotacionZ',
                'adRotacionX',
                'adRotacionY',
                'adRotacionZ',
                'b2idPosicionX',
                'b2idPosicionY',
                'b2idPosicionZ',
                'b2idRotacionX',
                'b2idRotacionY',
                'b2idRotacionZ',
                'a2idPosicionX',
                'a2idPosicionY',
                'a2idPosicionZ',
                'a2idRotacionX',
                'a2idRotacionY',
                'a2idRotacionZ',
              ]

              posicionesAlternativas.forEach((key) => {
                if (elementData[key] !== undefined && elementData[key] !== null) {
                  ;(linkedSon as any)[key] = elementData[key]
                }
              })

              newItems.set(linkedID as AnyID, linkedSon)
              newChildren.push(linkedID)

              const orderKey = `${parentNode}-${childNode}-0!${linkedID}`
              newChildrenOrder.push(orderKey)
            }
          }

          const existingChildrenOrder = (updatedLinkedItem.childrenOrder || []).filter(
            (orderKey: string) => {
              const childIdFromOrder = orderKey.split('!')[1]
              const shouldKeep = !childrenToRemove.includes(childIdFromOrder)

              return shouldKeep
            },
          )

          const finalLinkedItem = {
            ...updatedLinkedItem,
            childs: new Set(newChildren),
            childrenOrder: [...existingChildrenOrder, ...newChildrenOrder] as any[],
          } as LinkedItem

          newItems.set(itemId as AnyID, finalLinkedItem)

          return {
            ...s,
            items: newItems,
            increment: s.increment + childrenToAdd.length,
          }
        })

        if (childrenToAdd.length > 0) {
          const loader = await setGLTFLoader()
          const info = await fetchLinkedChildrenInfo(linkedItem.idUnidades)

          for (const addKey of childrenToAdd) {
            const elementData = newAvailableElements.get(addKey)

            if (elementData?.unidad?.referenciaBlender) {
              await get().loadAndUpdateLinkedSonGLB(
                itemId,
                elementData,
                addKey,
                loader,
                info,
                linkedItem,
                { useCatalogDefaultTextures: options?.useCatalogDefaultTextures === true },
              )
            }
          }
        }
      }

      return true
    } catch (error) {
      console.error('❌ Error actualizando linkedSon:', error)
      return false
    }
  },

  loadAndUpdateLinkedSonGLB: async (
    parentId: string,
    elementData: any,
    addKey: string,
    loader: any,
    info: any,
    parentLinkedItem: LinkedItem,
    loadOptions?: { useCatalogDefaultTextures?: boolean },
  ) => {
    try {
      // const [childNodeStr, description] = addKey.split('-')

      const sonGlbData = await loader.loadAsync(elementData.unidad.referenciaBlender)
      const backData = elementData.unidad
      const idElementosEnlazados = elementData.idElementosEnlazados

      let responseData = elementData
      try {
        const posicionesResp = await API().api.acabadosGetElementosEnlazadosUnidadAcabados2(
          idElementosEnlazados,
          idElementosEnlazados,
        )
        responseData = posicionesResp?.data || elementData
      } catch (error) {
        console.warn('❌ Error obteniendo posiciones:', error)
      }

      let processedInfo: any = null

      try {
        const extraInfoResponse = await API().api.acabadosGetUnidadAcabadosInfo(backData.idUnidades)

        const extraInfo = {
          zonas: Array.isArray(extraInfoResponse.data?.zonas)
            ? extraInfoResponse.data.zonas.map((z: any) => ({
                idAcabadoUnidad: z.idAcabadoUnidad,
                zona: z.zona ?? '',
              }))
            : [],
        }

        const backGLBInfo: any = {
          ...backData,
          linkedID: backData.idUnidades,
          idUnidades: backData.idUnidades,
          codigo: backData.codigo ?? '',
          active: true,
        }

        const store = get()
        const parentItem = store.items.get(parentId)
        if (!parentItem || parentItem.type !== 'linkedItem') return

        const existingSonId = Array.from(parentItem.childs || []).find((childId) => {
          const child = store.items.get(childId as AnyID) as LinkedItemSon | undefined
          return child && child.type === 'linkedSon' && child.nodo === elementData.nodo
        })

        if (!existingSonId) return
        const existingSon = store.items.get(existingSonId as AnyID) as LinkedItemSon
        if (!existingSon) return

        processedInfo = newGetDataFromGLB({
          glbData: sonGlbData,
          backGLBInfo,
          id: existingSonId,
          type: 'linkedSon',
          catalogID: backData.codigo ?? '',
          extraInfo,
          itemInfo: {},
        })
      } catch (error) {
        return
      }

      let finalX = 0,
        finalY = 0,
        finalZ = 0

      let prefix = 'b'
      if (
        parentLinkedItem.tipoNormalizado === 'MAL' ||
        parentLinkedItem.tipoNormalizado === 'COL'
      ) {
        prefix = 'a'
      }

      const cx = responseData[`${prefix}cPosicionX`]
      const cy = responseData[`${prefix}cPosicionY`]
      const cz = responseData[`${prefix}cPosicionZ`]

      finalX = cx ?? responseData.posicionX ?? 0
      finalY = cy ?? responseData.posicionY ?? 0
      finalZ = cz ?? responseData.posicionZ ?? 0

      const store = get()
      const parentItem = store.items.get(parentId)
      if (!parentItem || parentItem.type !== 'linkedItem') return

      const existingSonId = Array.from(parentItem.childs || []).find((childId) => {
        const child = store.items.get(childId as AnyID) as LinkedItemSon | undefined
        return child && child.type === 'linkedSon' && child.nodo === elementData.nodo
      })

      if (!existingSonId) return
      const existingSon = store.items.get(existingSonId as AnyID) as LinkedItemSon
      if (!existingSon) return

      let finalTextures = processedInfo.textures || []
      let finalZones = processedInfo.zones || {}

      if (finalTextures.length > 0 && !loadOptions?.useCatalogDefaultTextures) {
        const hasVitrinaModel = finalTextures.some((texture: any) => texture.modelo === 'Vitrina')

        const shouldInheritTextures =
          !hasVitrinaModel && !existingSon.catalogID?.toString().toUpperCase().startsWith('KABV')

        if (shouldInheritTextures) {
          const existingTextures = getExistingTexturesFromScene(
            store.items as any,
            existingSon.catalogID,
            parentId,
          )

          if (existingTextures && existingTextures.length > 0) {
            finalTextures = mapExistingTexturesToNewItemByDisplayName(
              existingTextures,
              finalTextures as any,
            )

            const syncResult = syncZonesWithMappedTextures(
              finalZones,
              finalTextures as any,
              store.items as any,
              'linkedSon',
              existingSon.description || 'Tirador',
            )
            finalZones = syncResult.zones
          }
        }
      }

      const isGolaUnit = isGolaEffective(parentLinkedItem)
      const isTirador = elementData.descripcion === 'Tirador'
      const shouldBeVisible = resolveLinkedSonVisibility(
        responseData,
        prefix,
        { x: finalX, y: finalY, z: finalZ },
        { isGolaUnit, isTirador },
      )

      const updatedSon = {
        ...existingSon,

        x: finalX,
        y: finalY,
        z: finalZ,

        textures: finalTextures,
        zones: finalZones,

        width: backData.ancho || existingSon.width,
        height: backData.alto || existingSon.height,
        thickness: backData.fondo || existingSon.thickness,
        long:
          backData.descripcionLarga || backData.descripcion || backData.codigo || existingSon.long,
        image: backData.referenciaJpgpng || existingSon.image,
        price: backData.precio || existingSon.price,

        catalogID: backData.codigo || existingSon.catalogID,
        code: backData.codigo || existingSon.code,
        glbPath: backData.referenciaBlender || existingSon.glbPath,
        referenciaBlender: backData.referenciaBlender || existingSon.referenciaBlender,

        idElementosEnlazados: idElementosEnlazados,
        nodo: elementData.nodo,
        handPosition: 'Centro',
        visible: shouldBeVisible,
      }

      set((s) => {
        const newItems = new Map(s.items)
        newItems.set(existingSonId as AnyID, updatedSon)

        const newSceneGLB = new Map(s.sceneGLB)
        const catalogIDForGLB = updatedSon.catalogID
        newSceneGLB.set(catalogIDForGLB, {
          glb: sonGlbData,
          backGLBInfo: backData,
        })

        return {
          ...s,
          items: newItems,
          sceneGLB: newSceneGLB,
        }
      })
    } catch (error) {
      console.error('❌ Error en loadAndUpdateLinkedSonGLB:', error)
    }
  },
  updateLinkedItemGLB: async (itemId: string, newCatalogId: string) => {
    const storeBefore = get()
    const linkedItemBefore = storeBefore.items.get(itemId)
    logAcabado('updateLinkedItemGLB_START', {
      itemId,
      newCatalogId,
      fromCatalogID: linkedItemBefore?.catalogID,
      idUnidades: isLinkedItem(linkedItemBefore) ? linkedItemBefore.idUnidades : undefined,
    })
    if (linkedItemBefore && isLinkedItem(linkedItemBefore) && linkedItemBefore.childs) {
      logLinkedChildrenSnapshot(
        'updateLinkedItemGLB_before',
        itemId,
        (id) => storeBefore.items.get(id as AnyID),
        linkedItemBefore.childs,
      )
      logItemTexturesSnapshot(
        'updateLinkedItemGLB_before',
        itemId,
        linkedItemBefore.textures as Array<Record<string, unknown>>,
        String(linkedItemBefore.catalogID || ''),
      )
    }

    const batchActive = isLinkedVisualBatchActive()
    let originalFrameloop: Store['frameloop'] = 'demand'
    if (!batchActive) {
      set((store) => {
        originalFrameloop = store.frameloop
        store.frameloop = 'always'
      })
    }

    const store = get()
    const linkedItem = store.items.get(itemId)

    if (!linkedItem || linkedItem.type !== 'linkedItem') {
      logAcabado('updateLinkedItemGLB_ABORT', { itemId, reason: 'invalid_linkedItem' })
      if (!batchActive) {
        set((s) => {
          s.frameloop = originalFrameloop
        })
      }
      return false
    }

    try {
      const normalizeCode = (code?: string) =>
        String(code ?? '')
          .replace(/\s+/g, ' ')
          .trim()
          .toUpperCase()

      logAcabadoApi('unidadesGetUnidadPost', {
        codigo: newCatalogId,
        catalogoId: newCatalogId,
      })
      const newGLBData = await API().api.unidadesGetUnidadPost({
        codigo: newCatalogId,
        date: '2023-05-12',
        catalogoId: newCatalogId,
      })
      logAcabadoApi(
        'unidadesGetUnidadPost',
        { codigo: newCatalogId },
        newGLBData,
      )

      if (newGLBData && newGLBData.data) {
        const loader = await setGLTFLoader()
        const ref = newGLBData.data.referenciaBlender
        if (!ref) return false

        const codeKey = normalizeCode(newGLBData.data.codigo || newCatalogId)

        const cached = store.sceneGLB.get(codeKey) as any
        const glbCached = Boolean(cached?.glb)
        logAcabado('GLB_LOAD', {
          itemId,
          codeKey,
          referenciaBlender: ref,
          glbCached,
        })
        const glb = cached?.glb ? cached.glb : await loader.loadAsync(ref)
        logAcabado('GLB_LOAD_DONE', { itemId, codeKey, glbCached })

        set((s) => {
          const newItems = new Map(s.items)
          const item = { ...newItems.get(itemId) }

          if (item && item.type === 'linkedItem') {
            item.catalogID = codeKey

            if (ref) {
              item.glbPath = ref
            }

            if (newGLBData.data.referencia) {
              item.referencia = newGLBData.data.referencia
            }

            if (newGLBData.data.referenciaJpgpng) {
              item.image = newGLBData.data.referenciaJpgpng
            }

            if (newGLBData.data.idUnidades) {
              item.idUnidades = newGLBData.data.idUnidades
            }

            if (newGLBData.data.descripcion) {
              item.description = newGLBData.data.descripcion
            }

            if (newGLBData.data.descripcionLarga) {
              item.long = newGLBData.data.descripcionLarga
            }

            if (newGLBData.data.ancho) {
              item.width = newGLBData.data.ancho
            }

            if (newGLBData.data.fondo) {
              item.thickness = newGLBData.data.fondo
            }

            if (newGLBData.data.alto) {
              item.height = newGLBData.data.alto
            }

            applyIdCambioFromApi(item as Record<string, unknown>, newGLBData.data)

            item.glbSize = { x: Math.random(), y: 1, z: 1 }

            newItems.set(itemId, item)
          }

          return {
            items: newItems,
            sceneGLB: (() => {
              const m = new Map(s.sceneGLB)
              if (!m.has(codeKey)) m.set(codeKey, { glb, backGLBInfo: newGLBData.data })
              return m
            })(),
          }
        })

        set((s) => ({
          ...s,
          shouldUpdateCamera: true,
        }))

        try {
          const glbCache = (window as any).glbCache
          if (glbCache && glbCache.clear) glbCache.clear()
        } catch {}

        setTimeout(() => {
          set((s) => ({
            ...s,
            shouldUpdateCamera: true,
          }))
        }, 100)

        const updatedItem = get().items.get(itemId)
        if (
          updatedItem &&
          updatedItem.type === 'linkedItem' &&
          updatedItem.childs &&
          Array.isArray(updatedItem.childs) &&
          updatedItem.childs.length > 0
        ) {
          adjustChildrenPositionsAfterInheritance(
            updatedItem,
            (childId) => get().items.get(childId),
            (childId, updates) => {
              get().editItem(childId, updates)
            },
          )
        }

        const currentCatalogID = linkedItem.catalogID?.toString() || ''
        const normalizedNewCatalogID = normalizeCode(newCatalogId)

        // Verificar si solo cambió el acabado (especialmente en Gola)
        // Si el código base es el mismo, no llamar a updateLinkedSonsForNewGLB
        // ya que LinkedTexturesEditor se encarga de actualizar los hijos
        const baseCatalogID = currentCatalogID.replace(/ (GO|LA GO|RU GO|R2 GO| LI| LA| RU| R2)/, '')
        const baseNewCatalogID = normalizedNewCatalogID.replace(/ (GO|LA GO|RU GO|R2 GO| LI| LA| RU| R2)/, '')

        logAcabado('SONS_DECISION', {
          itemId,
          currentCatalogID,
          normalizedNewCatalogID,
          baseCatalogID,
          baseNewCatalogID,
          willCallUpdateLinkedSonsForNewGLB:
            currentCatalogID !== normalizedNewCatalogID && baseCatalogID !== baseNewCatalogID,
          suffixOnlyChange:
            currentCatalogID !== normalizedNewCatalogID && baseCatalogID === baseNewCatalogID,
        })

        if (currentCatalogID !== normalizedNewCatalogID && baseCatalogID !== baseNewCatalogID) {
          logAcabado('CALL_updateLinkedSonsForNewGLB', { itemId, newCatalogId })
          await get().updateLinkedSonsForNewGLB(itemId, newCatalogId)
          logAcabado('RESULT_updateLinkedSonsForNewGLB', { itemId, newCatalogId })
        } else if (currentCatalogID !== normalizedNewCatalogID) {
          // Cambio solo de sufijo (LI/LA/GO…): mantener hijos visibles si tienen posición
          const itemWithChildren = get().items.get(itemId)
          if (
            itemWithChildren &&
            itemWithChildren.type === 'linkedItem' &&
            itemWithChildren.childs
          ) {
            itemWithChildren.childs.forEach((childID) => {
              const child = get().items.get(childID)
              if (!child || child.type !== 'linkedSon' || child.description === 'Tirador') return
              const x = child.x ?? 0
              const y = child.y ?? 0
              const z = child.z ?? 0
              if (!(x === 0 && y === 0 && z === 0)) {
                get().editItem(childID as AnyID, { visible: true })
              }
            })
          }
        }

        const finalItem = get().items.get(itemId)
        if (
          finalItem &&
          finalItem.type === 'linkedItem' &&
          finalItem.idUnidades &&
          !finalItem.tiradorOptions
        ) {
          try {
            logAcabadoApi('acabadosGetElementosEnlazadosUnidadAcabados', {
              idUnidades: finalItem.idUnidades,
              optionType: 'Tirador',
            })
            const tiradorOptionsResponse =
              await API().api.acabadosGetElementosEnlazadosUnidadAcabados(finalItem.idUnidades)
            logAcabadoApi(
              'acabadosGetElementosEnlazadosUnidadAcabados',
              { idUnidades: finalItem.idUnidades, optionType: 'Tirador' },
              tiradorOptionsResponse,
            )

            if (
              tiradorOptionsResponse.data &&
              tiradorOptionsResponse.data['0'] &&
              tiradorOptionsResponse.data['0']['Tirador']
            ) {
              set((s) => {
                const item = s.items.get(itemId)
                if (item && item.type === 'linkedItem') {
                  item.tiradorOptions = tiradorOptionsResponse.data['0'][
                    'Tirador'
                  ] as ItemWithOptions[]
                }
              })
            }
          } catch (error) {
            console.warn(
              'Error obteniendo tiradorOptions para unidad:',
              finalItem.idUnidades,
              error,
            )
          }
        }

        if (finalItem && finalItem.type === 'linkedItem' && finalItem.idUnidades) {
          try {
            logAcabadoApi('acabadosGetElementosEnlazadosUnidadAcabados', {
              idUnidades: finalItem.idUnidades,
              optionType: 'TPCGrifo',
            })
            const grifoOptionsResponse =
              await API().api.acabadosGetElementosEnlazadosUnidadAcabados(finalItem.idUnidades)
            logAcabadoApi(
              'acabadosGetElementosEnlazadosUnidadAcabados',
              { idUnidades: finalItem.idUnidades, optionType: 'TPCGrifo' },
              grifoOptionsResponse,
            )

            if (
              grifoOptionsResponse.data &&
              grifoOptionsResponse.data['0'] &&
              grifoOptionsResponse.data['0']['TPCGrifo']
            ) {
              set((s) => {
                const item = s.items.get(itemId)
                if (item && item.type === 'linkedItem') {
                  item.grifoOptions = grifoOptionsResponse.data['0'][
                    'TPCGrifo'
                  ] as ItemWithOptions[]
                }
              })
            }
          } catch (error) {
            console.warn('Error obteniendo grifoOptions para unidad:', finalItem.idUnidades, error)
          }
        }

        if (finalItem && finalItem.type === 'linkedItem' && finalItem.idUnidades) {
          try {
            const placaOptionsResponse =
              await API().api.acabadosGetElementosEnlazadosUnidadAcabados(finalItem.idUnidades)

            if (
              placaOptionsResponse.data &&
              placaOptionsResponse.data['0'] &&
              placaOptionsResponse.data['0']['Placa']
            ) {
              set((s) => {
                const item = s.items.get(itemId)
                if (item && item.type === 'linkedItem') {
                  item.placaOptions = placaOptionsResponse.data['0']['Placa'] as ItemWithOptions[]
                }
              })
            }
          } catch (error) {
            console.warn('Error obteniendo placaOptions para unidad:', finalItem.idUnidades, error)
          }
        }

        if (finalItem && finalItem.type === 'linkedItem' && finalItem.idUnidades) {
          try {
            logAcabadoApi('acabadosGetElementosEnlazadosUnidadAcabados', {
              idUnidades: finalItem.idUnidades,
              optionType: 'Fregadero',
            })
            const fregaderoOptionsResponse =
              await API().api.acabadosGetElementosEnlazadosUnidadAcabados(finalItem.idUnidades)
            logAcabadoApi(
              'acabadosGetElementosEnlazadosUnidadAcabados',
              { idUnidades: finalItem.idUnidades, optionType: 'Fregadero' },
              fregaderoOptionsResponse,
            )

            if (
              fregaderoOptionsResponse.data &&
              fregaderoOptionsResponse.data['0'] &&
              fregaderoOptionsResponse.data['0']['Fregadero']
            ) {
              set((s) => {
                const item = s.items.get(itemId)
                if (item && item.type === 'linkedItem') {
                  item.fregaderoOptions = fregaderoOptionsResponse.data['0'][
                    'Fregadero'
                  ] as ItemWithOptions[]
                }
              })
            }
          } catch (error) {
            console.warn(
              'Error obteniendo fregaderoOptions para unidad:',
              finalItem.idUnidades,
              error,
            )
          }
        }

        if (finalItem && finalItem.type === 'linkedItem' && finalItem.idUnidades) {
          try {
            const encimeraOptionsResponse =
              await API().api.acabadosGetElementosEnlazadosUnidadAcabados(finalItem.idUnidades)

            if (
              encimeraOptionsResponse.data &&
              encimeraOptionsResponse.data['0'] &&
              encimeraOptionsResponse.data['0']['Encimera']
            ) {
              set((s) => {
                const item = s.items.get(itemId)
                if (item && item.type === 'linkedItem') {
                  item.encimeraOptions = encimeraOptionsResponse.data['0'][
                    'Encimera'
                  ] as ItemWithOptions[]
                }
              })
            }
          } catch (error) {
            console.warn(
              'Error obteniendo encimeraOptions para unidad:',
              finalItem.idUnidades,
              error,
            )
          }
        }

        const storeAfter = get()
        const finalLinkedItem = storeAfter.items.get(itemId)
        if (finalLinkedItem && isLinkedItem(finalLinkedItem)) {
          logItemTexturesSnapshot(
            'updateLinkedItemGLB_after',
            itemId,
            finalLinkedItem.textures as Array<Record<string, unknown>>,
            String(finalLinkedItem.catalogID || ''),
          )
          if (finalLinkedItem.childs) {
            logLinkedChildrenSnapshot(
              'updateLinkedItemGLB_after',
              itemId,
              (id) => storeAfter.items.get(id as AnyID),
              finalLinkedItem.childs,
            )
          }
        }
        logAcabado('updateLinkedItemGLB_SUCCESS', { itemId, newCatalogId })
        return true
      } else {
        logAcabado('updateLinkedItemGLB_FAIL', { itemId, reason: 'no_glb_data' })
        return false
      }
    } catch (error) {
      console.error('Error al actualizar GLB:', error)
      logAcabado('updateLinkedItemGLB_ERROR', { itemId, newCatalogId, error: String(error) })
      return false
    } finally {
      if (!batchActive) {
        setTimeout(() => {
          set((s) => {
            s.frameloop = originalFrameloop
          })
        }, 100)
      }
    }
  },
  updateEncimeraHeight: async (encimeraID: string, newHeight?: number) => {
    set((state) => {
      const encimeraItem = state.items.get(encimeraID as AnyID)

      if (!encimeraItem || !isEncimeraLinkedSonItem(encimeraItem)) {
        return
      }

      const heightToUse = newHeight ?? encimeraItem.height
      const parentItem = encimeraItem.firstParentID
        ? state.items.get(encimeraItem.firstParentID as AnyID)
        : null
      const parentUsesFreeDim =
        parentItem?.type === 'linkedItem' && usesFreeDimensionScaling(parentItem)

      if (newHeight !== undefined) {
        encimeraItem.height = newHeight
        encimeraItem.morphs?.forEach((morph) => {
          if (morph.name === 'Alto') {
            morph.default = newHeight
            morph.value = newHeight
          }
        })
      } else {
        applyEncimeraThicknessMm(encimeraItem)
      }

      if (parentItem && parentItem.type === 'linkedItem') {
        if (parentUsesFreeDim) {
          placeLinkedEncimeraY(encimeraItem, parentItem)
        }

        state.items.forEach((item) => {
          if (
            item.type === 'linkedSon' &&
            (item.description === 'Placa' ||
              item.description === 'Fregadero' ||
              item.description === 'Grifo' ||
              item.description === 'TPCGrifo' ||
              (item as any).typeOfSon === 'Grifo' ||
              (item as any).typeOfSon === 'TPCGrifo') &&
            (item as any).firstParentID === encimeraItem.firstParentID
          ) {
            if (item.description === 'Placa') {
              const encimeraTextures = encimeraItem.textures || []
              const encimeraTexture = encimeraTextures[0]

              if (encimeraTexture) {
                const acabado = encimeraTexture.acabado?.toLowerCase() || ''
                let ajuste = 0

                if (acabado.includes('porcelanicas 12')) {
                  ajuste = 2
                } else if (acabado.includes('porcelanicas 20')) {
                  ajuste = 10
                } else if (acabado.includes('postformadas')) {
                  ajuste = 20
                } else if (acabado.includes('compacto')) {
                  ajuste = 0
                }

                // Asegurar que baseY represente siempre la altura original (no acumulada)
                if ((item as any).baseY === undefined || (item as any).baseY === null) {
                  ;(item as any).baseY = item.y
                }
                const baseY = (item as any).baseY
                const nuevaY = baseY + ajuste

                item.y = nuevaY
              }
              return
            }

            if (item.description === 'Fregadero') {
              const encimeraTextures = encimeraItem.textures || []
              const encimeraTexture = encimeraTextures[0]

              if (encimeraTexture) {
                const acabado = encimeraTexture.acabado?.toLowerCase() || ''
                let ajuste = 0

                if (acabado.includes('porcelanicas 12')) {
                  ajuste = 2
                } else if (acabado.includes('porcelanicas 20')) {
                  ajuste = 10
                } else if (acabado.includes('postformadas')) {
                  ajuste = 20
                } else if (acabado.includes('compacto')) {
                  ajuste = 0
                }

                // Asegurar que baseY represente siempre la altura original (no acumulada)
                if ((item as any).baseY === undefined || (item as any).baseY === null) {
                  ;(item as any).baseY = item.y
                }
                const baseY = (item as any).baseY
                const nuevaY = baseY + ajuste

                item.y = nuevaY
              }
              return
            }

            if (
              item.description === 'Grifo' ||
              item.description === 'TPCGrifo' ||
              (item as any).typeOfSon === 'Grifo' ||
              (item as any).typeOfSon === 'TPCGrifo'
            ) {
              const encimeraTextures = encimeraItem.textures || []
              const encimeraTexture = encimeraTextures[0]

              if (encimeraTexture) {
                const acabado = encimeraTexture.acabado?.toLowerCase() || ''
                let ajuste = 0

                if (acabado.includes('porcelanicas 12')) {
                  ajuste = 2
                } else if (acabado.includes('porcelanicas 20')) {
                  ajuste = 10
                } else if (acabado.includes('postformadas')) {
                  ajuste = 20
                } else if (acabado.includes('compacto')) {
                  ajuste = 0
                }

                // Asegurar que baseY represente siempre la altura original (no acumulada)
                if ((item as any).baseY === undefined || (item as any).baseY === null) {
                  ;(item as any).baseY = item.y
                }
                const baseY = (item as any).baseY
                const nuevaY = baseY + ajuste

                item.y = nuevaY
              }
              return
            }

            let nuevaY: number

            const encimeraTextures = encimeraItem.textures || []
            const encimeraTexture = encimeraTextures[0]

            if (encimeraTexture) {
              const acabado = encimeraTexture.acabado?.toLowerCase() || ''

              if (acabado.includes('porcelanicas 12')) {
                nuevaY = 912
              } else if (acabado.includes('porcelanicas 20')) {
                nuevaY = 920
              } else if (acabado.includes('postformadas')) {
                nuevaY = 930
              } else if (acabado.includes('compacto')) {
                nuevaY = 910
              } else {
                nuevaY = parentItem.y + parentItem.height + heightToUse
              }
            } else {
              nuevaY = parentItem.y + parentItem.height + heightToUse
            }

            item.y = nuevaY
          }
        })
      }

      state.shouldUpdateCamera = true
    })

    setTimeout(() => {
      set((state) => ({
        ...state,
        shouldUpdateCamera: true,
      }))
    }, 200)
  },

  // Función vacía - las placas solo se actualizan cuando se cambia la encimera manualmente
  adjustPlacaPositionAfterEncimeraCreation: (encimeraID: string) => {
    // No hacer nada - las placas se posicionan manualmente al inicio al menos en principio mientras todas sean compactas
  },

  positionFregaderoGrifoPlacaAccordingToEncimera: (childID: string) => {
    set((state) => {
      const childItem = state.items.get(childID as AnyID)

      if (!childItem || childItem.type !== 'linkedSon') {
        return
      }

      if (childItem.firstParentID) {
        const parentItem = state.items.get(childItem.firstParentID as AnyID)

        if (parentItem && parentItem.type === 'linkedItem') {
          let encimeraItem = null
          state.items.forEach((item, key) => {
            if (
              item.type === 'linkedSon' &&
              (item.description?.toLowerCase().includes('encimera') ||
                (item as any).typeOfSon === 'Encimera') &&
              (item as any).firstParentID === childItem.firstParentID
            ) {
              encimeraItem = item
            }
          })

          if (encimeraItem) {
            const encimeraTextures = encimeraItem.textures || []
            const encimeraTexture = encimeraTextures[0]
            const acabado = encimeraTexture?.acabado?.toLowerCase() || ''

            // Usar baseY si está disponible, sino usar la posición Y actual
            const baseY = (childItem as any).baseY || childItem.y
            let ajuste = 0
            let nuevaY: number

            if (acabado.includes('porcelanicas 12')) {
              ajuste = 2
            } else if (acabado.includes('porcelanicas 20')) {
              ajuste = 10
            } else if (acabado.includes('postformadas')) {
              ajuste = 20
            } else if (acabado.includes('compacto')) {
              ajuste = 0
            } else {
              ajuste = 10
            }

            nuevaY = baseY + ajuste

            const updatedChild = { ...childItem, y: nuevaY }
            state.items.set(childID as AnyID, updatedChild)
          } else {
            console.warn('⚠️ No se encontró encimera para posicionar el elemento:', childID)
          }
        }
      }

      state.shouldUpdateCamera = true
    })
  },

  updateZocaloWithCompleteTexture: async (
    itemId: AnyID,
    complementoName: string,
    linkedTextureID: number,
  ) => {
    try {
      const api = API()

      const info = await api.api.acabadosGetAcabadoZonaModeloAcabadoInfo(linkedTextureID)
      const data = (info as any)?.data ?? info
      const acabados = Array.isArray(data)
        ? data.find((z: any) => (z?.zona || '').toLowerCase().includes('zocalo'))?.acabados ||
          data[0]?.acabados ||
          []
        : []

      const idAcabado = acabados[0]?.idAcabadoUnidadModeloAcabado

      if (idAcabado) {
        const texturasResp = await api.api.acabadosGetTexturas(idAcabado)
        const texturas = (texturasResp as any)?.data ?? texturasResp

        const cmp = complementoName.toLowerCase()
        const matchingTexture = (Array.isArray(texturas) ? texturas : []).find((t: any) => {
          const n = (t?.nombre || '').toLowerCase()
          const d = (t?.descripcion || '').toLowerCase()
          return n === cmp || n.includes(cmp) || d.includes(cmp)
        })

        if (matchingTexture) {
          const refreshedState = useAppStore.getState()
          const refreshedItem = refreshedState.items.get(itemId)

          if (refreshedItem && isLinked(refreshedItem)) {
            const zocaloTexture = refreshedItem.textures.find(
              (el) => el.zoneDisplayName === 'Zocalo',
            )

            if (zocaloTexture) {
              const matchedZocaloMaterial = getZocaloMaterialFieldsFromOption(
                matchingTexture,
                complementoName,
              )

              Object.assign(zocaloTexture, matchedZocaloMaterial)

              const targetZoneKey = Object.keys(refreshedItem.zones).find(
                (k) => refreshedItem.zones[k].name === zocaloTexture.zoneName,
              )
              if (targetZoneKey && refreshedItem.zones[targetZoneKey]?.material) {
                Object.assign(refreshedItem.zones[targetZoneKey].material, matchedZocaloMaterial)
              }

              useAppStore.setState((s) => {
                s.shouldUpdateCamera = true
                ;(s as any).editorNeedsUpdate = true
                ;(s as any).lastEditorUpdate = Date.now()
              })
            }
          }
        }
      }
    } catch (e) {
      console.warn('[updateZocaloWithCompleteTexture] Error:', e)
    }
  },
})
